import React, { useState, useEffect, useLayoutEffect, useRef, useMemo } from 'react';
import { useExpandAnimation, useAnimatedPresence, animateHeight } from '../hooks/useExpandAnimation';
import LoadingSpinner from './LoadingSpinner';
import { formatCurrency } from '../utils/formatCurrency';
import { roundMoney, toMoneyNumber } from '../utils/money';
import { useDepartment } from '../contexts/DepartmentContext';
import { useOrderAuthority } from '../hooks/useOrderAuthority';
import { useVatMode } from '../hooks/useVatMode';
import useSubmitGuard from '../hooks/useSubmitGuard';
import { BTN, BTN_XS } from '../constants/buttons';
import { FIELD_LABEL_CLASS } from '../constants/inputs';
import { formatReadableDate } from '../utils/dateFormat';
import SupplierOptionList from './SupplierOptionList';
import InfoTip from './InfoTip';
import OrderAttachments, { formatFileSize } from './OrderAttachments';
import UploadingFileRow from './UploadingFileRow';
import PendingUploadRow from './PendingUploadRow';
import { pendingLineKeyFor, PENDING_ORDER_LINE } from '../hooks/usePendingOrderUploads';
import OrderItemActionsMenu from './OrderItemActionsMenu';
import { ItemReceivedCheckbox, LineOrderedDeliveredControls, NextStepAllButton, headerNextStep, lineItemsReceivedTitle, lineItemsReceivedShortTitle } from './OrderItemReceivedControls';
import OrderLineItemFlag from './OrderLineItemFlag';
import { isLineFlagged } from '../constants/notifyReasons';
import PaperclipIcon from './ui/PaperclipIcon';
// 💷 THE shared money input. Root `CLAUDE.md`, *Currency Inputs*: "Use the
// shared `components/ui/CurrencyInput` for every monetary input field — import
// it, never re-declare one." Unit Price was a raw `<input type="number">` until
// 2026-09-22, i.e. the one money field on the busiest form in the app was the
// one field not using it.
import CurrencyInput from './ui/CurrencyInput';
import NumberInput from './ui/NumberInput';
import SuggestInput from './ui/SuggestInput';
import OrderSectionHeader, { DESKTOP_SECTION_CHEVRON, DESKTOP_SECTION_TITLE, DESKTOP_SECTION_HEAD, DESKTOP_SECTION_HEAD_BLEED } from './OrderSectionHeader';
import PostApprovalEditDialog from './PostApprovalEditDialog';
import ConfirmDialog from './ConfirmDialog';
import {
  isPostApprovalStatus,
  postApprovalItemLabel,
  removalReasonNote,
  POST_APPROVAL_ACTIONS,
  RECEIVED_ITEM_REMOVAL_NOTICE,
  ONLY_ITEM_REMOVAL,
  ONLY_ITEM_REMOVE_TOOLTIP,
  ONLY_ITEM_REMOVE_DENIED_TOOLTIP,
} from '../constants/postApprovalEdit';
import { buildSupplierOptions, matchSupplierOptions } from '../utils/supplierOptions';
import { useToastOrNoop } from '../contexts/ToastContext';
import { resolveOrderMode, decidingItemIndex, clearConflictingCategories } from '../utils/orderHireMode';
// 🔻 `urgencyOptions` came off this import on 2026-08-09 with the urgency
// picker. It is still EXPORTED from `orderFormHelpers` and still read by
// `constants/statuses.js`'s consumers for LEGACY rows; it is this form that no
// longer offers the choice. Do not re-import it here.
import {
  createEmptyItem,
  newItemClientKey,
  buildPayload,
  validateForm,
  itemCategoryLabel,
  itemCategoryOptions,
} from '../utils/orderFormHelpers';
import { LEAD_TIME_OPTIONS, formatLeadTimeDays, DEFAULT_NEW_ORDER_LEAD_TIME_DAYS } from '../constants/leadTime';
import { hireLineCost, hireBilledLength, hireDetailsLength } from '../utils/hireCost';
import {
  HEADER_DETAILS_PLACEHOLDER,
  HEADER_DETAILS_TOOLTIP,
  HEADER_DETAILS_ROWS,
  HEADER_DETAILS_MAX_LENGTH,
} from '../constants/headerDetails';
import { isPersonalWorkspaceDepartment } from '../utils/personalSpace';
import { ORDER_NOTES_TOOLTIP } from '../constants/orderNotes';
import PhoneActionBar from './ui/PhoneActionBar';
import { OrderFormDecisionContext, PHONE_DECISION_HINT } from './ui/PhoneDecisionBar';
import { useIsMobileOrderViewport } from '../hooks/useIsMobileOrderViewport';
import useOpenAtTop from '../hooks/useOpenAtTop';

// 🔻 `ITEM_FILE_STAGED_HINT` ('Will upload when you save') was DELETED 2026-09-15
// (R13): a file on an unsaved line now uploads at once and reads "Uploaded"
// (`components/PendingUploadRow.js`), so the hint would be false.

/**
 * The tooltip while THIS line's own upload is in flight (immediate mode only).
 * Exported for the same reason as the one above — the test asserts the wording
 * the user sees, not a copy of it.
 */
export const ITEM_FILE_UPLOADING_TOOLTIP = 'Upload in progress for this line…';

// The per-item file row's box, reused by its uploading/failed row so the row
// keeps its size as it turns staged → uploading → listed (2026-09-14).
const ITEM_UPLOADING_ROW_CHROME = 'bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded px-2 py-1.5 text-xs';

/**
 * 📱 THE COLLAPSED LINE-ITEM ROW BELOW `md` — three lines (owner, 2026-10-01).
 * Every class here is a `max-md:` variant, so at `md` and up the row keeps
 * exactly the classes it had; the phone layout is a 3-column grid
 * (`description | (Hire) | chevron`) whose cells are the row's OWN elements,
 * reached through `display: contents` on the clusters. Exported for the test.
 */
export const MOBILE_COLLAPSED_ITEM_ROW =
  'max-md:grid max-md:grid-cols-[minmax(0,1fr)_auto_auto] max-md:gap-x-2 max-md:gap-y-0.5';
export const MOBILE_ITEM_DESCRIPTION =
  'max-md:col-start-1 max-md:row-start-1 max-md:min-w-0 max-md:flex max-md:items-baseline max-md:text-sm max-md:font-medium max-md:text-gray-800 max-md:dark:text-gray-100';
export const MOBILE_ITEM_PART_NUMBER =
  'max-md:col-start-1 max-md:row-start-2 max-md:mr-0 max-md:min-w-0 max-md:truncate max-md:text-xs max-md:text-gray-500 max-md:dark:text-gray-400';
export const MOBILE_ITEM_FLAG =
  'max-md:col-start-2 max-md:col-span-2 max-md:row-start-2 max-md:justify-self-end max-md:ml-0';
export const MOBILE_ITEM_PRICE_LINE =
  'max-md:col-start-1 max-md:row-start-3 max-md:gap-1.5 max-md:min-w-0 max-md:overflow-hidden';
// 📱 2026-10-02 (owner, option B): the tick moved to LINE 2 so line 3 is always the
// aligned Qty / Unit price / bold right-aligned total, whatever the order status.
// A line with a 2nd tick or a notify flag keeps its ticks on their own line (row 4).
export const MOBILE_ITEM_TICK_ZONE =
  'max-md:col-start-2 max-md:col-span-2 max-md:row-start-2 max-md:justify-self-end max-md:ml-0';
/**
 * 📱 A line carrying a SECOND tick (the hires pages' "Off hired?", via
 * `itemHeaderTick`) puts BOTH ticks on their OWN line — row 4, right-aligned
 * — and lets the price line span the whole row (2026-10-01). 📏 Measured at
 * 360px (Chromium, demo seed): with both ticks on row 3 the zone starts at
 * x=169 while "£1,668.00" needs to reach x=184, so the total was clipped to
 * "£1,66"; the price line was also boxed into column 1, which the "(Hire)"
 * tag's column ends at x=161. Lines with one tick keep the 3-line row above.
 */
export const MOBILE_ITEM_TICK_ZONE_OWN_LINE =
  'max-md:col-start-1 max-md:col-span-3 max-md:row-start-4 max-md:justify-self-end max-md:ml-0';
export const MOBILE_ITEM_PRICE_LINE_FULL = 'max-md:col-span-3';

/**
 * 🔒 A LOCKED field (owner 2026-10-03): the order's fields keep the SAME boxes in
 * the SAME places whatever its state, drawn greyed with a padlock, so creating,
 * approving and a locked order share one layout. A tap explains the lock through
 * the form's one notice (`LockedFieldContext`, provided by `OrderForm`).
 * Deliberately NOT `disabled`: a disabled element swallows the tap that explains it.
 */
const LockedFieldContext = React.createContext(null);

export function PadlockIcon({ className = 'h-3.5 w-3.5' }) {
  return (
    <svg className={`${className} shrink-0 text-gray-400 dark:text-gray-500`} viewBox="0 0 20 20" fill="currentColor" aria-hidden="true" focusable="false">
      <path fillRule="evenodd" d="M10 2a4 4 0 00-4 4v2H5a2 2 0 00-2 2v6a2 2 0 002 2h10a2 2 0 002-2v-6a2 2 0 00-2-2h-1V6a4 4 0 00-4-4zm2 6V6a2 2 0 10-4 0v2h4z" clipRule="evenodd" />
    </svg>
  );
}

// 🔗 S1 GAG LINK (2026-10-24) — the quiet "this text is a real gag" marker
// under Job / Rig / Gag. TEXT is the carrier (the supplier picker's "from past
// orders" rule: no colour-only dot, no badge), in the house hint style; the
// chain glyph is decoration and aria-hidden. Shown only while `gag_id` is set.
function GagLinkedMarker() {
  return (
    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1" data-testid="gag-linked-marker">
      <svg aria-hidden="true" viewBox="0 0 20 20" fill="currentColor" className="w-3 h-3 shrink-0">
        <path d="M12.6 4.6a3 3 0 0 1 4.24 4.24l-2.12 2.12a3 3 0 0 1-4.24 0 .75.75 0 0 1 1.06-1.06 1.5 1.5 0 0 0 2.12 0l2.12-2.12a1.5 1.5 0 0 0-2.12-2.12l-.7.7a.75.75 0 1 1-1.07-1.06l.71-.7ZM7.4 15.4a3 3 0 0 1-4.24-4.24l2.12-2.12a3 3 0 0 1 4.24 0 .75.75 0 0 1-1.06 1.06 1.5 1.5 0 0 0-2.12 0L4.22 12.2a1.5 1.5 0 0 0 2.12 2.12l.7-.7a.75.75 0 1 1 1.07 1.06l-.71.7Z" />
      </svg>
      Linked to gag
    </p>
  );
}

// A read-only display value with the same box as an input, greyed and locked.
function ReadOnlyValue({ children, testId }) {
  const onLocked = React.useContext(LockedFieldContext);
  const empty = children == null || children === '' || children === false;
  return (
    <p
      className={`w-full border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/60 rounded-lg px-2 h-11 md:h-8 text-base md:text-sm text-gray-600 dark:text-gray-400 flex items-center gap-2${onLocked ? ' cursor-not-allowed' : ''}`}
      data-testid={testId}
      data-readonly-value=""
      data-readonly-empty={empty ? '' : undefined}
      aria-readonly="true"
      title={onLocked ? 'Locked' : undefined}
      onClick={onLocked || undefined}
    >
      <span className="flex-1 min-w-0 truncate">{children || <span className="text-gray-400 dark:text-gray-500">—</span>}</span>
      {onLocked ? <PadlockIcon /> : null}
    </p>
  );
}

// ── ITEM-BODY GRID COL-SPANS (2026-08-16) ───────────────────────────────
// The item-body grid (Item Category / Cost Code / Payment Type / Part
// Number / Quantity / Unit Price) is `lg:grid-cols-12`, and Item Category
// and Part Number are the two fields that ABSORB whatever Cost Code and
// Payment Type are not occupying when `canViewAccounting` is false — see the
// block comment beside the grid itself for the full arithmetic and the
// owner's quote. Kept as a plain function (never inline JSX ternaries)
// because the two-gate table (accounting × hire) is the same shape
// wherever it is read, and a literal Tailwind class string is returned in
// every branch so `tailwind.config.js`'s content scan finds it — never
// build a `lg:col-span-${n}` string, which the scanner cannot see.
function itemGridSpans(canViewAccounting, isHire) {
  if (canViewAccounting) {
    return isHire
      ? { itemCategory: 'lg:col-span-3', partNumber: 'lg:col-span-3' }
      : { itemCategory: 'lg:col-span-2', partNumber: 'lg:col-span-2' };
  }
  return isHire
    ? { itemCategory: 'lg:col-span-7', partNumber: 'lg:col-span-4' }
    : { itemCategory: 'lg:col-span-5', partNumber: 'lg:col-span-4' };
}

// ORDERS ARE ALL-HIRE OR ALL-NON-HIRE (owner ruling, 2026-09-02) — a
// CONVENIENCE, never a gate (the server enforces independently and
// unconditionally). Owner's words, verbatim: "If the user selects anything
// other than hire then hire in the dropdown should be greyed out with a
// tool tip saying order cannot contain hires and non hires. If hire is
// selected as the first item then all other options should be greyed out
// with the same warning." The DECIDING item (see
// `utils/orderHireMode.js#decidingItemIndex`) is exempt — it is what
// establishes the mode, so its own dropdown stays fully open.
const HIRE_MODE_CONFLICT_TOOLTIP = 'Order cannot contain hires and non hires';
function isCategoryOptionGated(orderHireMode, deciderIndex, index, optionValue) {
  if (orderHireMode === null || index === deciderIndex) return false;
  if (!optionValue) return false; // the blank "-- Select --" option is never disabled
  const optionMode = optionValue === 'Hire' ? 'hire' : 'non_hire';
  return optionMode !== orderHireMode;
}

// The truncatable selects' tooltip text: the SELECTED option's own label, so
// hovering a clipped `<select>` reveals what native `title` would otherwise
// only show for a plain text node. Returns `undefined` (never `''`) when
// nothing is selected, so it spreads into `title=` as NO attribute — the
// `dueIndicatorTitle` convention (`CLAUDE.md`, the `titleOnParent` entry) —
// rather than an empty popup box.
function selectedOptionLabel(list, id, format) {
  if (id == null || id === '') return undefined;
  const found = (list || []).find((x) => x.id === id);
  return found ? format(found) : undefined;
}

// Local storage keys for remembered fields (NewOrder uses these via the
// `persistFieldsToLocalStorage` prop; OrderPage's edit mode does not).
const LS_KEYS = {
  requestedBy: 'sfx_requested_by',
  department: 'sfx_department',
  buyerEmail: 'sfx_buyer_email',
  userPhone: 'sfx_user_phone',
};

const EMPTY_FORM = {
  production_id: '',
  requested_by: '',
  po_number: '',
  // 🔻 `personal_ref` came off this object on 2026-08-13 — the ORDER-level
  // field was retired (already gone from the title and from search; the input
  // and the PDF row went with this change). The DB column STAYS and the server
  // keeps it: `PUT /api/orders/:id` preserves the stored value when the field
  // is absent from the body (routes/orders.js — `personal_ref !== undefined ?
  // … : order.personal_ref`), so removing it here stops the client sending it
  // without blanking legacy data. NOT the user-level GDPR `users.personal_ref`
  // (MySettings/PrivacyPolicy), which is a different column and untouched.
  department: '',
  sub_department: '',
  job_rig: '',
  // S1 gag link: the picked gag's id, or null for free text (the server
  // auto-links an exact same-department title match). Sent by buildPayload's
  // spread of `form`.
  gag_id: null,
  supplier_name: '',
  only_use_this_supplier: false,
  // 🔻 `null`, NOT `'7_days'` (2026-08-09, lead-time increment 3). The urgency
  // picker is gone and urgency is derived from slack behind the scenes, so a
  // new order must store NO urgency — leaving the old default here would go on
  // stamping `7_days` on every order raised through a form that no longer
  // offers the choice, which is worse than the picker was: an invented deadline
  // with nobody's decision behind it. The COLUMN and its CHECK stay and NULL is
  // admitted; `orders.urgency` is still READ as the deadline fallback for the
  // 1,736 rows that carry one.
  urgency: null,
  // THE ORDER-LEVEL NEEDED-BY DATE. The field name is legacy — see the column
  // comment in server/database.js — and it is now an ordinary always-visible
  // order-level field rather than something the `custom` urgency option
  // revealed.
  custom_needed_by_date: '',
  // ORDER-LEVEL lead time, in whole days (2026-08-08). Held as a STRING in form
  // state, like every other `<select>` here, because that is what the DOM gives
  // back; `buildPayload` is the one place it becomes a number again.
  // 🪤 `''` is "not stated" and is NOT interchangeable with `'0'`, which is
  // "Same day". Every falsy test written against this field is a bug.
  //
  // 🚨 **THIS STAYS `''` — THE NEW-ORDER DEFAULT IS *NOT* APPLIED HERE, AND
  // PUTTING IT HERE WOULD MAKE IT INVISIBLE TO ALMOST EVERY USER.** See
  // `buildInitialForm` below: this object is only reached when `initialOrder` is
  // falsy, and `pages/NewOrder.js` hands this form a truthy `initialOrder` on
  // essentially every real mount (remembered localStorage fields at `:64`, the
  // auth-user `requested_by` seed at `:76`, "Create Another" at `:315`). A
  // default written here alone would show up only for a brand-new browser
  // profile belonging to a user with no display name — i.e. it would be the
  // invisible default this feature exists to remove, wearing a fresh coat.
  lead_time_days: '',
  buyer_email: '',
  user_phone: '',
  notes: '',
  // MY SPACE "HEADER DETAILS" (2026-09-11) — the free-text block printed under
  // the ORDER REQUEST title.
  // 🚨 `''`, AND NEVER THE EXAMPLE. The owner asked for *"faded grey text
  // showing an example"*, which is a `placeholder` attribute; seeding
  // `HEADER_DETAILS_PLACEHOLDER` here instead would look identical on screen,
  // would still "disappear when the user types", and would PRINT an invented
  // delivery address on the first order of anyone who did not clear it — while
  // also contradicting his third decision that the stored default starts blank.
  // Pinned by §3.1 of `OrderForm.headerDetails.test.js`, which asserts the
  // placeholder attribute and the empty VALUE together; either alone passes on
  // the defect.
  header_details: '',
};

/**
 * @param {Object|null} initialOrder
 * @param {'create'|'edit'} mode The form's mode. **REQUIRED for the lead-time
 *   default to be correct** — see below. Exactly two values reach here:
 *   `pages/NewOrder.js:424` passes `'create'`, `pages/OrderPage.js:1078` and
 *   `components/OrderInlineEditor.js:324` pass `'edit'`. There is no third
 *   caller and no third value, which is what makes the gate below total.
 */
function buildInitialForm(initialOrder, mode) {
  // ── THE ORDER-LEVEL LEAD-TIME DEFAULT (2026-08-09, product-owner ruling) ────
  // Product owner: *"default lead time should be set to 3 days as its ther
  // orderes responsability to state if its longer. I dont want to nag the
  // buyer"*. The value and the full reasoning live on
  // `DEFAULT_NEW_ORDER_LEAD_TIME_DAYS` in `constants/leadTime.js`, which is the
  // one definition of what a user may pick — **never write a literal `3` here.**
  //
  // 🚨 **GATED ON `mode`, AND THAT IS WHAT KEEPS 1,113 LEGACY ORDERS STILL.** A
  // default is for a NEW order. Applying it on the edit path would stamp a
  // three-day lead time on every legacy order the moment somebody opened it and
  // saved an unrelated field — the exact "an edit invents a value nobody
  // entered" defect that `buildInitialItems` below was fixed for hours earlier,
  // arriving through the order-level door instead of the per-item one. An edit
  // of an order with no stated lead time therefore still opens on `''`.
  //
  // 🔑 **IT IS APPLIED IN BOTH BRANCHES, NOT IN `EMPTY_FORM`.** The `!initialOrder`
  // branch is very nearly unreachable in production — see the note on
  // `EMPTY_FORM.lead_time_days` — so a default that lives only there is a
  // default nobody sees. This expression is the whole of the feature.
  //
  // ⚠️ **A DEFAULT, NOT A REMEMBERED VALUE — decided, not overlooked.** "Create
  // Another" (`pages/NewOrder.js:315`) is still `mode === 'create'`, so each
  // order in a run re-opens on 3 days even if the previous one was set to 3
  // weeks. Carrying the choice forward would follow this file's own
  // `sfx_requested_by` / `sfx_department` precedent, and it was DECLINED: those
  // remembered fields are facts about the PERSON raising the order and are
  // stable between orders, whereas a lead time is a fact about the PURCHASE and
  // is exactly what varies. Carrying it would silently apply one order's
  // decision to a different order — a value nobody chose for THIS order, which
  // is the defect class this change closes, inverted. The control is visible and
  // one click to change. **Flagged to the owner as the alternative; if he wants
  // it, it is a REMEMBERED value under a new `sfx_`-prefixed key, never a second
  // stored default.**
  const createDefault = mode === 'create' ? String(DEFAULT_NEW_ORDER_LEAD_TIME_DAYS) : '';

  if (!initialOrder) return { ...EMPTY_FORM, lead_time_days: createDefault };
  return {
    production_id: initialOrder.production_id || '',
    requested_by: initialOrder.requested_by || '',
    po_number: initialOrder.po_number || '',
    // `personal_ref` is deliberately NOT hydrated — see EMPTY_FORM. An edit
    // save therefore omits it from the payload and the server round-trips the
    // stored value untouched.
    department: initialOrder.department || '',
    sub_department: initialOrder.sub_department || '',
    job_rig: initialOrder.job_rig || '',
    gag_id: initialOrder.gag_id ?? null,
    supplier_name: initialOrder.supplier_name || '',
    only_use_this_supplier: !!initialOrder.only_use_this_supplier,
    // 🚨 `|| null`, NOT `|| '7_days'`. The old default MANUFACTURED an urgency
    // on any order that had none, so opening and saving such an order would
    // have written a deadline nobody chose — the "editing an unrelated field
    // silently stamps a value" shape. It round-trips a legacy value UNCHANGED,
    // which is the whole of "stop writing urgency, keep reading it": the form
    // still carries the value in state, it just has no control that can alter
    // it. Do not simplify this away as an unused field.
    urgency: initialOrder.urgency || null,
    custom_needed_by_date: initialOrder.custom_needed_by_date || '',
    // 🚨 NOT `initialOrder.lead_time_days || ''`. `0` is "Same day" and is
    // falsy, so the idiomatic form used by every line above it would open the
    // edit form on a same-day order showing "-- Select --", and saving would
    // then write the lead time away. This is the third site in this feature
    // where the house idiom is wrong for this one field.
    //
    // 🔑 `createDefault` — NOT `''` — is the miss branch since 2026-08-09, and
    // it is `''` on the edit path, so this line is byte-identical in behaviour
    // for every existing order. A STORED value always wins, including a stored
    // `0`; the default only fills a genuine absence, and only when creating.
    lead_time_days: initialOrder.lead_time_days == null
      ? createDefault
      : String(initialOrder.lead_time_days),
    buyer_email: initialOrder.buyer_email || '',
    user_phone: initialOrder.user_phone || '',
    notes: initialOrder.notes || '',
    // MY SPACE "HEADER DETAILS" — hydrated from THIS ORDER'S stored snapshot,
    // never from the user's default (owner decision 1: the edit page changes
    // only that order). `|| ''` is safe here and is NOT the `lead_time_days`
    // falsy trap: this is free text, `''` is the empty control, and there is no
    // legitimate falsy value to lose.
    //
    // 🔑 ON CREATE it comes through `initialOrder` too, seeded by
    // `pages/NewOrder.js` from `GET /api/users/me` — the same channel every
    // other remembered create-path field uses. That is why there is no
    // `mode === 'create'` branch here: the value is a REMEMBERED default the
    // user typed, not one this form invents, so the `DEFAULT_NEW_ORDER_LEAD_TIME_DAYS`
    // reasoning above does not apply.
    header_details: initialOrder.header_details || '',
  };
}

/**
 * NOTIFY USER — the server-owned line state, keyed by item id.
 *
 * 🚨 **THIS IS DELIBERATELY NOT PART OF `items`, AND MERGING IT IN WOULD BE A
 *    MONEY-SHAPED BUG WEARING A TIDY-UP'S CLOTHES.** `items` is the EDITABLE
 *    model: `buildPayload` serialises it and `PUT /api/orders/:id` writes what
 *    it is given. `notify_reasons` / `notify_note` are written ONLY by
 *    `POST|DELETE /api/orders/:id/items/:itemId/notify`, so putting them in
 *    `items` would (a) send them on every unrelated save and (b) trip §6.6 of
 *    `OrderForm.leadTime.test.js`, the CLASS ratchet that pins the WHOLE payload
 *    item so a new invented field fails CI until somebody justifies it.
 *
 * 🔑 That ratchet is the reason this shape exists rather than the reason it is
 *    awkward — it is the repo's own rule working: *"an item the user did not
 *    touch must be written back exactly as it came, and every default a form IS
 *    allowed must be declared."* This form is allowed no notify default at all.
 */
function buildNotifyRows(initialOrder) {
  const rows = {};
  (initialOrder?.items || []).forEach((item) => {
    if (item && item.id != null) {
      rows[item.id] = { notify_reasons: item.notify_reasons, notify_note: item.notify_note };
    }
  });
  return rows;
}

function buildInitialItems(initialOrder) {
  if (!initialOrder || !initialOrder.items || initialOrder.items.length === 0) {
    return [createEmptyItem()];
  }
  return initialOrder.items.map(item => {
    const rateType = item.hire_rate_type || (item.daily_rate ? 'daily' : item.weekly_rate ? 'weekly' : 'total');
    const rateValue = rateType === 'daily' ? (item.daily_rate || 0) : rateType === 'weekly' ? (item.weekly_rate || 0) : (item.total_hire_rate || 0);
    return {
      id: item.id, // preserved on edit; undefined on create-from-duplicate (server treats absent id as new)
      // A line with no id is NEW to the server, so it gets its own key — see
      // `newItemClientKey`. A saved line never carries one.
      ...(item.id == null ? { client_key: newItemClientKey() } : {}),
      part_number: item.part_number || '',
      description: item.description || '',
      quantity: item.quantity || 1,
      unit_price: item.unit_price || 0,
      needed_by_date: item.needed_by_date || '',
      // 🚨 NOT `item.lead_time || 'Unknown'`. THAT INVENTED A LEAD TIME NOBODY
      // ENTERED, AND THE INLINE EDITOR MADE IT UNAVOIDABLE — 2026-08-09.
      //
      // `order_items.lead_time` is NULL on nearly every row (25 populated in the
      // whole dev database). Hydrating NULL as the string `'Unknown'` meant a
      // buyer could expand a row, change a PO number or a supplier — something
      // COMPLETELY UNRELATED — save, and `buildPayload` would send `'Unknown'`
      // for every line item. `PUT /api/orders/:id` stores the literal string,
      // and `LEAD_TIME_PRESETS` (server/helpers/leadTimeHelpers.js:27) maps it
      // to **3 days**. The order silently acquired a three-day lead time: slack
      // changes, the tooltip starts asserting ", 3 day lead time", and the row
      // can appear in the amber urgency badge. Nothing warned, the field-level
      // audit diff recorded no lead-time change because the FORM had already
      // changed it before the diff saw it, and — with no empty option on the
      // `<select>` — it was not reversible through the form.
      //
      // 🔑 It contradicted the contract this feature shipped with: `leadDays:
      // null` means "NOT STATED, A DEFAULT WAS ASSUMED", and exists precisely so
      // the UI never presents an assumption as data.
      //
      // ⚠️ `== null`, not `||`. An empty string is already "not stated" and must
      // stay `''`; the point of the guard is that NULL and `undefined` — the
      // shapes a legacy row actually arrives in — round-trip as unstated rather
      // than being renamed. `buildPayload` passes `''` through and the server
      // writes `item.lead_time || null` (routes/orders.js:1976 UPDATE, :1996
      // INSERT), so an untouched item is written back exactly as it came.
      //
      // 🔴 THIS DOES NOT CHANGE WHAT 'Unknown' MEANS. A human who really did
      // pick it still stores `'Unknown'` and still resolves to 3 days — that is
      // a stated answer ("I do not know how long"), which is a different fact
      // from no answer, and the server helper's own docblock turns on the
      // distinction. Only the DEFAULT changed.
      lead_time: item.lead_time == null ? '' : String(item.lead_time),
      is_hire: !!item.is_hire,
      hire_from: item.hire_from || '',
      hire_to: item.hire_to || '',
      hire_rate_type: rateType,
      hire_rate: rateValue,
      daily_rate: item.daily_rate || 0,
      weekly_rate: item.weekly_rate || 0,
      total_hire_rate: item.total_hire_rate || 0,
      item_notes: item.item_notes || '',
      item_category: item.item_category || '',
      cost_code_id: item.cost_code_id || '',
      payment_type_id: item.payment_type_id || '',
    };
  });
}

/**
 * Shared order form used by NewOrder (create) and OrderPage (edit mode).
 *
 * Parent owns: data loading, API submission, success/redirect UX, modals
 *              (cancel/post-approval), file upload (create only today).
 * This component owns: form/items/errors/collapsed state, validation, layout.
 *
 * On submit, this component validates and (if valid) calls
 *   onSubmit(payload, { form, items })
 * with the assembled API payload. The parent does the actual fetch.
 */
function OrderForm({
  mode,
  initialOrder = null,
  productions = [],
  suppliers = [],
  // Supplier names typed on PAST ORDERS that reached approval
  // (`GET /api/suppliers/name-suggestions`). ADDITIVE and optional — defaults to
  // `[]`, so a caller that does not pass it renders exactly what it rendered
  // before this prop existed.
  //
  // 🚨 THEY ARE NOT `suppliers` ROWS AND MUST NEVER BE TREATED AS ONE. A
  // suggestion has a name and nothing else; a record has an account number and a
  // contact. Picking either just puts a string in `supplier_name` (free text on
  // `orders`), and NOTHING on this form creates a supplier — that absence is the
  // product decision, see `utils/supplierOptions.js`.
  supplierSuggestions = [],
  // ═════════════════════════════════════════════════════════════════════════
  // 🔀 `headerActions` / `headerActionsBelow` (2026-08-11) — THE ORDER-ROW
  //    ACTION GROUP, RENDERED INSIDE THE `Order Details` HEADER.
  // ═════════════════════════════════════════════════════════════════════════
  // Product owner: *"I get why the action menu is on the left as it matches to
  // approve which looks ok as it has the two other buttons with it but on
  // department orders it just looks in the wrong place. lets try moving the
  // 'Reject Approve Actions' into the Order Details header"*.
  //
  // 🚨 THIS FORM RESOLVES NO PERMISSIONS AND BUILDS NO ITEMS. It receives a
  // FINISHED, ALREADY-GATED node and renders it — the same contract
  // `components/OrderActionsMenu.js` and `components/OrderTableView.js` have,
  // and for the same reason: the moment a shared component can decide who sees
  // an action, the pages that use it can disagree about who may do what.
  //
  // 🔑 ADDITIVE AND OPTIONAL, defaulting to `null` — the same shape
  // `supplierSuggestions` above uses. **With `headerActions` absent the header
  // renders the plain `<h3>` it has always rendered, byte for byte**, which is
  // what keeps `/orders/new` (`pages/NewOrder.js`) and `/orders/:id/edit`
  // (`pages/OrderPage.js`) untouched by this change. Only `OrderInlineEditor`
  // passes it, and only from the two order-list pages' EDIT branch.
  //
  // 🪤 `headerActionsBelow` EXISTS BECAUSE OF ONE DOCUMENTED INVARIANT AND MUST
  //    NOT BE FOLDED INTO `headerActions`. `Approvals.js`'s reject flow is two
  //    steps — click `Reject`, a reason textarea appears, `Confirm Reject`
  //    consumes it — and `Approvals.js:1671-1676` states in terms that the
  //    textarea *"MUST STAY DIRECTLY BENEATH"* the buttons, because "the
  //    control that REVEALS it and the control that CONSUMES it must not be
  //    separated by a panel's height". Moving the buttons into this header
  //    without moving the textarea would have left the reveal ABOVE the button
  //    that revealed it, on the far side of the whole form card. It renders
  //    immediately under the header bar, inside the same card. A textarea
  //    cannot go IN the bar, which is why this is a second slot and not one
  //    node.
  headerActions = null,
  headerActionsBelow = null,
  // ═════════════════════════════════════════════════════════════════════════
  // 🔀 `orderDetailsRow2Lead` / `orderDetailsRow2Rest` (2026-09-13 owner
  //    ruling, SECOND PASS) — A SECOND ROW OF THE `Order Details` GRID,
  //    DIRECTLY UNDER PO NUMBER.
  // ═════════════════════════════════════════════════════════════════════════
  // Owner, verbatim: *"put 'expected delivery date' on a new row under PO
  // Number. Internal notes goes next to Expected delivery date"*. This
  // SUPERSEDES the same-day morning shape, where Expected Delivery was a
  // HALF cell tacked onto the end of row 1 (`orderDetailsExtraField`) and
  // Internal notes was a full-width sibling BELOW the whole grid
  // (`orderDetailsFooter`) — renamed here because both names described that
  // superseded geometry and would mislead about this one.
  //
  // 🔑 ADDITIVE AND OPTIONAL, both defaulting to `null`, exactly like the two
  // header slots above and `supplierSuggestions`. **With both absent the
  // section renders byte-identical to what it rendered before either prop
  // existed** — which is what keeps `pages/NewOrder.js` and
  // `pages/OrderPage.js` untouched — asserted against the shipped markup
  // rather than claimed.
  //
  // `orderDetailsRow2Lead` — the FIRST cell of row 2: `xl:col-start-1
  //   xl:row-start-2 xl:col-span-3` — a fixed THREE tracks so its label and
  //   helper note never wrap, starting in PO Number's column directly above
  //   it. Carries the expected-delivery editor-or-badge.
  // `orderDetailsRow2Rest` — the SECOND cell of row 2: `xl:col-start-4
  //   xl:row-start-2`, spanning whatever is left of the row (N−3 tracks when
  //   the lead cell renders, the full N from col 1 when it does not — see
  //   `orderDetailsRow2RestClass` below). Carries Internal notes.
  // Absent lead, present rest → rest starts at col 1 and spans the full row.
  // Present lead, absent rest → lead alone; the rest of the row stays empty.
  // Absent both → row 2 is simply never rendered (no wrapper, no row).
  //
  // 🚨 **ROW 1's TRACK COUNT NO LONGER VARIES WITH EITHER OF THESE SLOTS.**
  // A second grid ROW costs no columns at all — `orderDetailsGridColsClass`
  // is back to exactly its pre-2026-09-13 arithmetic (13 / 12 / 11 / 10, by
  // `isAdmin` and `showPoNumberField` alone), because CSS Grid's implicit
  // rows share the SAME column tracks the explicit `repeat(N, …)` declares;
  // row 2's cells are placed with `xl:col-start`/`xl:row-start` into those
  // same N tracks rather than adding a fourteenth.
  //
  // 🚨 **STILL NAMED SLOTS, NOT A GENERIC ARRAY, FOR THE SAME REASON AS
  // BEFORE.** Tailwind's scanner reads source TEXT, so the row-2-rest span
  // class must be one of a bounded set of LITERALS (eight of them — one per
  // `isAdmin` × `showPoNumberField` × "lead present?" combination), never a
  // class built from a variable at runtime.
  //
  // 🔑 BOTH ARE FINISHED, ALREADY-GATED NODES AND THIS FORM RESOLVES NOTHING
  // ABOUT EITHER — it never reads `expected_delivery_date` or `admin_notes`,
  // never decides who may see either. Same contract as `headerActions`.
  //
  // 🪤 **A NODE THAT RENDERS NOTHING IS STILL TRUTHY.** The caller decides
  // "will it render?" itself (`OrderExpandedRow` gates on
  // `detail.expected_delivery_date`, never on the element) — passing a
  // component that returns `null` still occupies row 2's lead cell.
  orderDetailsRow2Lead = null,
  orderDetailsRow2Rest = null,
  // ═════════════════════════════════════════════════════════════════════════
  // 🔀 `poNumberFieldSlot` (2026-09-13) — THE PO CELL'S CONTENTS, SUPPLIED BY
  //    THE CALLER.
  // ═════════════════════════════════════════════════════════════════════════
  // It exists because the order-list pages edit PO through a dedicated
  // endpoint (`PATCH /:id/po-number`, `components/InlinePoNumberEditor.js`)
  // that this form has no field for, and until now they rendered that editor
  // OUTSIDE the form while passing `hidePoNumberField` to stop a second PO
  // control appearing beside it. It replaces the CONTENTS of the PO cell —
  // never its position, never its grid track (col 1, row 1).
  //
  // 🔑 With the slot supplied the cell renders REGARDLESS of
  // `hidePoNumberField` — that prop means "do not render a SECOND control",
  // and a caller handing its control IN has no second control to avoid.
  //
  // 🔑 A FINISHED, ALREADY-GATED NODE, SAME CONTRACT AS THE ROW-2 PAIR ABOVE.
  // Default `null`; absent, this section is byte-identical to what shipped.
  poNumberFieldSlot = null,
  gags = [], // gag names for the Job / Rig / Gag autocomplete (free-text still allowed)
  subDepartments = [],
  costCodes = [],
  paymentTypes = [],
  // ═════════════════════════════════════════════════════════════════════════
  // 🚨 `loadFailures` (2026-08-22) — WHICH OF THE ARRAYS ABOVE ARE EMPTY
  //    BECAUSE NOBODY ASKED, RATHER THAN BECAUSE THERE IS NOTHING THERE.
  // ═════════════════════════════════════════════════════════════════════════
  // `{ suppliers?, costCodes?, paymentTypes?: boolean }`. Additive and optional,
  // defaulting to `{}`, so a caller that passes nothing renders exactly what it
  // rendered before this prop existed.
  //
  // 🔑 THIS PROP EXISTS BECAUSE THE FAILURE HAPPENS IN THE PARENT AND THE FALSE
  // CLAIM IS RENDERED HERE, AND AN EMPTY ARRAY IS NOT ENOUGH INFORMATION TO
  // CROSS THAT BOUNDARY. Every one of the five parents — `NewOrder.js`,
  // `DepartmentOrders.js`, `Approvals.js`, `MyOrders.js`, `OrderPage.js` —
  // hands this form `[]` on a dropped connection, and `[]` is also exactly what
  // a department with no supplier records legitimately sends. So this component
  // rendered *"No suppliers set up for this department yet"* — wording chosen
  // (see the render site) to send the user off to CREATE supplier records —
  // to a buyer whose request had simply been refused. Duplicate `suppliers`
  // rows are a measured live-production hazard on this instance: the 2026-08-16
  // Laird import forced a department-scoped uniqueness fix on production.
  //
  // 🚨 DO NOT REPLACE THIS WITH AN INFERENCE FROM THE ARRAY. "Empty" and
  // "unasked" are the same value; only the parent knows which happened, so only
  // the parent can say. A heuristic here (a timer, a "still empty after N
  // renders" guess) would be a second, quieter version of the same bug.
  loadFailures = {},
  userAssignedProductions = null, // null = admin/loading, [] = none, [...] = list
  originalStatus = '',
  submitting = false,
  submitError = '',
  submitLabel = 'Submit Order',
  // 🔒 DISARMS SUBMIT WITHOUT CLAIMING ONE IS IN FLIGHT (2026-08-29). Separate
  // from `submitting` on purpose: `pages/NewOrder.js` sets this while its
  // green success panel is showing (the form itself stays visible on desktop,
  // `hidden md:block` only), so a deliberate second press cannot re-run
  // `handleSubmit` and mint a fresh `Idempotency-Key` for a real second
  // `POST /api/orders`. Folding it into `submitting` would leave the button
  // reading "Submitting..." forever after a genuine success — its own
  // defect. Re-armed the moment the parent clears it (`handleCreateAnother`).
  // Default `false` so every existing caller is byte-for-byte unaffected.
  submitDisabled = false,
  // Optional native `title` shown ONLY while `submitDisabled` is the reason
  // the button is dead — a disabled control with no explanation reads as
  // broken. `pages/NewOrder.js` points it at "Create Another Order" rather
  // than merely restating "already submitted", since that is the control the
  // user actually wants next.
  submitDisabledHint = null,
  // OPTIONAL (2026-09-14, review M3): disables the Discard/Cancel button — the
  // inline editor sets it while a save and its attachment uploads are in
  // flight, when the order is already saved and there is nothing to discard.
  // Default false → byte-identical for every other caller.
  cancelDisabled = false,
  onSubmit,
  onCancel = null,
  showItemFileUpload = false,
  showOrderFileUpload = false,
  persistFieldsToLocalStorage = false,
  // File upload (the parent owns the requests — we just render the UI).
  // 🔻 THE INDEX-KEYED STAGED MODE IS GONE (2026-09-15, R13): `orderFiles`,
  // `itemFiles`, `onItemFilesChange`, `existingItemFiles` and `uploadProgressFor`
  // were DELETED once all three callers moved to `pendingFiles` (upload at pick,
  // claim on save) or `itemFilesImmediate` (by the item's own id). A file keyed
  // by its line's POSITION is how attachments misfiled — do not bring it back.
  // `onOrderFilesChange(files)` — an IMMEDIATE order-level upload (a surface
  // without `pendingFiles`); `existingOrderFiles` are the saved order-level rows;
  // `onExistingFileToggleRemove(fileId)` removes (or marks for removal) a saved file.
  onOrderFilesChange = null,
  existingOrderFiles = [],
  pendingDeleteFileIds = null,
  onExistingFileToggleRemove = null,
  // ── IMMEDIATE PER-ITEM ATTACHMENTS (2026-09-13) ─────────────────────────
  // ADDITIVE and optional, defaulting `null` → `pages/NewOrder.js` and
  // `pages/OrderPage.js` pass nothing and render byte-for-byte what they
  // rendered before. When supplied (and `showItemFileUpload`), the per-item
  // control switches from STAGING to an immediate upload:
  //   { filesByItemId: { [itemId]: [savedFile] },
  //     onUpload: (itemId, files) => void,
  //     uploadingItemIds: Set<itemId>,   // in-flight, per line
  //     canRemoveFile: (file) => boolean,
  //     uploadingEntriesFor: (itemId) => [{ key, file, fraction, error }],
  //     onDismissUpload: (key) => void }  // a saved line's bars (2026-09-14)
  //
  // 🚨 **IT RESOLVES THE TARGET BY THE ITEM'S OWN `id`, NEVER BY INDEX.** An
  // upload fires mid-edit, when the user may already have added or removed a
  // line — so a position captured by the parent from the stored order can name a
  // different row than the one the paperclip sits on, and the file lands on the
  // wrong line silently. `items[index].id` is the only value that cannot drift,
  // and only this component holds it. (The index-keyed staged props this
  // contrasted with were deleted 2026-09-15.) Since 2026-09-15 `pages/OrderPage.js`
  // passes it too: with `onUpload` in view mode, without it in edit mode — where
  // a pick goes to `pendingFiles` instead.
  //
  // 🔀 **A LINE WITH NO `id` YET (added during this edit)** was a DISABLED
  // control until 2026-09-14, then STAGED its files in the browser until Save;
  // since 2026-09-15 (R13) it UPLOADS AT ONCE through `pendingFiles` (below) and
  // `OrderInlineEditor`'s save claims it onto the line's new id.
  //
  // 🪤 **`uploadingItemIds` DISABLES A LINE WHILE ITS OWN UPLOAD IS IN FLIGHT
  // (review F3, 2026-09-13)** — the input is cleared after every pick so an
  // identical re-pick fires a second `change`, and a double-pick attached the
  // file twice. It disables the BUTTONS and the INPUT, and a DROP is refused
  // inside `acceptItemFiles` — never by detaching the drop zone, which would
  // hand the file to the BROWSER (see the drop zone's own comment).
  //
  // 🔑 REMOVE stays `onExistingFileToggleRemove(fileId)` — the same callback the
  // staged callers use — narrowed per file by `canRemoveFile`, because
  // `DELETE /api/orders/:id/files/:fileId` decides PER FILE server-side. There
  // is no `pendingDeleteFileIds` in this mode: the delete is immediate, so there
  // is no pending state to render and no "Undo".
  itemFilesImmediate = null,
  // ── PENDING UPLOADS (2026-09-15, R13) ──────────────────────────────────
  // OPTIONAL — the `hooks/usePendingOrderUploads.js` object the PARENT owns.
  // Owner: *"upload the file straight away, delete it if the user does not
  // save the line or they delete the line"*. When supplied, a pick with no
  // immediate target (a line with no id; a saved line on a surface without
  // `itemFilesImmediate.onUpload`; the order-level list) UPLOADS AT ONCE via
  // `pendingFiles.add(lineKey, files)`, keyed by `pendingLineKeyFor(item)` —
  // the item's id, else its `client_key`, NEVER its index — and the parent's
  // save claims them. Rows render as `PendingUploadRow` ("Uploaded", never
  // "Staged"). Removing a line calls `pendingFiles.removeLine`, which deletes
  // its uploads on the server. `pendingFilesDisabled` makes the controls inert
  // while the parent's save + claims run.
  pendingFiles = null,
  pendingFilesDisabled = false,
  // When true: render plain text instead of inputs/selects/checkboxes.
  // Add/Remove item buttons hidden, submit row hidden, validation skipped.
  // Used by the unified OrderPage in view mode.
  readOnly = false,
  // ── READ-ONLY-ONLY ESCAPE HATCHES (2026-08-16) ──────────────────────────
  // Added so `OrderExpandedRow`'s read-only branch can render this SAME form
  // instead of its own bespoke panel, without losing the manager-only
  // controls that panel had — controls that write through DEDICATED
  // endpoints and therefore work on a non-editable order regardless of the
  // edit window (unlike everything else in this form, which only ever
  // reaches the server through the single PUT this component builds). All
  // four default to preserving TODAY's readOnly behaviour; every existing
  // caller (`NewOrder.js`, `OrderPage.js`'s own view mode, `OrderInlineEditor`)
  // passes none of them and is byte-for-byte unaffected.
  //
  // `hidePoNumberField` — when true AND readOnly, the PO Number field is
  // suppressed entirely (not even as plain text), so a caller with its own
  // live PO editor (`InlinePoNumberEditor`, `PATCH /:id/po-number`) never
  // ends up rendering PO twice. Only ever passed by `OrderExpandedRow`'s
  // read-only branch, and only for a manager (`canManage`).
  hidePoNumberField = false,
  // `onReadOnlyCostCodeChange(itemId, value)` / `onReadOnlyPaymentTypeChange(itemId, value)`
  // — when supplied AND readOnly AND the viewer can manage this order's
  // department, the item's Cost Code / Payment Type render as a LIVE
  // `<select>` instead of static text, wired to these handlers instead of
  // `updateItem` (which only ever writes to LOCAL form state that a
  // read-only form never submits). The same "dedicated endpoint works on a
  // non-editable order" pattern as PO — `PATCH /:id/items/:itemId/cost-code`
  // and `…/payment-type`. Default null preserves today's readOnly behaviour
  // (always static text) for every caller that omits them, including
  // `OrderPage.js`'s own view mode.
  onReadOnlyCostCodeChange = null,
  onReadOnlyPaymentTypeChange = null,
  // `onReadOnlyItemNotifyUpdated(updatedItem)` — when supplied AND readOnly,
  // the per-item Notify/flag menu (`OrderItemActionsMenu`, which writes
  // `POST|DELETE /:id/items/:itemId/notify`) stays live instead of being
  // suppressed outright. Default null preserves today's readOnly behaviour
  // (menu hidden, no Remove either) for every other caller.
  onReadOnlyItemNotifyUpdated = null,
  // `itemExtraActions(item, index)` → FINISHED, already-gated items for that
  // line's `OrderItemActionsMenu`, placed BEFORE Remove (2026-09-28 — the
  // hires pages' Extend / Off hire / Bring back, via `OrderExpandedRow`).
  // Reaches the menu in BOTH modes; it cannot make the menu appear on its own
  // (`showsItemActionsMenu` is unchanged). Default null → byte-identical.
  itemExtraActions = null,
  // `itemHeaderTick(item, index)` → a FINISHED, already-gated tick for that
  // line's header, or null (2026-09-29 — the hires pages' per-line "Off
  // hired" tick, via `OrderExpandedRow`): `{ checked, disabled, title,
  // onChange, testId, ariaLabel, label }`. Rendered beside the received tick,
  // beside the line's Ordered / Delivered controls (`LineOrderedDeliveredControls`). This component
  // decides nothing about it. Default null → byte-identical.
  itemHeaderTick = null,
  // When true, every line item starts in collapsed state on mount (e.g. when
  // OrderPage loads an existing order). NewOrder leaves this off — items are
  // empty there and collapsing them would just hide the form.
  initialItemsCollapsed = false,
  // OPTIONAL received-tick plumbing (2026-08-15). Product owner: *"I would
  // like the tick boxes to go in the collpased line items in the 'Line items'
  // section maybe between the price and the actions menu so they can be
  // ticked off without haveing to expand the order."* Default null → NewOrder
  // and Approvals are byte-for-byte unaffected (pinned): no checkbox, no
  // Select-all, and the section header keeps today's markup — which is what
  // keeps CREATE mode honest, since a new line has no `order_items.id` and no
  // received state to show. 🔻 `OrderPage.js` DID default to null here until
  // 2026-08-19 (it had its own separate, since-deleted checkbox); it now
  // passes the same prop shape DepartmentOrders' `OrderExpandedRow` builds.
  //
  // Shape (built by the PAGE — DepartmentOrders — beside its other gates, and
  // forwarded untouched by OrderInlineEditor; this component decides nothing):
  //   {
  //     items: { [order_items.id]: { is_received, received_date, received_by_name,
  //                                  is_ordered, ordered_date, ordered_by_name } },
  //             // from the DETAIL payload, never form state — an unsaved new
  //             // line has no entry and gets no step control
  //     orderedCount, receivedCount, total,   // ditto: the DETAIL's counts
  //     canOrder, orderLockedReason, // 🔀 2026-10-28: may this viewer mark a
  //                             // line ORDERED (order management in the
  //                             // order's dept) — resolved by the PAGE
  //     busy,                   // a mutation is in flight — disable the ticks
  //     locked, lockedReason,   // a page-level lock (OrderPage's stale read);
  //                             // the 2026-08-16 order-level "approved" lock
  //                             // went per LINE on 2026-10-28 (ruling A)
  //     onToggleItem(itemId, currentReceived),  onReceiveAll(nextReceived),
  //     onToggleOrdered(itemId, currentOrdered), onOrderAll(nextOrdered),
  //   }
  // The page passes it ONLY when the order is delivery-stage
  // (DELIVERY_STAGE_STATUSES) AND the viewer passes `canReceiveItems`, so
  // presence ⇒ the "Line Items — ordered x/N · delivered y/N" title. No permission and no
  // status predicate is re-derived here.
  received = null,
  // OPTIONAL (2026-09-13) — the order's status, handed down by an EDIT caller
  // (`OrderInlineEditor`, `pages/OrderPage.js`) so that removing a SAVED line
  // on an approved / ordered / delivered / cancelled order pauses first. Owner:
  // *"There should definitely be a warning and check to ask if we do want to
  // remove the item as it's already been ordered and marked as delivered."*
  // Default null → no pause, so `pages/NewOrder.js` and create mode are
  // byte-identical. See `requestRemoveItem` below.
  postApprovalGuardStatus = null,
  // OPTIONAL (2026-10-01) — the PAGE'S EXISTING cancel flow for this order,
  // passed by an EDIT caller ONLY when the viewer may cancel it there (the
  // page resolves that; nothing here re-derives it). Removing the ONLY line in
  // the form asks "Remove the only item?" and its "Cancel order" calls this —
  // same route, reason box, audit and gates as the page's own Cancel, never a
  // second cancellation path. Null → that Remove is disabled with
  // `ONLY_ITEM_REMOVE_DENIED_TOOLTIP`. Create mode never reaches it (there is
  // no order to cancel), so `pages/NewOrder.js` is unchanged.
  onRequestCancelOrder = null,
  // OPTIONAL dirty reporting: called with `true` as soon as form or items
  // differ from the snapshot this component hydrated from, and `false` again
  // when they match it (and on unmount). Added 2026-08-07 for the expand-to-edit
  // row on Department Orders, whose dirty guard has to block a COLLAPSE — the
  // page owning the row cannot see this component's internal state any other
  // way. Default null. 🔀 Now also passed by `NewOrder` (dept-switch + leave
  // guards) and, since R14 (2026-09-15), `OrderPage` — whose own `dirty` flag
  // never saw a typed edit, so its leave guard needs this one.
  //
  // 🔑 The comparison is the SAME idiom as GagDetailPanel (`JSON.stringify`
  // of the whole form against a snapshot taken at hydration), deliberately —
  // CLAUDE.md's "Expandable Table Rows" says to track an original snapshot vs
  // current, and a second dirty mechanism in a codebase that already has one
  // is how two rows come to disagree about what "unsaved" means.
  onDirtyChange = null,
  // OPTIONAL submit handle: a ref object this component fills with its OWN
  // `handleSubmit`, so a parent outside the <form> can run a submit that is the
  // SAME submit the Save button runs. Default null → NewOrder and OrderPage are
  // byte-for-byte unaffected.
  //
  // 🔑 **THE POINT IS THAT THERE IS EXACTLY ONE SUBMIT PATH, AND THIS IS WHAT
  // KEEPS IT THAT WAY.** Added 2026-08-08 for the inline editor's "Save and
  // close" prompt button, which must validate, build and submit identically to
  // the form's own Save. The two obvious alternatives are both wrong:
  //   - Re-implementing validate + buildPayload at the call site is a SECOND
  //     save path, and two save paths drift. This repo's whole Code Unification
  //     Principle is about that failure.
  //   - `document.querySelector('form#order-form').requestSubmit()` reaches
  //     across the DOM by a hard-coded id — the same id that cannot be unique
  //     once two forms are on a page (see the accordion reasoning on
  //     `Approvals.js`), so it silently submits the WRONG form the day the
  //     `useId` work lands and multi-expand becomes possible.
  // A ref costs nothing, is checked by the compiler-of-record (a missing
  // function is a loud TypeError, not a silent no-op), and cannot address the
  // wrong instance.
  //
  // ⚠️ It hands back `handleSubmit` UNWRAPPED, which is why the caller gets the
  // real behaviour including the early returns: read-only, re-entrancy, and
  // **validation failure, which sets errors and never calls `onSubmit`**. That
  // last one is what makes "a failed save must not collapse the row" structural
  // rather than a special case — the collapse hangs off `onSaved`, which only a
  // successful PUT reaches.
  submitRef = null,
}) {
  // 🔻 `activeDepartment` / `loading` (as `deptLoading`) were destructured
  // here ONLY for the read-only Production display (`productionDisplay`,
  // below) — REMOVED 2026-08-16 with that field. See the removal note at
  // the old `productionDisplay` site for why the destructure came out too:
  // an unused destructure reads as "this still matters somewhere" and it no
  // longer does. `capabilities` is still the only authority signal here.
  const { isAdmin, capabilities, canViewAccounting, assignments, activeDepartment } = useDepartment();
  const { canManageOrder } = useOrderAuthority();
  const { formatMoney } = useVatMode();

  // ── ORDER-MANAGEMENT AUTHORITY, RESOLVED THE WAY THE SERVER RESOLVES IT ────
  //
  // 🚨 THIS REPLACED `(isAdmin || isBuyer)` ON 2026-08-09, AND IT IS A
  // CORRECTION, NOT A WIDENING FOR ITS OWN SAKE. `isBuyer` reaches the client
  // from the caller's SINGLE UNSCOPED `user_roles` row
  // (`middleware/departmentScope.js:177-182`), so a correctly-created
  // department buyer — `department_assignments.role = 'buyer'` with NO
  // `user_roles` row, which is what the role model requires — reads
  // `isBuyer === false`. Every server gate this form's fields land on resolves
  // `canManageDepartmentOrders` PER DEPARTMENT, so that user was being refused
  // by the FORM a write the API would have accepted. CLAUDE.md: *"use the
  // per-department CAPABILITY, never `isBuyer`"* and *"Match the client gate to
  // the SERVER gate for the same action"*.
  //
  // 🔑 THE TWO MODES ASK ABOUT DIFFERENT DEPARTMENTS, AND THAT IS WHY THIS IS
  // NOT SIMPLY `canManageOrder(initialOrder)`.
  //   · EDIT  — `PUT /api/orders/:id` resolves
  //     `departmentAuthority(req, order.department_id)` (`routes/orders.js:1850`,
  //     `canSetPoNumber`), i.e. THE ORDER'S OWN department. That is exactly
  //     `useOrderAuthority().canManageOrder(order)`, and it must be the order's
  //     department rather than the active one because Approvals lists orders
  //     from departments the viewer is not standing in.
  //   · CREATE — there is no order and no `department_id` yet. `NewOrder.js:250`
  //     posts `department_id: activeDepartment?.department_id`, and
  //     `POST /api/orders` resolves `departmentAuthority(req, finalDepartmentId)`
  //     over that same value (`routes/orders.js:1454`, `canManageOrdersHere`).
  //     So the ACTIVE department's capability set is the matching question here.
  //
  // 🪤 `canManageOrder(null)` would be the obvious one-liner and is WRONG on the
  // create path: it resolves `capabilitiesFor(undefined)` → `{}` → false, which
  // would hide the PO field and lock the accounting selects for every non-admin
  // on the New Order page — a REGRESSION, and one that no edit-mode test could
  // see. The `mode` split is load-bearing; do not collapse it.
  //
  // FAILS CLOSED in both branches: an absent/`null`/foreign `department_id`, and
  // an active department the caller holds no assignment in, both yield `{}`, so
  // `.canManageDepartmentOrders` is `undefined` — falsy. That matches
  // `departmentAuthority`'s EMPTY_AUTHORITY for the same cases. A user with no
  // assignment at all posts `department_id: null`, the server finds no
  // assignment either, and the two agree that nothing may be stamped.
  //
  // ⚠️ MIGRATING AN `isBuyer` SITE IS NOT A PURE NARROWING (docs/ROLES.md §C.2).
  // It NARROWS for an unscoped buyer standing in a department they hold nothing
  // in, and WIDENS for (a) the department buyer above and (b) a SUPERVISOR,
  // whose `can_manage_department_orders` went 0 → 1 by product ruling on
  // 2026-08-05 — *"Supervisors manage their own department's orders — mark
  // delivered, PO numbers, cost codes, cancel"*, which names two of the three
  // controls below. Both directions are pinned by
  // `OrderForm.orderAuthority.test.js`.
  //
  // ⚠️ A UI CONVENIENCE, NEVER A GATE. Every field it governs is refused or
  // silently dropped server-side per department.
  const canManageOrderFields = mode === 'create'
    ? (isAdmin || capabilities.canManageDepartmentOrders === true)
    : canManageOrder(initialOrder);

  // 🔻 `productionSpanClass` — REMOVED 2026-08-16 WITH THE PRODUCTION FIELD
  // IT COMPENSATED FOR. It used to hold `lg:col-span-2` / `sm:col-span-2
  // lg:col-span-3`, expanding Production to absorb the PO Number track
  // whenever `canManageOrderFields` was false, so that a SECOND sibling grid
  // (Supplier · Needed By · Lead Time)'s column edges stayed lined up with
  // this one's. Product owner: *"I'm thinking we can loose the 'Production'
  // lable as its a locked field and the user know the production they are
  // in… then we can put supplier, needed by date and lead time on the same
  // row as Po, Requested by etc."*
  //
  // 🔑 THE COMPENSATION IS GONE BECAUSE ITS REASON FOR EXISTING IS GONE, NOT
  // BECAUSE IT WAS SIMPLIFIED. It existed ONLY to keep TWO SIBLING GRIDS'
  // column edges in step with each other — see the deleted
  // `OrderForm.gridAlignment.test.js` history and its own long comment for
  // the 2026-08-11 defect this originally fixed. Supplier / Needed By Date /
  // Lead Time are now grid children of the SAME grid as PO / Requested By /
  // Sub Department / Job/Rig/Gag (below) — there is no second grid left to
  // drift out of step with, so nothing needs to absorb a track to hold an
  // alignment that no longer has two sides. When PO is absent every field
  // after it now simply shifts one track left, in the SAME row, which is
  // ordinary CSS grid flow and needs no help.
  //
  // 🔀 UPDATED 2026-08-16, SAME DAY, SECOND PASS: that single grid is no
  // longer `lg:grid-cols-10` either — the owner asked for a literal ONE ROW
  // at `lg` (*"these are still not on the same line as PO Number etc"*), so
  // the container is now `lg:grid-cols-[repeat(13,minmax(0,1fr))]` with a
  // fixed FULL/HALF span budget per field. Full arithmetic at the grid's own
  // top comment.
  //
  // ⚠️ **CONSEQUENCE FLAGGED TO THE OWNER ON THE FIRST PASS AND STILL TRUE:
  // THE FIELDS THAT FOLLOW PO DO NOT SIT AT THE SAME COLUMN FOR EVERY ROLE.**
  // A department buyer (PO rendered) and a plain crew member (PO hidden) see
  // Supplier start one track apart. That shift is harmless in itself —
  // nothing depends on a fixed column any more, because the alignment
  // problem was ONLY ever "two grids must agree with each other" — but it
  // remains a visible difference worth the owner's eyes on staging.

  // ── THE GRID'S TRACK COUNT IS PER-ROLE, THIRD PASS THE SAME DAY (2026-08-16) ──
  // 🚨 **FIXES A REAL DEFECT IN THE SECOND PASS, NOT A STYLE CHANGE.** Owner, on
  // staging: *"it fits but does not fill the full width."* The second pass fixed
  // `lg:grid-cols-[repeat(13,minmax(0,1fr))]` for every role, but N=13 is only
  // the ADMIN variant's exact sum — a department buyer's fields sum to 11 (2
  // empty trailing tracks) and a plain user's to 10 (3 empty). Every track is an
  // equal `minmax(0,1fr)` fraction of the row REGARDLESS of whether anything
  // occupies it, so those extra tracks rendered as dead whitespace on the right
  // for the two role shapes that are NOT an admin — exactly the "does not fill
  // the full width" the owner saw, because a plain user or an ordinary
  // department buyer is who is actually on staging testing this.
  //
  // 🔑 **THE FIX: N TRACKS EACH ROLE, OFF THE SAME TWO CONDITIONS THAT ALREADY
  // GATE PO AND DEPARTMENT.** `isAdmin` implies `canManageOrderFields` (see
  // `useOrderAuthority().canManageOrder` — `if (isAdmin) return true`, and the
  // `mode === 'create'` branch above ORs `isAdmin` directly), so the three
  // reachable shapes are exhaustive and mutually exclusive:
  //   isAdmin === true                              → PO + Department render → N = 13
  //   canManageOrderFields === true, isAdmin === false → PO only              → N = 11
  //   canManageOrderFields === false (⇒ isAdmin false)  → neither             → N = 10
  // Each number is that variant's EXACT span sum (see the grid's own top
  // comment for the per-field FULL/HALF budget and the arithmetic), so every
  // role's row now fills exactly as far as it did before — no more, no less —
  // and still cannot wrap, because a variant's own fields can never exceed its
  // own N.
  //
  // 🚨 **THREE COMPLETE LITERAL CLASSES, NEVER A RUNTIME-BUILT NUMBER.**
  // Tailwind's scanner reads SOURCE TEXT (`client/tailwind.config.js` scans
  // `./src/**`, test files included — CLAUDE.md's live hazard), so
  // `` `xl:grid-cols-[repeat(${n},minmax(0,1fr))]` `` would compile to NOTHING:
  // no literal candidate exists in the source for the scanner to find, and the
  // class would do nothing in production while looking correct in a diff. Same
  // idiom as `itemGridSpans` above and the deleted `productionSpanClass`.
  //
  // 🔀 **PREFIX MOVED `lg:` → `xl:` (1280px), FOURTH PASS THE SAME DAY
  // (2026-08-16).** Owner, on staging, reviewing the one-row layout at a
  // narrower width: *"as the screen narrows some LABELS wrap to two lines and
  // push their inputs down, so the single row looks disjointed … at the point
  // the labels go to two rows we should just go to the double row/MOBILE
  // version."* The "double row/mobile version" he means is the layout that
  // was ALREADY HERE below `lg:` — `grid-cols-1` (mobile) / `sm:grid-cols-2`
  // (tablet) — not a new bespoke desktop tier. So the fix is narrower than it
  // first looks: move every class that builds the one-row layout (this
  // container class, plus each field's `col-span`/`col-start`/`row-start`
  // below) from the `lg:` prefix up to `xl:`, and change NOTHING ELSE. Below
  // 1280px there is now no `lg:` override left on any of them, so
  // `sm:grid-cols-2` simply keeps applying all the way from 640px to 1279px —
  // the exact fallback he asked for, with no new markup.
  //
  // 📏 **WHY `xl` (1280) AS THE STARTING THRESHOLD.** The longest label in
  // this row is "Job / Rig / Gag" (a FULL-size, `xl:col-span-2`, field); at
  // the narrower one-row track widths this pass introduces, the first
  // breakpoint wide enough to hold it on one line without wrapping is the
  // one to start the one-row tier at. `xl` is Tailwind's next stop up from
  // `lg` (1024) and is the owner's own suggestion as the first value to try.
  // **This is a browser check, not a class-model claim** — say so rather than
  // claim it is measured. If labels still wrap between 1280 and ~1400 on his
  // screen, he may want it bumped to `2xl` (1536): that is a ONE-LINE change
  // — swap every `xl:` in this block (container + all eight fields below) for
  // `2xl:`, including these three literals.
  // ── PO's VISIBILITY, ADDED 2026-08-16 ───────────────────────────────────
  // Product owner, on `OrderExpandedRow`'s read-only branch: *"None of this
  // should be gated as its the user who ordered it"* — PO is named in his
  // field list. Until now PO was gated on `canManageOrderFields` for BOTH
  // visibility and editability, so a plain requester never saw their own
  // order's PO number even in read-only mode. `readOnly` widens VISIBILITY
  // only — editability is untouched, still `canManageOrderFields` alone
  // (readOnly renders no input regardless). `hidePoNumberField` narrows it
  // back for a caller supplying its own live PO editor (see the prop docs
  // above) so the field is never rendered twice. In every mode this prop
  // was not built for (create/edit, `hidePoNumberField` always false there)
  // this is `canManageOrderFields && true`, i.e. byte-identical to before.
  // 🔀 2026-09-13 — THE ROW-INDEPENDENT HALF OF THE PER-ITEM `Actions ▾` GATE,
  // extracted so the Line Items section bar can reserve exactly the width that
  // menu occupies (see the alignment spacer beside `NextStepAllButton`).
  // 🔑 It is the SAME expression, not a second one: the item gate is this
  // `&& item.id != null`, which is the only per-row term. A header that
  // reserved space on a different condition from the rows would misalign on
  // exactly the viewers the two disagreed about.
  const showsItemActionsMenu = (!readOnly || onReadOnlyItemNotifyUpdated) && canManageOrder(initialOrder);
  // The caller's per-line header tick (`itemHeaderTick` above) — a SAVED line
  // only; a new unsaved line has no id and so nothing to tick.
  const headerTickFor = (item, index) => (itemHeaderTick && item.id != null ? itemHeaderTick(item, index) : null);

  // 🔀 2026-09-13: `poNumberFieldSlot` forces the cell on. See that prop.
  const showPoNumberField = poNumberFieldSlot
    ? true
    : ((canManageOrderFields || readOnly) && !hidePoNumberField);

  // orderDetailsGridColsClass's track count is 10 PLUS ONE PER VISIBLE
  // FULL/HALF-budgeted field that isn't in the base 10 — PO (HALF, +1) and
  // Department (FULL, +2, admin-only, unaffected by the above). Driving it
  // off `showPoNumberField` rather than `canManageOrderFields` directly is
  // what keeps this correct in the two NEW reachable shapes readOnly
  // introduces: a plain requester viewing read-only now shows PO (was never
  // reachable before, needs 11 not 10), and a manager viewing read-only with
  // `hidePoNumberField` (PO covered by their own layered editor instead)
  // must NOT reserve a dead track for it (would reintroduce the exact
  // "does not fill the full width" defect the per-role track count was
  // built to fix). 🚨 FOUR LITERAL CLASSES, NEVER A RUNTIME-BUILT NUMBER —
  // see the passes above; Tailwind's scanner needs the literal source text.
  //
  // 🔻 CORRECTED 2026-09-13, SECOND PASS: this briefly went to eight literals
  // (a +1 term for `orderDetailsExtraField`) when that slot lived in row 1.
  // The owner's follow-up ruling moved it to a SECOND ROW instead (see the
  // prop docs above), and a second row costs no columns at all — CSS Grid's
  // implicit rows reuse the same `repeat(N, …)` column tracks row 1
  // declares. So this is back to exactly FOUR literals, byte-for-byte the
  // arithmetic that shipped before `orderDetailsExtraField` existed.
  const orderDetailsGridColsClass = isAdmin
    ? (showPoNumberField ? 'xl:grid-cols-[repeat(13,minmax(0,1fr))]' : 'xl:grid-cols-[repeat(12,minmax(0,1fr))]')
    : (showPoNumberField ? 'xl:grid-cols-[repeat(11,minmax(0,1fr))]' : 'xl:grid-cols-[repeat(10,minmax(0,1fr))]');

  // orderDetailsRow2RestClass — the SECOND cell of row 2 (Internal notes).
  // 🔀 CORRECTED 2026-09-13, THIRD PASS — owner, live in the browser at 1280:
  // *"it looks cramped … Expected delivery label and text can be wider and
  // Internal notes can be narrower if required."* The lead cell (Expected
  // Delivery) was a HALF cell (col-span-1, PO Number's own width, ~90px at
  // 1280) and its label/date/helper text wrapped to 3-4 lines. Row 2 does
  // not have to mirror row 1's per-field widths — it only has to START under
  // PO Number — so the split moved from col 2/(N−1) to col 4/(N−3): the lead
  // cell is now THREE tracks wide (roughly PO Number + Requested By's
  // combined width), never varying with N, and the rest cell starts at
  // column 4. 🚨 ROW 1 IS COMPLETELY UNTOUCHED BY THIS — `orderDetailsGridColsClass`
  // above still reads the same four N values (13/12/11/10); only where THIS
  // ROW's own split falls within those N tracks moved.
  // 🔑 Same N as the container above (13/12/11/10 by `isAdmin` ×
  // `showPoNumberField`); this cell spans N−3 tracks starting at col 4 when
  // `orderDetailsRow2Lead` renders, or the full N starting at col 1 when it
  // does not (that branch is UNCHANGED by this pass — the lead cell isn't
  // there to share the row with). 🚨 EIGHT COMPLETE LITERAL CLASSES, NEVER A
  // RUNTIME-BUILT NUMBER — Tailwind's scanner needs the literal source text.
  // `xl:col-span-[13]` (lead absent, admin, PO visible) is the one case
  // outside Tailwind's default 1-12 col-span scale; every N−3 value (10/9/8/7)
  // is within that default scale and needs no arbitrary-value literal.
  const orderDetailsRow2RestClass = isAdmin
    ? (showPoNumberField
      ? (orderDetailsRow2Lead ? 'xl:col-start-4 xl:row-start-2 xl:col-span-10' : 'xl:col-start-1 xl:row-start-2 xl:col-span-[13]')
      : (orderDetailsRow2Lead ? 'xl:col-start-4 xl:row-start-2 xl:col-span-9' : 'xl:col-start-1 xl:row-start-2 xl:col-span-12'))
    : (showPoNumberField
      ? (orderDetailsRow2Lead ? 'xl:col-start-4 xl:row-start-2 xl:col-span-8' : 'xl:col-start-1 xl:row-start-2 xl:col-span-11')
      : (orderDetailsRow2Lead ? 'xl:col-start-4 xl:row-start-2 xl:col-span-7' : 'xl:col-start-1 xl:row-start-2 xl:col-span-10'));

  // ═══════════════════════════════════════════════════════════════════════
  // MY SPACE "HEADER DETAILS" — whether to offer the box at all (2026-09-11)
  // ═══════════════════════════════════════════════════════════════════════
  // Owner decision 2: *"My Space ONLY — no department order carries or renders
  // it."*
  //
  // 🔑 WHICH DEPARTMENT THE QUESTION IS ABOUT DIFFERS BY MODE, and that is the
  // whole of the expression:
  //   EDIT   — the ORDER'S OWN department. A caller can be standing anywhere
  //            while editing (`MyOrders` and an admin's Approvals both list
  //            orders from departments the viewer is not in), so reading the
  //            active department here would show the box on a department order
  //            and hide it on a My Space one. Same reasoning as
  //            `useOrderAuthority`, which resolves per ORDER for this reason.
  //   CREATE — the ACTIVE department, because that is where the order will
  //            land (`pages/NewOrder.js` posts `activeDepartment.department_id`).
  //
  // 🪤 `activeDepartment.department_id`, NEVER `.id` — root `CLAUDE.md` records
  // 15 sites that read `.id` and failed SILENTLY.
  //
  // ⚠️ A UI CONVENIENCE, NOT A GATE. `POST`/`PUT /api/orders` re-decide from
  // `productions.is_personal` and store NULL for a department order whatever
  // this form sends.
  const headerDetailsDepartmentId = initialOrder && initialOrder.department_id != null
    ? initialOrder.department_id
    : (activeDepartment ? activeDepartment.department_id : null);
  const showHeaderDetails = isPersonalWorkspaceDepartment(assignments, headerDetailsDepartmentId);

  const [form, setForm] = useState(() => buildInitialForm(initialOrder, mode));

  // 🔻 `productionDisplay` — REMOVED 2026-08-16. It built the
  // `company_name - production_name` string the locked Production field
  // showed (create: the active department's production, derived live at
  // render; edit: the order's STORED production, never reacting to the
  // switcher — see the deleted `OrderForm.productionReadOnly.test.js` §2/§3
  // for the full create/edit asymmetry this used to encode). Product owner:
  // *"I'm thinking we can loose the 'Production' lable as its a locked field
  // and the user know the production they are in."*
  //
  // 🚨 **WHAT DID NOT COME OUT WITH IT, AND MUST NOT.** `form.production_id`
  // is STILL hydrated by `buildInitialForm` on the edit path (unchanged,
  // below) and `buildPayload` STILL strips it from every submit — see the
  // `_productionIdIsServerDerived` destructure in
  // `utils/orderFormHelpers.js`. That stripping was already in place before
  // this change (2026-08-12, when the field went read-only) and is
  // UNTOUCHED by removing the display: `production_id` was already never
  // sent, on create or edit, because the server derives/preserves it and no
  // longer reads it from the request body at all. Deleting the JSX that used
  // to RENDER a value cannot affect a `buildPayload` that never READ that
  // JSX in the first place — it only ever read `form` state, which nothing
  // here wrote to (`ReadOnlyValue` has no `onChange`). Pinned by §4 of
  // `OrderForm.productionReadOnly.test.js`, kept in this change specifically
  // because it is the one assertion in that file that does not depend on the
  // display existing.
  //
  // The `productions` prop (still accepted, still defaults to `[]`) is now
  // unread inside this component — every caller may keep passing it
  // unchanged; React ignores a prop nothing destructures for use.
  const [items, setItems] = useState(() => buildInitialItems(initialOrder));
  // See `buildNotifyRows` — server-owned, never serialised by `buildPayload`.
  const [notifyRows, setNotifyRows] = useState(() => buildNotifyRows(initialOrder));
  const [errors, setErrors] = useState({});
  const [itemNotesExpanded, setItemNotesExpanded] = useState({});
  const isPhone = useIsMobileOrderViewport();
  const openItemAtTop = useOpenAtTop();
  // 🔀 2026-10-09 (owner): desktop folds too, but OPENS by default — the person
  // opening an order on a desktop wants to read and act on it at once.
  const [itemsListOpen, setItemsListOpen] = useState(() => !isPhone || (mode !== 'create' && !initialItemsCollapsed));
  // 📱 PHONE (owner 2026-10-03): the order's details fold into a "Details" section
  // like Notes / Items — closed on an opened order, open on New Order, opened by a
  // validation error. Desktop is unchanged (no toggle).
  // 🔀 2026-10-11 (owner: "I want all sections folded on New order form"): on a
  // phone New Order now starts with Details AND Line Items folded too, so the
  // blank form is its four section bars. A validation error still opens Details.
  const [detailsOpen, setDetailsOpen] = useState(() => !isPhone || (mode !== 'create' && !initialItemsCollapsed));
  // Order Details and the Line Items list open AND close smoothly (owner
  // 2026-10-09) — `hooks/useExpandAnimation.js`; both are `hidden`-toggled.
  // `*Shown` stays true for a closing ease, then the body is `hidden` as before.
  const detailsBodyRef = useRef(null);
  const detailsShown = useAnimatedPresence(detailsOpen, detailsBodyRef);
  useExpandAnimation(detailsBodyRef, detailsShown);
  const itemsListRef = useRef(null);
  const itemsListShown = useAnimatedPresence(itemsListOpen, itemsListRef);
  useExpandAnimation(itemsListRef, itemsListShown);
  const [collapsedItems, setCollapsedItems] = useState(() => {
    if (!initialItemsCollapsed) return new Set();
    const count = initialOrder?.items?.length > 0 ? initialOrder.items.length : 1;
    return new Set(Array.from({ length: count }, (_, i) => i));
  });

  // 🔑 OPEN/CLOSED ONLY. The list CONTENTS are DERIVED further down
  // (`supplierSuggestions` / `gagSuggestions`) rather than stored, because
  // `suppliers` and `gags` both default to `[]` and arrive from the parent AFTER
  // first paint — a copy filtered inside a keystroke handler is a snapshot of
  // whatever had loaded at that instant, and it never refreshed itself. Symptom:
  // type before the fetch lands and the box stays empty until you press another
  // key. Proven by §2 of `OrderForm.suggestionHints.test.js`, which re-renders
  // with the fetched array and no further keystroke.
  const [supplierListOpen, setSupplierListOpen] = useState(false);
  const supplierRef = useRef(null);

  // Job / Rig / Gag — `ui/SuggestInput` since 2026-10-28 (job cost codes); it
  // owns its own open state. 🔻 The "Add a job? … Pick a job" hint was DELETED
  // 2026-10-28 (owner: "i dont think we need this helper hint"); its absence is pinned.
  // True once a job is PICKED in this session — lines added later fill too.
  const jobPickedRef = useRef(false);
  // JOB FILL marks: the keys (`id` / `client_key`) of lines whose cost code
  // was filled from the job in THIS session — "◆ Set from the job".
  const [jobFilledKeys, setJobFilledKeys] = useState(() => new Set());

  // The clean state this form is measured against. Seeded from the SAME two
  // builders that seed `form`/`items`, and re-seeded by the re-hydration effect
  // below — so a re-hydration lands as "clean", never as a spurious edit.
  // A ref, not state: nothing renders from it, and re-rendering on a snapshot
  // change would fight the very render that produced it.
  const snapshotRef = useRef(null);
  if (snapshotRef.current === null) {
    snapshotRef.current = JSON.stringify({ form, items });
  }

  // ── THE POST-APPROVAL REMOVAL PAUSE (2026-09-13) ─────────────────────────
  // `pendingRemoval` is the line the dialog is asking about; the ref records
  // every CONFIRMED removal, keyed by the stored `order_items.id`, with the
  // label and the reason typed. Read at submit (see `handleSubmit`), cleared by
  // re-hydration — a ref, not state, because nothing renders from it.
  const [pendingRemoval, setPendingRemoval] = useState(null);
  // The 'untick delivered first' notice (2026-09-28) — open while true.
  const [receivedRemovalNotice, setReceivedRemovalNotice] = useState(false);
  // 🔒 One notice for every locked field and the locked Remove (owner 2026-10-03).
  const [lockedNotice, setLockedNotice] = useState(false);
  const showLockedNotice = React.useCallback(() => setLockedNotice(true), []);
  // Notes / Comments is a collapsible section like Attachments, COLLAPSED by
  // default (owner 2026-10-03); a "Has notes" tag on the closed header keeps a
  // note — printed for the supplier — from going unnoticed.
  const [notesOpen, setNotesOpen] = useState(false);
  // "Remove the only item?" (2026-10-01) — open while true.
  const [onlyItemPrompt, setOnlyItemPrompt] = useState(false);
  const confirmedRemovalsRef = useRef(new Map());

  // Re-hydrate when initialOrder reference changes (edit page finishes loading).
  // Skip during create-mode to avoid clobbering localStorage hydration.
  // In read-only mode, always re-hydrate so the displayed values reflect
  // the latest fetched order.
  useEffect(() => {
    if ((mode === 'edit' || readOnly) && initialOrder) {
      // 🔑 `mode` is passed for the lead-time default, and this branch is
      // reached only when `mode === 'edit' || readOnly` — so the default is
      // never applied by a re-hydration. Passing it is what makes that true by
      // construction rather than by the guard above happening to agree.
      const nextForm = buildInitialForm(initialOrder, mode);
      const nextItems = buildInitialItems(initialOrder);
      snapshotRef.current = JSON.stringify({ form: nextForm, items: nextItems });
      // A re-hydration is a new clean state: no removal is pending against it.
      confirmedRemovalsRef.current = new Map();
      setForm(nextForm);
      setItems(nextItems);
      // 🔑 Re-seeded with the rest, and NOT part of `snapshotRef` — the dirty
      //    test is `{ form, items }`, so a notify can never make the form look
      //    edited. That matters: a dirty row blocks the poll, dims its
      //    neighbours and raises the save/discard prompt on collapse, and none
      //    of that should fire because a buyer flagged a line.
      setNotifyRows(buildNotifyRows(initialOrder));
    }
  }, [mode, initialOrder, readOnly]);

  // ── Dirty reporting (optional; see the `onDirtyChange` prop docs) ──
  // `readOnly` can never be dirty: it renders text, not inputs, so a value
  // change there is a re-hydration and not an edit.
  const isDirty = !readOnly && JSON.stringify({ form, items }) !== snapshotRef.current;
  // The Save / Discard (or Submit) bar's ONE condition — also what tells a
  // `ui/PhoneDecisionBar` (Approve / Reject) to step aside on a phone.
  const showSaveBar = !readOnly && (mode === 'create' || isDirty || !!(pendingFiles && pendingFiles.hasEntries));
  const [hasPhoneDecision, setHasPhoneDecision] = useState(false);
  const decisionContextValue = useMemo(
    () => ({ dirty: showSaveBar && mode !== 'create', setHasDecision: setHasPhoneDecision }),
    [showSaveBar, mode]
  );
  // Ref-stabilised exactly as GagDetailPanel does it, so a parent passing an
  // inline arrow does not re-fire the effect on every render, and the unmount
  // cleanup always clears the parent's flag — a discarded panel must not leave
  // a stale block behind that nothing can now clear.
  const onDirtyChangeRef = useRef(onDirtyChange);
  useEffect(() => { onDirtyChangeRef.current = onDirtyChange; });
  useEffect(() => { onDirtyChangeRef.current?.(isDirty); }, [isDirty]);
  useEffect(() => () => onDirtyChangeRef.current?.(false), []);

  const hasNoAssignments = userAssignedProductions !== null && userAssignedProductions.length === 0;

  // Close supplier suggestions on outside click (the Job box's SuggestInput
  // closes itself on blur).
  useEffect(() => {
    function handleClickOutside(e) {
      if (supplierRef.current && !supplierRef.current.contains(e.target)) {
        setSupplierListOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // ---- Autocomplete matching + "nothing to show" hints ----
  //
  // ONE matcher per autocomplete, read by BOTH the input handler (which decides
  // whether to open the list) and the hint below (which decides what to say when
  // the list stays shut). They were two copies of the same `.filter(...)`
  // expression, and two copies that can disagree about what "matches" means is
  // exactly how a box shows neither a list nor a reason.
  //
  // 🔀 THE SUPPLIER MATCHER MOVED TO `utils/supplierOptions.js` (2026-08-08),
  // because the list is now TWO TIERS — real `suppliers` records plus names
  // typed on past approved orders — and the merge, the de-duplication and the
  // filter are one rule that the admin-settings supplier box shares. Derived at
  // render time exactly as before, never stored: both arrays default to `[]` and
  // arrive from the parent AFTER first paint.
  const supplierOptions = useMemo(
    () => buildSupplierOptions(suppliers, supplierSuggestions),
    [suppliers, supplierSuggestions]
  );
  const matchSuppliers = (query) => matchSupplierOptions(supplierOptions, query);
  // 🔗 S1 GAG LINK, review F4 (2026-10-24): ONLY A GAG OF THE ORDER'S OWN
  // DEPARTMENT IS OFFERED. Every host fetches `GET /api/gags` for the ACTIVE
  // department, but an order of ANOTHER department is reachable (a direct URL,
  // the no-seat bell fallback, a stale list after a switch) — and picking one of
  // those gags would only earn a 400 `gag_not_in_department`. So the rule lives
  // HERE, covering every host: the order's department (a saved order's own;
  // a new order lands in the active department) must equal each gag's
  // `department_id` (the list rows are `gags.*`). Unknown on either side →
  // nothing offered (fail closed). Free text still works; the server's text
  // rule decides. The "Linked to gag" marker still shows a link the order HAS.
  const gagOrderDepartmentId = mode === 'edit' && initialOrder && initialOrder.id != null
    ? (initialOrder.department_id ?? null)
    : (activeDepartment ? activeDepartment.department_id : null);
  const linkableGags = gagOrderDepartmentId == null
    ? []
    : gags.filter(g => g && g.department_id != null && Number(g.department_id) === Number(gagOrderDepartmentId));
  // The list belongs to ANOTHER department than the order: say nothing rather
  // than "No jobs, rigs or gags set up for this department yet", which would be
  // a claim about the wrong department.
  const gagListIsForAnotherDepartment = gagOrderDepartmentId == null
    || (gags.length > 0 && linkableGags.length === 0)
    || (activeDepartment && activeDepartment.department_id != null
      && Number(activeDepartment.department_id) !== Number(gagOrderDepartmentId));
  const matchGags = (query) => {
    const q = (query || '').trim().toLowerCase();
    return q ? linkableGags.filter(g => (g.title || '').toLowerCase().includes(q)) : linkableGags;
  };

  // ── JOB COST CODES (2026-10-28) ── the job codes this viewer may see
  // (`costCodes` is `/cost-codes/active`: `[]` without budget rights), keyed by
  // their gag. A job code without a gag is not offered as a JOB here — the
  // order links to gags, and the fill matches by the gag link only.
  const jobCodeForGagId = (gagId) => (gagId == null ? null
    : costCodes.find(cc => cc && cc.is_job && cc.gag_id != null && Number(cc.gag_id) === Number(gagId)) || null);
  const JOB_GROUP = 'Job codes';
  const GAG_GROUP = 'Other gags';
  // label → gag, labels → group: ONE derivation the box's options, its group
  // headings and the pick all read. Duplicate labels keep the first gag.
  const jobOptionGag = new Map();
  const jobOptionGroup = new Map();
  for (const g of linkableGags) {
    const jc = jobCodeForGagId(g.id);
    if (!jc) continue;
    const label = `${jc.code} · ${g.title}`;
    if (!jobOptionGag.has(label)) { jobOptionGag.set(label, g); jobOptionGroup.set(label, JOB_GROUP); }
  }
  for (const g of linkableGags) {
    if (jobCodeForGagId(g.id) || !g.title || jobOptionGag.has(g.title)) continue;
    jobOptionGag.set(g.title, g);
    jobOptionGroup.set(g.title, GAG_GROUP);
  }
  const jobOptions = [...jobOptionGag.keys()];
  const hasJobCodeOptions = [...jobOptionGroup.values()].includes(JOB_GROUP);
  // The order's job code, from its GAG LINK (never the typed name).
  const activeJobCode = jobCodeForGagId(form.gag_id);
  // Who may be handed a cost code by the fill / the "Use J01?" offer — exactly
  // who sees an EDITABLE Cost Code select on a line (see the Cost Code block).
  const jobFillAllowed = !!canViewAccounting && canManageOrderFields && !readOnly && !loadFailures.costCodes;
  const jobLineKey = (item) => (item ? (item.id ?? item.client_key ?? null) : null);
  // A line shows "◆ Set from the job" while it still holds the job's code.
  const jobFilledLine = (item) => jobFillAllowed && !!activeJobCode
    && jobFilledKeys.has(jobLineKey(item)) && Number(item.cost_code_id) === Number(activeJobCode.id);

  // Derived, not state — see the note beside `supplierListOpen`. `showX` folds the
  // open flag together with "is there anything to show", so `aria-expanded` can
  // never claim an open popup that renders nothing.
  // 🪞 RENAMED 2026-08-08: this was `supplierSuggestions`, which is now the name
  // of the PROP carrying the past-order names. `…Matches` is what it always
  // was — the subset of the merged option list matching what has been typed —
  // and leaving the old name would have shadowed the prop silently.
  const supplierSuggestionMatches = matchSuppliers(form.supplier_name);
  const gagSuggestions = matchGags(form.job_rig);
  const showSupplierSuggestions = supplierListOpen && supplierSuggestionMatches.length > 0;

  // Why these hints exist, from a real report (2026-08-08): when a filter
  // produced nothing the list was simply hidden and the user got SILENCE —
  // indistinguishable from a broken feature. The product owner
  // lost real time to it twice in one session on the supplier box, which could
  // never suggest anything because his department had **no rows in `suppliers`
  // at all** (258 orders using 17 distinct typed names, 0 supplier records), and
  // nothing on screen said so. The list is a curated `suppliers` table fetched
  // from `GET /api/suppliers`; it is NOT derived from names previously typed on
  // orders, which is what he assumed.
  //
  // 🔑 TWO CASES, AND KEEPING THEM DISTINGUISHABLE IS THE WHOLE FIX — conflating
  // them is the bug, not a tidy-up:
  //   'no-match' → the list has entries, none match what you typed. Keep typing.
  //   'empty'    → this department's list is empty. Nothing will EVER match; go
  //                and add some. A different action, so it needs a different
  //                sentence, not a shared "nothing found".
  //
  // DERIVED at render time from the props, never stored in state: `suppliers` /
  // `gags` both default to `[]` and arrive from the parent AFTER first paint
  // (NewOrder.js fetches them in an effect), so a verdict snapshotted inside an
  // input handler would freeze as 'empty' and never recover. Derived, it
  // self-corrects on the render the fetch triggers.
  //
  // Gated on a non-empty typed query so a form that mounts before its fetch
  // resolves cannot flash "No suppliers set up…" at someone who has typed
  // nothing. Both fields accept free text, so neither sentence may imply the
  // typed value is invalid — it is a hint, not a validation error.
  //
  // 🚨 A THIRD VERDICT, 'load-failed', ADDED 2026-08-22 — AND IT OUTRANKS BOTH
  // OF THE OTHER TWO. See the `loadFailures` prop for why the component has to
  // be told rather than infer it.
  //   'load-failed' → the request was refused or never landed. We know nothing
  //                   about this department's suppliers, so BOTH other
  //                   sentences are unsupported claims, not just the 'empty'
  //                   one. It takes precedence for exactly that reason: with
  //                   the records fetched but the past-order names refused, the
  //                   list is non-empty and 'no-match' looks reachable — and
  //                   "No suppliers match" is still false, because a name that
  //                   would have matched was in the half that never arrived.
  const autocompleteHint = (query, options, matches, loadFailed = false) => {
    if (!(query || '').trim()) return null;
    // 🔑 A VISIBLE MATCH NEEDS NO EXPLANATION, so this returns before any
    // verdict — including 'load-failed'. Every sentence here exists to explain
    // an ABSENCE; warning about a half-loaded list while the box is showing
    // options is noise, and noise is how a warning that matters gets ignored.
    // (Equivalent to the old ordering: `options` empty implies `matches` empty.)
    if (matches.length > 0) return null;
    if (loadFailed) return 'load-failed';
    if (options.length === 0) return 'empty';
    return 'no-match';
  };
  // Reads the SAME derived arrays the lists render, so the hint and the list can
  // never disagree about whether the typed value matched.
  // ⚠️ The `options` argument is `supplierOptions`, the MERGED list — not
  // `suppliers`. That is what keeps the 'empty' sentence honest: a department
  // with no supplier records but a history of approved orders now HAS something
  // to offer, so "No suppliers set up for this department yet" would be a false
  // statement about a box that is about to show suggestions.
  const supplierHint = readOnly
    ? null
    : autocompleteHint(form.supplier_name, supplierOptions, supplierSuggestionMatches, !!loadFailures.suppliers);
  const gagHint = readOnly || gagListIsForAnotherDepartment
    ? null
    : autocompleteHint(form.job_rig, linkableGags, gagSuggestions);

  // Muted helper text, the house convention for a hint: the colour pair
  // `text-xs text-gray-500` + its dark variant has 139 uses across client/src.
  // The dark variant is mandatory — see CLAUDE.md, Colour Theme & Dark Mode.
  const HINT_CLASS = 'mt-1 text-xs text-gray-500 dark:text-gray-400';

  // A reference list did not load. Amber rather than the muted grey above,
  // because this is not a hint about what you typed — it is the form telling
  // you a question went unanswered. Dark variant mandatory (CLAUDE.md, Colour
  // Theme & Dark Mode).
  const LOAD_FAILED_HINT_CLASS = 'mt-1 text-xs text-amber-700 dark:text-amber-300';

  // ---- Form field handlers ----

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newVal = type === 'checkbox' ? checked : value;
    setForm(prev => ({ ...prev, [name]: newVal }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
    if (persistFieldsToLocalStorage) {
      if (name === 'requested_by') localStorage.setItem(LS_KEYS.requestedBy, value);
      if (name === 'department') localStorage.setItem(LS_KEYS.department, value);
      if (name === 'buyer_email') localStorage.setItem(LS_KEYS.buyerEmail, value);
      if (name === 'user_phone') localStorage.setItem(LS_KEYS.userPhone, value);
    }
  };

  const handleSupplierInput = (e) => {
    const value = e.target.value;
    setForm(prev => ({ ...prev, supplier_name: value }));
    if (errors.supplier_name) {
      setErrors(prev => ({ ...prev, supplier_name: '' }));
    }
    // Typing opens the list; whether anything is IN it is decided by the derived
    // `showSupplierSuggestions` above, so an array that arrives late still lands.
    setSupplierListOpen(true);
  };

  const selectSupplier = (name) => {
    setForm(prev => ({ ...prev, supplier_name: name }));
    setSupplierListOpen(false);
  };

  // ---- Job / Rig / Gag handlers ----
  // Free-text field: typing updates job_rig directly and filters the gag-name
  // suggestions; focusing an empty field shows the full gag list so users can
  // simply pick one. 🔗 S1 GAG LINK (2026-10-24): picking a gag sends its
  // `gag_id` (a REAL link — the server checks it is a gag of the order's own
  // department); typing clears it to null, and the server links an exact
  // same-department title match itself (helpers/gagLink.js). Free text stays valid.
  //
  // 🔀 JOB COST CODES (owner ruling 2026-10-28). The box is `ui/SuggestInput`:
  // the department's JOB CODES first ("J01 · Exploding car"), then the other
  // gags, free text allowed. 🔑 THE GAG LINK DECIDES, NEVER THE TYPED NAME: only
  // a PICK (`onPick`) sets `gag_id` and fills lines; typing — even the exact
  // title — clears the link and fills nothing (the server's title rule may
  // still link it on save; it never fills a cost code).
  const handleJobRigText = (value) => {
    // 🔗 OVERTYPING A PICK UNLINKS THE JOB, SO IT UN-FILLS THE LINES THE JOB
    // FILLED (review I-1, 2026-10-28) — they were set for that job, and keeping
    // them would submit its code while the mark said nothing. The same un-fill
    // a pick of a code-less gag runs (`applyJobFill(null)`): only the lines THIS
    // session filled go back to blank; a hand-chosen code is never touched.
    if (form.gag_id != null && jobFilledKeys.size > 0) {
      applyJobFill(null);
      jobPickedRef.current = false;
    }
    setForm(prev => ({ ...prev, job_rig: value, gag_id: null }));
    if (errors.job_rig) setErrors(prev => ({ ...prev, job_rig: '' }));
  };

  const selectGag = (gag) => {
    setForm(prev => ({ ...prev, job_rig: gag.title, gag_id: gag.id ?? null }));
    if (errors.job_rig) setErrors(prev => ({ ...prev, job_rig: '' }));
    applyJobFill(gag);
  };

  const handleJobPick = (label) => {
    const gag = jobOptionGag.get(label);
    if (gag) selectGag(gag);
    else handleJobRigText(label);
  };

  // ── THE JOB FILL — the money/authority rule, pinned by
  // `OrderForm.jobCodeFill.test.js` ─────────────────────────────────────────
  // On a PICK of a gag that has a job code: every line with NO cost code takes
  // the job code (marked "◆ Set from the job"); a line already coded KEEPS its
  // code (it is offered "Use J01 for this line?" instead). Lines this session
  // filled follow a later pick (to the new job's code, or back to blank when
  // the new job has none). Never on mount: an order that already carries the
  // job is never re-coded by being opened (owner ruling 3).
  // 🚨 Only for a viewer who can SEE and SET the line's cost code — the same
  // gates the Cost Code control renders on (`canViewAccounting` outer,
  // `canManageOrderFields` inner, not read-only, list loaded). Anyone else is
  // never handed a code (and `/cost-codes/active` gives them `[]` anyway).
  function applyJobFill(gag) {
    if (!jobFillAllowed) return;
    jobPickedRef.current = true;
    const jobCode = jobCodeForGagId(gag && gag.id);
    const filled = new Set(jobFilledKeys);
    // Computed from THIS render's lines (a pick is a click handler), so the
    // marks and the lines are decided together — never inside an updater.
    setItems(items.map((item) => {
      const key = jobLineKey(item);
      if (key != null && filled.has(key)) {
        if (jobCode) return { ...item, cost_code_id: jobCode.id };
        filled.delete(key);
        return { ...item, cost_code_id: '' };
      }
      if (jobCode && !item.cost_code_id) {
        if (key != null) filled.add(key);
        return { ...item, cost_code_id: jobCode.id };
      }
      return item;
    }));
    setJobFilledKeys(filled);
  }

  // A line's cost code chosen BY HAND drops its "set from the job" mark.
  const setLineCostCode = (index, value) => {
    const key = jobLineKey(items[index]);
    if (key != null && jobFilledKeys.has(key)) {
      setJobFilledKeys(prev => { const next = new Set(prev); next.delete(key); return next; });
    }
    updateItem(index, 'cost_code_id', value);
  };

  // 🔻 `handleUrgencySelect` was DELETED here on 2026-08-09 with the urgency
  // picker it served. Its body carried the FIRST of the seven
  // `urgency === 'custom'` guards — `custom_needed_by_date: urgency ===
  // 'custom' ? prev.custom_needed_by_date : ''` — which CLEARED the order's
  // needed-by date on any change of urgency. With the date now an ordinary
  // order-level field, nothing may clear it as a side effect of anything.
  // **Do not reinstate a handler that writes `urgency`**: the column is read
  // for legacy rows and written by nothing.

  // ---- Line item handlers ----

  const updateItem = (index, field, value) => {
    setItems(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      // Sync item_category ↔ is_hire
      if (field === 'item_category') {
        updated[index].is_hire = value === 'Hire';
      }
      if (field === 'is_hire' && value === true) {
        updated[index].item_category = 'Hire';
      }
      // 🔻 LATEST CHANGE WINS (owner, 2026-09-03) — a category edit that
      // conflicts with other lines resets THOSE lines to "--Select--", never
      // itself. Without this, changing the line that ESTABLISHED the order's
      // mode silently flipped the mode and left the other lines conflicting,
      // producing a submission the server refuses. Full reasoning and why
      // `is_hire` must clear too: `utils/orderHireMode.js`.
      if (field === 'item_category' || field === 'is_hire') {
        return clearConflictingCategories(updated, index);
      }
      return updated;
    });
  };

  // 🚨 ORDERS ARE ALL-HIRE OR ALL-NON-HIRE — owner ruling, verbatim: "If no
  // item category is selected then we need a warning, toast, vibrate and
  // highlight the item category to show that one MUST be chosen before
  // adding a new line." `resolveOrderMode` returning null means every
  // existing item's category is still blank — nothing has established a
  // mode yet, so there is nothing to default a new line onto and the
  // buyer must decide first. Once a mode IS established, a new line
  // defaults onto it ("once the first item is set to hire all new lines
  // are set to hire" — the non-hire mirror has no single category to
  // default to, so it opens blank and the greyed dropdown steers it).
  const addItem = () => {
    const mode = resolveOrderMode(items);
    if (mode === null) {
      toast.error('Choose an item category before adding another line');
      // ⚠️ Best-effort only — NEVER load-bearing. iOS Safari does not
      // implement navigator.vibrate at all; the toast + highlight below
      // must carry the signal alone, per the brief's own instruction.
      try {
        if (typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function') {
          navigator.vibrate(200);
        }
      } catch { /* best-effort */ }
      setCategoryHighlightIndex(0);
      setCollapsedItems(prev => {
        if (!prev.has(0)) return prev;
        const next = new Set(prev);
        next.delete(0);
        return next;
      });
      return;
    }
    let newItem = mode === 'hire'
      ? { ...createEmptyItem(), item_category: 'Hire', is_hire: true }
      : createEmptyItem();
    // JOB FILL: a line added after a job was PICKED this session is a blank
    // line too, so it takes the job code (same gates as `applyJobFill`).
    if (jobPickedRef.current && jobFillAllowed && activeJobCode) {
      newItem = { ...newItem, cost_code_id: activeJobCode.id };
      const key = newItem.client_key;
      setJobFilledKeys(prev => new Set(prev).add(key));
    }
    setItems(prev => [...prev, newItem]);
  };

  const removeItem = (index) => {
    if (items.length <= 1) return;
    // A removed line takes its PENDING uploads with it — deleted on the server
    // now (owner, 2026-09-15: *"delete it if the user does not save the line or
    // they delete the line"*). A saved line's already-attached files are the
    // save's to delete (`PUT /api/orders/:id` unlinks a removed line's files).
    const removedLine = items[index];
    const removedLineKey = pendingLineKeyFor(removedLine);
    if (pendingFiles && removedLineKey) pendingFiles.removeLine(removedLineKey);
    setItems(prev => prev.filter((_, i) => i !== index));
    setCollapsedItems(prev => {
      const next = new Set();
      prev.forEach(i => {
        if (i < index) next.add(i);
        else if (i > index) next.add(i - 1);
      });
      return next;
    });
  };

  // ── THE ONE CHOKE POINT FOR REMOVING A LINE (2026-09-13) ────────────────
  // Both Remove controls — the line's actions-menu item and the plain button —
  // call THIS, never `removeItem` directly, so the pause cannot be on one and
  // missing from the other.
  //
  // 🔑 **IT PAUSES ONLY FOR A SAVED LINE ON A POST-APPROVAL ORDER.** A line with
  // no `id` was added during THIS edit and has never been ordered by anyone —
  // asking "it's already been ordered, remove anyway?" about it would be false.
  // Create mode never passes `postApprovalGuardStatus`, so it never pauses.
  //
  // 🔴 **THE RECEIVED NOTICE IS CHOSEN FROM THE RECEIVED STATE, READ LIVE FIRST**
  // (it chose the retired "remove received item" variant until 2026-09-28).
  // `is_received` is NOT on a form item (`buildInitialItems` does not carry it):
  // it lives in the page-built `received.items[id]` map — which reflects a tick
  // made during this edit — and, failing that, on the stored row in
  // `initialOrder.items`. `Number(x) === 1 || x === true`, because the column is
  // an INTEGER on both engines and a boolean is what a test fixture may send.
  const isItemReceived = (itemId) => {
    const live = received && received.items ? received.items[itemId] : null;
    const stored = (initialOrder?.items || []).find((row) => row && row.id === itemId);
    const flag = live ? live.is_received : stored ? stored.is_received : null;
    return flag === true || Number(flag) === 1;
  };

  // 🔀 2026-09-28 (owner: *"the fisrt pop up says untick delived before
  // deleteing. then … a warning that the proce will be removed from the budget
  // and are they sure and requires a note"*):
  //   1. a SAVED line that is received (read LIVE, above) → a blocking notice,
  //      OK only, and NOTHING is removed — at ANY status, because the server
  //      refuses it at any status (`received_item_not_removable`). This
  //      REPLACED the "Remove received item" anyway variant.
  //   2. otherwise, a saved line on a post-approval order → the red pause that
  //      names the money and DEMANDS a note (`PostApprovalEditDialog`'s
  //      required mode; the note travels as `removal_reasons[<id>]`).
  //   3. otherwise (a new line, or a draft/submitted order) → removed at once,
  //      exactly as before.
  // 🔀 2026-10-01 (owner, reversing 2026-09-28's "disabled on the last line"):
  //   0. the ONLY line in the FORM — `items` is form state, saved + unsaved
  //      lines, never the stored order, so with one saved and one new line the
  //      saved one is removable (rule 3, nothing persists until Save) — becomes
  //      a CANCEL: "Remove the only item?" whose "Cancel order" runs the page's
  //      own flow. Checked FIRST because nothing is removed here: the received
  //      notice and the post-approval note are about REMOVING a line, and the
  //      page's cancel flow carries its own reason box and gates.
  const isSavedOrderEdit = mode === 'edit' && initialOrder?.id != null;
  const requestRemoveItem = (index) => {
    const item = items[index];
    if (items.length <= 1) {
      if (isSavedOrderEdit && onRequestCancelOrder) setOnlyItemPrompt(true);
      return;
    }
    if (item && item.id != null && isItemReceived(item.id)) {
      setReceivedRemovalNotice(true);
      return;
    }
    if (!item || item.id == null || !isPostApprovalStatus(postApprovalGuardStatus)) {
      removeItem(index);
      return;
    }
    // 💷 "and the budget" only when budget spend really counts this line:
    // `server/helpers/budgetSpend.js` sums `line_total` for lines carrying a
    // cost code or pot, on orders that are not cancelled. Read off the STORED
    // row (the form item carries no `budget_pot_id`) — that row is what the
    // budget sums until the Save lands.
    //
    // 💷 THE AMOUNT IS THE STORED `line_total` (fix round 1, 2026-09-28): that
    // is what the server audits and what the budget summed — an unsaved price
    // edit on the line does not change what leaves. The live form total is the
    // fallback only when no stored row exists.
    // 🔗 AND IT INCLUDES THE LINE'S PRICE-ADJUSTMENT CHILDREN: `adjusts_item_id`
    // is ON DELETE CASCADE, so the server deletes them with the parent.
    const storedRows = initialOrder?.items || [];
    const stored = storedRows.find((row) => row && row.id === item.id);
    const children = storedRows.filter((row) => row && row.adjusts_item_id != null && row.adjusts_item_id === item.id);
    const storedTotal = stored ? toMoneyNumber(stored.line_total) : null;
    const parentAmount = storedTotal !== null ? storedTotal : getLineTotal(item);
    const childAmount = roundMoney(children.reduce((sum, row) => sum + (toMoneyNumber(row.line_total) ?? 0), 0)) ?? 0;
    const coded = Boolean(item.cost_code_id)
      || [stored, ...children].some((row) => row && (row.cost_code_id || row.budget_pot_id));
    setPendingRemoval({
      itemId: item.id,
      label: postApprovalItemLabel(item, index),
      amountText: formatCurrency(roundMoney(parentAmount + childAmount)),
      adjustmentsText: children.length > 0 ? formatCurrency(childAmount) : null,
      countsInBudget: coded && postApprovalGuardStatus !== 'cancelled',
    });
  };

  // Confirm → remove from the FORM. The stored row is deleted only by the Save
  // that follows, which is itself guarded (`OrderInlineEditor`'s choke point).
  // The index is re-resolved by id at confirm time rather than captured at
  // request time: ids are stable, positions are not.
  const confirmPendingRemoval = (reason) => {
    const pending = pendingRemoval;
    setPendingRemoval(null);
    if (!pending) return;
    const index = items.findIndex((row) => row.id === pending.itemId);
    if (index === -1) return;
    confirmedRemovalsRef.current.set(pending.itemId, { label: pending.label, reason });
    removeItem(index);
  };

  // 📱 Opening an item on a PHONE (owner 2026-10-03): it opens while the others stay
  // open, scrolls to the top under the pinned chrome, THEN the others close —
  // `hooks/useOpenAtTop.js` (the order and its reasons live there).
  // 📱 Smooth open/close of ONE line item on a phone (owner 2026-10-09: "same
  // with … individual items within line items"). The card's height is read in
  // the CLICK, before the re-render, and eased to its new height once the DOM
  // has changed — so typing in a card never triggers a measurement.
  // `hooks/useExpandAnimation.js` owns the rule (phone only, reduced motion off).
  const pendingItemAnim = useRef(new Map());
  const noteItemHeight = (index, fromEl) => {
    const card = fromEl && fromEl.closest ? fromEl.closest('[data-line-item]') : null;
    if (card) pendingItemAnim.current.set(index, { el: card, from: card.offsetHeight });
  };
  useLayoutEffect(() => {
    const pending = pendingItemAnim.current;
    if (!pending.size) return;
    pending.forEach(({ el, from }) => { if (el.isConnected) animateHeight(el, from, el.offsetHeight); });
    pending.clear();
  }, [collapsedItems]);

  const openItemOnPhone = (index, fromEl) => {
    const card = fromEl && fromEl.closest ? fromEl.closest('[data-line-item]') : null;
    noteItemHeight(index, fromEl);
    setCollapsedItems((prev) => { const next = new Set(prev); next.delete(index); return next; });
    const closeOthers = () => setCollapsedItems((prev) => {
      const others = new Set(items.map((_, i) => i)); others.delete(index);
      if ([...others].every((i) => prev.has(i))) return prev;
      return others;
    });
    openItemAtTop(card, closeOthers);
  };

  const toggleItemCollapse = (index, fromEl) => {
    if (isPhone && collapsedItems.has(index)) { openItemOnPhone(index, fromEl); return; }
    noteItemHeight(index, fromEl);
    setCollapsedItems(prev => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  };

  const collapseAllItems = () => setCollapsedItems(new Set(items.map((_, i) => i)));
  const expandAllItems = () => setCollapsedItems(new Set());
  // 📱 PHONE (owner 2026-10-03): the items fold INTO the Items header — the
  // header shows / hides the list of collapsed rows (closed by default on an
  // opened order, open on New Order); a ROW opens its item, and opening one
  // closes any other (`toggleItemCollapse`). Desktop keeps expand / collapse all.
  const itemsHeaderClick = items.length >= 1
    // 🔀 2026-10-09 (owner): the header FOLDS the list on every width, as on the
    // phone (it was expand-all / collapse-all on desktop).
    ? () => {
      // 📱 Owner 2026-10-30: on a phone New Order, opening Line Items with its one (blank) line
      // OPENS Item 1 — "they are logically trying to at least add one item". Phones only.
      if (isPhone && mode === 'create' && !itemsListOpen && items.length === 1) setCollapsedItems(new Set());
      else collapseAllItems();
      setItemsListOpen((o) => !o);
    }
    : undefined;
  const itemsHeaderClosed = !itemsListOpen;

  // ---- Totals ----

  // 🚨 A HIRE LINE'S TOTAL IS RATE × DURATION. This returned
  // `parseFloat(item.hire_rate) || 0` until 2026-08-11 — the raw rate out of
  // the single rate input, whatever the rate type — so the form showed £50 for
  // a 21-day hire at £50/day and the server stored the same £50.
  //
  // 🔑 The form's own state carries the typed rate in `hire_rate`, which is NOT
  // a column; `hireLineCost` reads the three rate COLUMNS. So map the form
  // shape onto the stored shape before asking, exactly as `normalizeItemRate`
  // does on submit — otherwise this preview and the stored value disagree,
  // which is the defect it is being fixed to close.
  //
  // Display only. `POST`/`PUT /api/orders` recompute from the same shared rule
  // (`server/helpers/hireCost.js`) and are the authority.
  const getLineTotal = (item) => {
    if (item.is_hire) {
      const rate = parseFloat(item.hire_rate) || 0;
      const rateType = item.hire_rate_type || 'daily';
      return hireLineCost({
        hire_rate_type: rateType,
        hire_from: item.hire_from,
        hire_to: item.hire_to,
        daily_rate: rateType === 'daily' ? rate : 0,
        weekly_rate: rateType === 'weekly' ? rate : 0,
        total_hire_rate: rate,
        // 🚨 LOAD-BEARING, added 2026-08-20. This literal is a SYNTHETIC shape,
        // so a field omitted here is a field `hireLineCost` cannot see. Quantity
        // was omitted, so this preview showed 1× while the server stored the
        // real quantity — the form-says-£50 / Gantt-says-£1,050 split again.
        // The Quantity input is rendered for hire lines too, so it is reachable.
        quantity: item.quantity,
      });
    }
    return (parseFloat(item.quantity) || 0) * (parseFloat(item.unit_price) || 0);
  };

  const subtotal = useMemo(
    () => items.reduce((sum, item) => sum + getLineTotal(item), 0),
    [items]
  );
  // 🔻 `vat` / `grandTotal` (subtotal × 0.2 and their sum) were deleted here on
  // 2026-08-08 with the VAT display. They are NOT kept as unused locals: a bare
  // ×0.2 hard-codes the standard rate, and hard-coding one rate is precisely the
  // mistake that got the feature removed (rates vary per line item — see
  // `hooks/useVatMode.js`). The retained arithmetic lives in `constants/vat.js`.

  // ---- File upload (parent-owned state) ----

  // 🔀 The ORDER-level upload state (`fileInputRef`, `orderDragOver`,
  // `showAttachments`) and its four handlers moved into
  // `components/OrderAttachments.js` with the markup on 2026-08-10 — that
  // component owns its own ref, drag state and disclosure. What remains here is
  // the PER-ITEM upload, which is a different surface with a different endpoint
  // (`POST /api/orders/:id/items/:itemId/files`) and stays in the form because
  // it renders inside each line item's own block.
  const itemFileInputRefs = useRef({});
  const [itemDragOver, setItemDragOver] = useState({});

  // ── THE ONE FORK BETWEEN STAGED AND IMMEDIATE, and it lives in ONE function
  // so the three entry points (pick, drop, and the paperclip's twin buttons)
  // cannot disagree about which mode they are in. Returns nothing; the caller
  // clears its own input.
  //
  // 🔑 `items[index].id` is read HERE, at the moment of the gesture, not from a
  // map the parent built earlier — see `itemFilesImmediate`'s docblock for why
  // an index captured before an add/remove is not a safe target.
  const acceptItemFiles = (index, files) => {
    if (!files || files.length === 0) return;
    const item = items[index];
    const itemId = item && item.id;
    // PENDING: no immediate target → upload now to the pending store, claimed
    // by the parent's save (2026-09-15). Never by index.
    if (pendingFiles && !(itemId != null && itemFilesImmediate && typeof itemFilesImmediate.onUpload === 'function')) {
      const lineKey = pendingLineKeyFor(item);
      if (!lineKey || pendingFilesDisabled) return;
      pendingFiles.add(lineKey, files);
      return;
    }
    if (itemFilesImmediate) {
      if (itemId == null || typeof itemFilesImmediate.onUpload !== 'function') return;
      // The in-flight guard is enforced HERE as well as on the controls,
      // because a drop reaches this through `handleItemDrop`, not through the
      // disabled button or input — and the drop zone deliberately stays
      // attached while a line is inert (see the zone's own comment below).
      if (itemFilesImmediate.uploadingItemIds
        && itemFilesImmediate.uploadingItemIds.has(itemId)) return;
      itemFilesImmediate.onUpload(itemId, files);
      return;
    }
  };

  const handleItemFileSelect = (index, e) => {
    acceptItemFiles(index, Array.from(e.target.files));
    if (itemFileInputRefs.current[index]) {
      itemFileInputRefs.current[index].value = '';
    }
  };

  const handleItemDragOver = (index, e) => { e.preventDefault(); e.stopPropagation(); setItemDragOver(prev => ({ ...prev, [index]: true })); };
  const handleItemDragLeave = (index, e) => { e.preventDefault(); e.stopPropagation(); setItemDragOver(prev => ({ ...prev, [index]: false })); };
  const handleItemDrop = (index, e) => {
    e.preventDefault();
    e.stopPropagation();
    setItemDragOver(prev => ({ ...prev, [index]: false }));
    acceptItemFiles(index, Array.from((e.dataTransfer && e.dataTransfer.files) || []));
  };
  // An INERT zone (a line uploading, or a line not saved yet) still CLAIMS the
  // drag — `preventDefault` is what stops the browser treating the drop as
  // "open this file in the tab" — but shows no "Drop files here" highlight and
  // tells the OS cursor the drop is not accepted.
  const refuseItemDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer) {
      try { e.dataTransfer.dropEffect = 'none'; } catch (_) { /* read-only in some engines */ }
    }
  };

  // `formatFileSize` is imported from `components/OrderAttachments.js` rather
  // than redefined — the per-item list below and the order-level list in that
  // component must round identically or one surface reads "1.5 KB" where the
  // other reads "1.6 KB" for the same bytes.

  // ---- Submit ----

  // ── THE DOUBLE-SUBMIT GUARD (2026-08-13, reported live by a user:
  //    "if you hit submit twice on the order page it can make a duplicate
  //    order") ──────────────────────────────────────────────────────────────
  // `hooks/useSubmitGuard.js` — EXTRACTED from this file 2026-08-13; the hook's
  // docblock carries the full history (the synchronous-`finally` release that
  // let both async parents — `pages/NewOrder.js` POST,
  // `components/OrderInlineEditor.js` PUT — be double-fired, and why the ref is
  // the gate while `busy` only drives `disabled`). The semantics here are the
  // hand-rolled original's, byte for byte: second call while armed is a
  // synchronous no-op; the gate holds until the parent's `onSubmit` promise
  // SETTLES (resolve or reject); a synchronous `onSubmit` releases before
  // `handleSubmit` returns.
  // `busy` (formerly `submitInFlight`) is read ONLY by the submit button's
  // `disabled` — it covers the frames before a parent's `submitting` prop has
  // flushed, and callers that never pass one. Do not gate `handleSubmit` on it;
  // state is asynchronous, which is exactly the hole the ref closes.
  // ⚠️ CLIENT-SIDE ONLY: this closes the reported case (double-click / Enter
  // in one tab). Two tabs, or a network-layer retry, need the server-side
  // idempotency key the parents send on `POST /api/orders`.
  const { guard, busy: submitInFlight } = useSubmitGuard();

  // ── ORDERS ARE ALL-HIRE OR ALL-NON-HIRE (owner ruling, 2026-09-02) ───────
  // Convenience only — see `utils/orderHireMode.js`'s docblock and the
  // design note. The shared toast (never `window.alert`), via
  // `useToastOrNoop` rather than the throwing `useToast()` — DELIBERATE,
  // and a departure from that hook's own "stays the default" guidance,
  // reasoned through rather than reached for by habit: `OrderForm` is
  // rendered BARE (no `ToastProvider` ancestor) by a large number of
  // pre-existing test files (`OrderForm.hireTotal.test.js`,
  // `OrderForm.leadTime.test.js`, …), so the throwing hook would break all
  // of them on render. In the running app `App.js` always mounts
  // `ToastProvider` above every route that renders this form, so the noop
  // branch is never reached in production — this differs from `useToast()`
  // only inside a bare test harness, which is exactly the case
  // `useToastOrNoop`'s own docblock carves out.
  // `categoryHighlightIndex` drives a 2s pulse ring on the item category
  // `<select>` that most needs a choice, same auto-clear idiom as
  // `OrderInlineEditor`'s `dirtyWarnSignal` pulse.
  const toast = useToastOrNoop();
  const [categoryHighlightIndex, setCategoryHighlightIndex] = useState(null);
  useEffect(() => {
    if (categoryHighlightIndex === null) return undefined;
    const timer = setTimeout(() => setCategoryHighlightIndex(null), 2000);
    return () => clearTimeout(timer);
  }, [categoryHighlightIndex]);
  // Re-derived every render — `items` is a handful of rows, no memo needed,
  // and this must always reflect the LATEST edit. `orderHireDeciderIndex` is
  // the one line whose own dropdown stays fully open; every other line's
  // options are derived from it.
  const orderHireMode = resolveOrderMode(items);
  const orderHireDeciderIndex = decidingItemIndex(items);

  // ── WHAT THE CONFIRMED REMOVALS TELL THE SAVE (2026-09-13) ──────────────
  // Handed to `onSubmit` as `meta.postApprovalRemovals`:
  //   notes      — `Removed "<item>": <reason>` for each CONFIRMED removal that
  //                is still a removal and had a reason; the parent folds them
  //                into the PUT's `edit_reason` so the audit row says WHY.
  //   reasons    — `{ "<order_items.id>": reason }`, the same removals keyed by
  //                id; `handleSubmit` puts it in the PUT as `removal_reasons`
  //                (2026-09-28 — the server refuses a post-approval removal
  //                without one, and `notes` is truncatable so cannot prove it).
  //   onlyChange — true when those removals are the WHOLE difference from the
  //                hydrated snapshot.
  //
  // 🔑 **ONE PAUSE PER INTENT.** When `onlyChange` is true the user has already
  // been told the order is placed and has already said "remove it anyway" —
  // the Save pause would ask the same question about the same act a second
  // time, which is how a confirmation stops being read. Any OTHER change (a
  // quantity, a supplier, a second line removed without the pause) makes it
  // false and the Save pause shows, because that change has not been asked
  // about. Computed against the SAME `snapshotRef` the dirty flag uses — not a
  // second notion of "unsaved".
  //
  // 🪤 A removal is counted only while its id is in the snapshot AND absent
  // from the current items, so a stale entry (a re-hydrated form, a line the
  // user somehow restored) can neither add a note nor claim `onlyChange`.
  const describeConfirmedRemovals = () => {
    const confirmed = confirmedRemovalsRef.current;
    if (!confirmed || confirmed.size === 0) return { notes: [], reasons: {}, onlyChange: false };
    const snapshot = JSON.parse(snapshotRef.current);
    const snapshotIds = new Set((snapshot.items || []).map((row) => row.id).filter((id) => id != null));
    const currentIds = new Set(items.map((row) => row.id).filter((id) => id != null));
    const removedIds = [...confirmed.keys()].filter((id) => snapshotIds.has(id) && !currentIds.has(id));
    if (removedIds.length === 0) return { notes: [], reasons: {}, onlyChange: false };
    const notes = removedIds
      .map((id) => removalReasonNote(confirmed.get(id).label, confirmed.get(id).reason))
      .filter(Boolean);
    // Per line, keyed by the stored id — what the server's
    // `removal_reason_required` gate reads (2026-09-28). Blank never enters.
    const reasons = {};
    removedIds.forEach((id) => {
      const text = String(confirmed.get(id).reason || '').trim();
      if (text) reasons[String(id)] = text;
    });
    const removedSet = new Set(removedIds);
    const onlyChange = JSON.stringify({ form, items }) === JSON.stringify({
      form: snapshot.form,
      items: (snapshot.items || []).filter((row) => !removedSet.has(row.id)),
    });
    return { notes, reasons, onlyChange };
  };

  // Drop every validation error recorded against line `index` (typing into the
  // field that raised it). One definition for the Quantity and Description
  // inputs rather than two copies of the same `setErrors` updater.
  const clearItemErrors = (index) => {
    if (!errors.itemErrors || !errors.itemErrors[index]) return;
    setErrors(prev => {
      const newItemErrors = { ...prev.itemErrors };
      delete newItemErrors[index];
      if (Object.keys(newItemErrors).length === 0) {
        const { itemErrors, ...rest } = prev;
        return rest;
      }
      return { ...prev, itemErrors: newItemErrors };
    });
  };

  const handleSubmit = (e) => {
    // Optional event: a real submit passes one, `submitRef.current()` passes
    // nothing. Everything below is identical either way — that identity is the
    // whole contract of `submitRef`.
    if (e) e.preventDefault();
    if (readOnly) return; // No submission in read-only mode
    // `submitDisabled` gates the SUBMIT ITSELF, not only the button's
    // `disabled` attribute — a disabled button drops a click, but a raw
    // `submit` event (Enter in a field) or `submitRef.current()` reaches this
    // function directly and must be refused the same way.
    if (submitDisabled) return;

    // The whole submit body runs INSIDE the guard: while a submission is in
    // flight the second call drops before validation, exactly as the old
    // `if (submittingRef.current) return;` early-return did.
    guard(() => {
      // 🔻 The `{ hasNoAssignments }` option came off on 2026-08-12 with the
      // `'Production is required'` check it existed to exempt one cohort from.
      // `hasNoAssignments` itself is still live — it gates the Sub Department
      // select's `Not Assigned` option.
      // 📅 The stored rows, so a legacy hire line saved with no end date does
      // not block an unrelated edit (the end-date rule's carve-out). EDIT only:
      // a create's `initialOrder` is a PREFILL (NewOrder's `duplicateFrom`)
      // whose item ids name another order's rows — every line is new there.
      const storedItems = mode === 'edit' ? (initialOrder?.items || []) : [];
      const validationErrors = validateForm(form, items, storedItems);
      if (Object.keys(validationErrors).length > 0) {
        setErrors(validationErrors);
        // A per-item error must be reachable where it is: both EDIT callers
        // pass `initialItemsCollapsed`, and a collapsed line renders none of
        // its fields — so the inline message (and the Remove control a blank
        // SAVED line's message points at) would be hidden. Expand every line
        // carrying an error; lines without one keep their state.
        setDetailsOpen(true);
        if (validationErrors.itemErrors) {
          setItemsListOpen(true);
          const errored = Object.keys(validationErrors.itemErrors).map(Number);
          setCollapsedItems(prev => {
            if (!errored.some(i => prev.has(i))) return prev;
            const next = new Set(prev);
            errored.forEach(i => next.delete(i));
            return next;
          });
        }
        return undefined; // non-thenable → guard releases synchronously
      }

      setErrors({});
      const postApprovalRemovals = describeConfirmedRemovals();
      // `removal_reasons` (2026-09-28) rides IN the payload so every parent's
      // PUT carries it without learning about it; the key is absent unless a
      // confirmed removal gave one, so every other body is byte-identical.
      const built = Object.keys(postApprovalRemovals.reasons).length > 0
        ? { ...buildPayload(form, items), removal_reasons: postApprovalRemovals.reasons }
        : buildPayload(form, items);
      // 🔗 S1 GAG LINK: `gag_id` rides only when the Job / Rig / Gag box was
      // actually changed (picked, or typed → null). Untouched, the key is
      // ABSENT, so the server keeps the stored link on an edit and applies its
      // exact-title rule on a create (helpers/gagLink.js#resolveEditGagLink) —
      // an unrelated save never re-decides the link.
      const snapForm = JSON.parse(snapshotRef.current).form;
      const gagTouched = form.gag_id !== snapForm.gag_id || form.job_rig !== snapForm.job_rig;
      const { gag_id: _gagId, ...withoutGag } = built;
      const payload = gagTouched ? built : withoutGag;
      // Hand off to parent. Parent owns the network call + post-submit UX.
      // Returning the parent's promise is what holds the guard until it
      // settles.
      return onSubmit
        ? onSubmit(payload, { form, items, postApprovalRemovals })
        : undefined;
    });
  };

  // Publish the submit handle. Assigned on EVERY render, not once on mount, so
  // it always closes over the current `form`/`items` — a mount-only assignment
  // would submit the state as it was when the row opened, which is precisely the
  // edit the user is trying to save. Cleared on unmount so a collapsed row's
  // handle cannot be called against a dead component.
  useEffect(() => {
    if (!submitRef) return undefined;
    submitRef.current = handleSubmit;
    return () => { submitRef.current = null; };
  });

  // Expose a way for the parent (NewOrder) to reset the form after a
  // successful submission. We do this by exposing reset via a side-channel
  // ref-like prop is overkill — instead, NewOrder will remount the form
  // by changing its `key` or accept a partial state reset through props.
  // Kept simple here: parent just changes the `initialOrder` to a fresh
  // duplicate or clears it; the effect above re-hydrates.

  // ---- Styles ----

  const inputClass = (fieldName) =>
    `w-full px-2 h-11 md:h-8 border rounded-lg text-base md:text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
      errors[fieldName] ? 'border-red-500 bg-red-50' : 'border-gray-300'
    }`;

  // 🎨 Owner ruling 2026-09-28: match the item fields' label style exactly —
  // see `constants/inputs.js#FIELD_LABEL_CLASS`, the one definition now shared
  // by these top fields and the line-item fields further down this form.
  const labelClass = FIELD_LABEL_CLASS;
  const itemInputClass = 'w-full border border-gray-300 rounded-lg px-2 h-11 md:h-8 text-base md:text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none focus:border-blue-500';

  return (
    <LockedFieldContext.Provider value={readOnly ? showLockedNotice : null}>
    <OrderFormDecisionContext.Provider value={decisionContextValue}>
    <form id="order-form" onSubmit={handleSubmit} noValidate className="space-y-6">
      {/* Order Details */}
      <div className="bg-white rounded-lg shadow-md p-2.5 space-y-4" data-form-card="">
        {/* ═══════════════════════════════════════════════════════════════════
            THE `Order Details` HEADER — AND SINCE 2026-08-11 IT IS ALSO WHERE
            THE ORDER-ROW ACTION GROUP LIVES, IN THE EDIT BRANCH ONLY.
            ═══════════════════════════════════════════════════════════════════
            🔑 **TWO BRANCHES, ONE CLASS STRING (`SECTION_BAR`), AND THE `null`
            BRANCH IS BYTE-IDENTICAL TO WHAT SHIPPED.** With no `headerActions`
            this renders the same single `<h3>` carrying the same classes and
            the same `data-section-title` it has always carried — that is what
            keeps `pages/NewOrder.js` and `pages/OrderPage.js` untouched, and it
            is asserted rather than claimed (`OrderForm.headerActions.test.js`
            §1 compares the rendered `<h3>`'s className and attributes against
            the literal). **Never let the two branches drift onto two class
            strings**; that is why `SECTION_BAR` exists.

            🪤 **`data-section-title` MOVES TO THE WRAPPER IN THE ACTIONS
            BRANCH, AND IT MUST.** `client/src/index.css:202-208` is
            `html.dark [data-section-title] { background-color:
            rgba(51,65,85,0.3) !important }` — a DARK-MODE-ONLY fill that no
            light screenshot can show. Left on the inner `<h3>` the fill would
            cover the title and stop dead before the buttons, giving a
            half-painted bar to dark-mode users alone. It is the same
            attribute-driven `index.css` table already recorded against
            `OrderExpandedDetails`'s section headers.

            🚨 **`-mx-2.5 -mt-2.5` IS LOAD-BEARING AND TRAVELS WITH THE BAR, NOT
            WITH THE TITLE.** It cancels the card's own `p-2.5` so the header
            spans the full card width and its `border-b` reads as the card's
            own divider. On the wrapper it keeps doing that; left on the `<h3>`
            inside a flex row it would drag the title out past the card edge
            and leave the buttons inset.

            📱 **AT 375 THE BAR WRAPS AND THE GROUP KEEPS TODAY'S MOBILE
            GEOMETRY.** The actions node is `w-full md:w-auto`, so below `md`
            the title takes line 1 and the group takes line 2 at full width —
            which is exactly the 2-column grid with its `min-h-[44px]` tap
            targets that the group already rendered when it sat above the form.
            **Nothing is shrunk to win a row**; the 44px floor is never traded,
            per `constants/inputs.js`. Above `md` the group sits at the right
            end of the title's line.

            🔴 **The `Line Items` header at the bottom of this file carries the
            same `data-section-title` and is DELIBERATELY NOT TOUCHED** — it is
            a separate JSX site, not a shared component, and restyling every
            section header to suit one of them is the trap.

            🔀 **EXTRACTED TO `components/OrderSectionHeader.js` (2026-08-16)**
            so this header and the shared read-only panel's new one
            (`OrderExpandedRow`'s `readOnlyPanelBody`) cannot drift onto two
            class strings — owner: *"share code where possible."* Byte-identical
            output to what this file rendered inline; `OrderForm.headerActions
            .test.js` §1/§2 pin the literal and did not need to change. */}
        <OrderSectionHeader
          actions={headerActions}
          actionsTestId="order-form-header-actions"
          // Only on an OPENED order (the list panels and the order page pass
          // `initialItemsCollapsed`); New Order's header is untouched.
          collapse={initialItemsCollapsed ? { open: detailsOpen, onToggle: () => setDetailsOpen((o) => !o), controls: 'order-details-body' } : null}
        />
        {headerActionsBelow}

        {/* ── ORDER DETAILS GRID — ONE ROW FROM `xl` (1280px) UP; BELOW THAT,
            THE EXISTING MOBILE/TABLET STACK. FOURTH PASS THE SAME DAY
            (2026-08-16) ─────────────────────────────────────────────────────
            🔀 **FOUR PASSES, EACH A REAL CONTRACT CHANGE OVER THE LAST.**
            Pass 1 merged the old two-grid layout into one grid but let it
            settle onto TWO rows at `lg` (superseded). Pass 2, after the owner
            reviewed pass 1 on staging (*"'Supplier / Exclusive /
            Flow-1786826023958-1 / Needed By Date / dd/mm/yyyy / Lead Time'
            these are still not on the same line as PO Number etc."*), put
            every field on ONE row with a FIXED `lg:grid-cols-[repeat(13,
            minmax(0,1fr))]` (superseded). Pass 3 fixed a dead-whitespace
            defect in pass 2 by making the track count PER-ROLE (superseded
            as a `lg:`-prefixed layout — the per-role arithmetic below is
            UNCHANGED by this pass, only the breakpoint moved). **Pass 4,
            this one**, moves the whole one-row layout up to `xl` (1280px).
            Owner, on staging, once more: *"as the screen narrows some LABELS
            wrap to two lines and push their inputs down, so the single row
            looks disjointed … at the point the labels go to two rows we
            should just go to the double row/MOBILE version."* **"The double
            row/mobile version" is the layout that was ALREADY HERE below the
            one-row breakpoint** — `grid-cols-1` (mobile) / `sm:grid-cols-2`
            (tablet) — not a new bespoke desktop tier: nothing needed
            building, only moving where the one-row layout starts.

            🔑 **EVERY Order Details FIELD IS ONE GRID CHILD, IN DOCUMENT
            ORDER:** PO · Requested By · Sub Department · Job/Rig/Gag ·
            Supplier · Needed by (`custom_needed_by_date`) · Lead Time ·
            Department (admin only, still last) — that is ROW 1, unchanged
            since pass 4. Production stays deleted (see the removal note
            above `productionDisplay` was) and no compensation class is
            needed — there is no second grid to keep in step with, exactly as
            pass 1 established.

            🔀 **A SECOND ROW, ADDED 2026-09-13 (owner's second-pass ruling):**
            `orderDetailsRow2Lead` (Expected Delivery, pinned under PO) then
            `orderDetailsRow2Rest` (Internal notes, filling the rest of the
            row) — two more grid children of THIS SAME container, placed with
            `xl:row-start-2` rather than added to row 1's document order. See
            the props' own docs and `orderDetailsRow2RestClass` below.

            📏 **THE SIZING RULE, the owner's own hint, verbatim: *"po, need by
            and lead time can be half width. if it helps"* and, on a follow-up
            refinement, that Supplier joins Requested By / Sub Department /
            Job/Rig/Gag at the SAME "full" span rather than wider.** So there
            are exactly two sizes, UNCHANGED by this pass and now expressed at
            `xl:` instead of `lg:`:
              FULL (`xl:col-span-2`) — Requested By, Sub Department,
                Job/Rig/Gag, Supplier, Department (admin).
              HALF (`xl:col-span-1`) — PO Number, Needed by, Lead Time.
            Nothing else varies per field; the "which fields need more room"
            judgement is the owner's, not a developer's.

            🔢 **THE PER-ROLE TRACK COUNT (pass 3) IS UNCHANGED — ONLY ITS
            PREFIX MOVED.** `orderDetailsGridColsClass` (above, beside
            `canManageOrderFields`) still picks N from the same two
            conditions that already gate PO and Department:
              isAdmin === true                                  → N = 13 (1+2+2+2+2+1+1+2)
              canManageOrderFields === true, isAdmin === false   → N = 11 (1+2+2+2+2+1+1)
              canManageOrderFields === false (⇒ isAdmin false)   → N = 10 (2+2+2+2+1+1)
            `isAdmin` implies `canManageOrderFields` (see the comment beside
            `orderDetailsGridColsClass`), so those three cases are exhaustive
            and mutually exclusive. **A wrong sum is still the one way that
            bug reappears** (too LOW re-wraps a variant within the `xl` tier;
            too HIGH reopens dead trailing whitespace) — verify both whenever
            a span or a conditional member changes.

            🚨 **`minmax(0,1fr)` ON EVERY TRACK, WHICHEVER N APPLIES, IS WHAT
            MAKES A CRAMMED ROW SHRINK INSTEAD OF OVERFLOW ONCE IT IS ONE
            ROW.** A bare `1fr` is `minmax(auto, 1fr)` — its AUTO minimum
            re-arms each track's intrinsic content width, so a long value (a
            supplier name, the literal Flow-generated PO placeholder the
            owner quoted) can still force the row wider than its container
            and reopen a horizontal scrollbar exactly like the one this class
            exists to prevent (the same fact `OrderTableView.js` documents at
            length for its own resizable columns — read that file's
            `minmax(0, …)` notes before "tidying" this back to a bare
            literal). **Each of the three `xl:grid-cols-[repeat(N,minmax(0,
            1fr))]` strings is written as ONE COMPLETE LITERAL, never built
            from `N` at runtime**, because Tailwind's scanner reads source
            text — see the `lg:grid-cols-${n}` warning at the top of this
            file, and the reasoning repeated beside `orderDetailsGridColsClass`
            itself.

            📏 **WHY `xl` (1280px), AND HOW TO MOVE IT — see the fuller note
            beside `orderDetailsGridColsClass` above.** The longest label in
            this row is "Job / Rig / Gag" (a FULL field); `xl` is the next
            Tailwind stop up from `lg` (1024) and the owner's own suggestion
            to start at. **If labels still wrap between 1280 and ~1400 on his
            screen he may want `2xl` (1536) instead — a ONE-LINE change**:
            every `xl:` in this block (the container class above plus every
            field's span/start below) becomes `2xl:`, and nothing else moves.
            This is a BROWSER judgement, not a class-model claim.

            ⚠️ **JUDGMENT CALL, CARRIED FORWARD FROM EARLIER PASSES: fields
            are still genuinely NARROW inside the one-row tier, and the owner
            asked for exactly this trade** (*"Accept narrower fields"*, and
            separately *"he'd rather SEE one cramped row and judge it than
            have you pre-empt it"*). PO's own label already exceeded its
            span-1 cell in the OLD 10-track `lg:` grid at 1024 (measured
            elsewhere in this file); PO's label wrapping to two lines (its
            existing, already-safe overflow behaviour — see the PO Number
            comment) is expected at every one of the three track counts, not
            a regression to fix here.

            ⚠️ **Supplier's start track still varies by role** (PO rendered
            vs hidden), exactly as pass 1 recorded — that per-role difference
            is cosmetic, not an alignment defect, because nothing in a single
            grid needs to line up with a second one any more.

            📵 **MOBILE / `sm` / `md` ARE DELIBERATELY UNTOUCHED BY EVERY ONE
            OF THESE FOUR PASSES, AND THIS PASS IS THE ONE THAT MAKES THAT
            LAYOUT VISIBLE AGAIN BELOW `xl`.** Owner: *"DO NOT change the
            mobile / sm layout at all … He's only looking at desktop right
            now."* Every `sm:`/`md:`-prefixed class, every unprefixed
            (mobile-default) class, PO's `sm:col-start-1 sm:row-start-1` pin
            and Requested By's `hidden md:block` stay byte-for-byte what they
            were. **Between 1024px (the old `lg:` threshold) and 1280px
            (the new `xl:` one) the row now renders the `sm:grid-cols-2`
            tablet layout it always had below 640px — that IS the "double
            row/mobile version" fallback the owner asked for, not a
            regression.** A genuine mobile layout improvement was NOT looked
            for; if one surfaces while reading this section, it is flagged in
            the change's own report for later, not applied here.

            ⚠️ jsdom applies no stylesheet and performs no layout, so **the
            fills-the-row claim above is about the CLASS MODEL — each role's
            declared track count equalling that role's own declared span sum
            exactly — not a rendered pixel, and NOT a claim about which
            breakpoint labels actually wrap at.** Whether narrow tracks read
            as "comfortably cramped" or "too tight" inside the `xl` tier, and
            whether 1280px is the right place to switch, is the BROWSER
            check this pass exists to let the owner make; nothing under
            `client/src` can perform it. */}
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); setDetailsOpen((o) => !o); }}
          className="md:hidden flex items-center gap-2 w-full text-left"
          aria-expanded={detailsOpen}
          aria-controls="order-details-body"
          data-testid="details-toggle"
          data-phone-section-head=""
        >
          <svg data-chevron xmlns="http://www.w3.org/2000/svg" className={`h-3.5 w-3.5 text-gray-400 transition-transform ${detailsOpen ? '' : '-rotate-90'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
          <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Details</span>
        </button>
        <div ref={detailsBodyRef} id="order-details-body" className={`space-y-3${!detailsShown ? ' hidden' : ''}`} data-testid="order-details-body">
        <div className={`grid grid-cols-1 sm:grid-cols-2 ${orderDetailsGridColsClass} gap-3`} data-testid="order-details-grid" data-phone-pair-grid="">
          {/* PO Number — order managers of THIS ORDER'S OWN department (on create,
              of the department it is being filed into). Server counterparts, all
              three the same capability: `POST /api/orders` (`canManageOrdersHere`,
              routes/orders.js:1455 → `finalPoNumber`, :1468),
              `PUT /api/orders/:id` (`canSetPoNumber`, :1850) and
              `PATCH /api/orders/:id/po-number` (`requireDepartmentCapability(...,
              order.department_id, 'canManageDepartmentOrders')`, :2595). The first
              two DROP the field silently for a caller without it; the third
              refuses outright. Pinned to col 1 row 1 so it never reflows.
              Half-size at lg.
              🔀 **VISIBILITY gate is `showPoNumberField`, not `canManageOrderFields`,
              since 2026-08-16** — see that constant's own comment above. Editability
              is unaffected: the `readOnly` ternary below still always renders
              `ReadOnlyValue` in read-only mode, whoever is looking. */}
          {showPoNumberField && (
            <div className="sm:col-start-1 sm:row-start-1 xl:col-start-1 xl:row-start-1 xl:col-span-1">
              {poNumberFieldSlot || (<>
              <label htmlFor="po_number" className={labelClass}>PO Number</label>
              {readOnly ? (
                <ReadOnlyValue testId="po-number">{form.po_number}</ReadOnlyValue>
              ) : (
                // 🚨 `type="text"`, NOT `type="number"` — CHANGED 2026-08-08, the same
                // DEFECT FIX as `InlinePoNumberEditor`. A number input refuses any value
                // that is not a valid numeric literal, so with PO numbers now stored
                // carrying a `PO-` prefix (`server/helpers/poNumber.js`; product owner:
                // *"Make all Po numbers start with PO-"*) this control would render an
                // EMPTY box for an order that HAS a PO — and a buyer opening the edit form
                // would conclude it had been lost, then save the blank over it.
                // It was already wrong for `PO-BUYER-345`, a real value in the dev database
                // that could never have been typed here: the column is free TEXT on both
                // engines (`TEXT` / `VARCHAR(100)`) and always has been.
                // ⚠️ Do not "restore" the number input. The value is not a number.
                // 🪤 A `{/* … */}` JSX comment does NOT work in this position — it is the
                // single-expression slot of a ternary, so a comment node beside the element
                // is two children and a syntax error. A `//` comment in the expression
                // position, as here, is fine.
                <input
                  type="text"
                  id="po_number"
                  name="po_number"
                  value={form.po_number || ''}
                  onChange={handleChange}
                  // The server normalises whatever is typed, so a bare `12345`
                  // is still a perfectly good thing to enter — the placeholder
                  // shows the stored form so the prefix is not a surprise.
                  placeholder="e.g. PO-12345"
                  className={inputClass('po_number')}
                  data-testid="po-number"
                />
              )}
              </>)}
            </div>
          )}

          {/* 🔻 PRODUCTION — REMOVED OUTRIGHT 2026-08-16 (was here, read-only,
              since 2026-08-12). Product owner: *"I'm thinking we can loose the
              'Production' lable as its a locked field and the user know the
              production they are in."* Not merely hidden: the `<label
              htmlFor="production_id">`, the `production-dropdown` testid and
              the `ReadOnlyValue` it wrapped are all gone from the DOM in every
              mode. `form.production_id` is UNCHANGED — still hydrated by
              `buildInitialForm` on the edit path, still stripped from every
              payload by `buildPayload` (see the note above `productionDisplay`
              used to be, and `utils/orderFormHelpers.js`). See
              `OrderForm.productionReadOnly.test.js` for the CONTRACT CHANGE
              this made to that file: §1-§3 (the display's content and its
              create/edit asymmetry) are gone with the field; §4 (the payload
              never carries `production_id`) and §5 (`Production is required`
              stays deleted) are UNCHANGED, because neither ever depended on
              the display existing. */}

          {/* Requested By — hidden on mobile when value already set (mobile
              behaviour untouched by every one of the four passes, see the
              grid comment).
              🔻 `lg:col-span-3` → `lg:col-span-2` (pass 2), then that same
              `col-span-2` moved from the `lg:` prefix to `xl:` (pass 4) —
              the SAME "FULL" size as Sub Department / Job/Rig/Gag / Supplier,
              see the sizing rule in the grid's own top comment. It was `3`
              from 2026-08-13 (absorbing the retired `Personal Ref` field's
              track) until the one-row pass's fixed FULL/HALF budget replaced
              that reasoning outright. */}
          <div className={`xl:col-span-2 ${form.requested_by ? 'hidden md:block' : ''}`}>
            <label htmlFor="requested_by" className={labelClass}>Requested By{readOnly ? '' : ' *'}</label>
            {readOnly ? (
              <ReadOnlyValue testId="requested-by">{form.requested_by}</ReadOnlyValue>
            ) : (
              <>
                <input
                  type="text"
                  id="requested_by"
                  name="requested_by"
                  value={form.requested_by}
                  onChange={handleChange}
                  placeholder="Your name"
                  className={inputClass('requested_by')}
                  data-testid="requested-by"
                  aria-required="true"
                  aria-invalid={!!errors.requested_by}
                  aria-describedby={errors.requested_by ? "requested_by-error" : undefined}
                />
                {errors.requested_by && (
                  <p id="requested_by-error" role="alert" className="text-red-500 text-xs mt-1">{errors.requested_by}</p>
                )}
              </>
            )}
          </div>

          {/* 🔻 `Personal Ref` (track 6, `lg:col-span-1`) was REMOVED here on
              2026-08-13 — the ORDER-level field is retired (it had already left
              the title and search; the form input and the PDF row went with
              this change). Requested By absorbed its track. The user-level
              GDPR `users.personal_ref` is a different column and untouched. */}

          {/* Sub Department */}
          <div className="xl:col-span-2">
            <label htmlFor="sub_department" className={labelClass}>Sub Department</label>
            {readOnly ? (
              <ReadOnlyValue testId="sub-department">{form.sub_department}</ReadOnlyValue>
            ) : (
              <select
                id="sub_department"
                name="sub_department"
                value={form.sub_department}
                onChange={handleChange}
                className={inputClass('sub_department')}
                data-testid="sub-department"
                disabled={hasNoAssignments}
              >
                {hasNoAssignments ? (
                  <option value="">Not Assigned</option>
                ) : (
                  <>
                    <option value="">-- Select --</option>
                    {subDepartments.map(sd => (
                      <option key={sd.id} value={sd.name}>
                        {sd.name}
                      </option>
                    ))}
                  </>
                )}
              </select>
            )}
          </div>

          {/* Job / Rig / Gag — free text with gag-name suggestions */}
          <div className="relative xl:col-span-2">
            <label htmlFor="job_rig" className={labelClass}>Job / Rig / Gag</label>
            {readOnly ? (
              <>
                <ReadOnlyValue testId="job-rig">{form.job_rig}</ReadOnlyValue>
                {form.gag_id != null && <GagLinkedMarker />}
              </>
            ) : (
              <>
                {/* 🔀 2026-10-28 (job cost codes): THE shared `ui/SuggestInput`
                    replaced a hand-rolled `gag-suggestions-list` listbox. Job
                    codes first ("J01 · Exploding car"), then the other gags;
                    free text allowed. Only a PICK links the gag (`onPick`). */}
                <SuggestInput
                  id="job_rig"
                  value={form.job_rig}
                  onChange={handleJobRigText}
                  onPick={handleJobPick}
                  options={jobOptions}
                  optionGroup={hasJobCodeOptions ? (label) => jobOptionGroup.get(label) || null : null}
                  openOnFocus
                  placeholder="Which job is this for?"
                  inputClassName={inputClass('job_rig')}
                  ariaLabel="Job / Rig / Gag"
                  listLabel="Jobs and gags"
                  testId="job-rig"
                />
                {/* Static (not absolute) so it reads as helper text belonging to
                    the field rather than overlaying the row below; the list above
                    is the absolutely-positioned element. `role="status"`
                    announces it without moving focus. */}
                {gagHint && (
                  <p
                    // "No jobs, rigs or gags match" is hidden at every width, as
                    // "No suppliers match" is (owner 2026-10-03: typing a new job
                    // is normal). The nothing-set-up-yet line still shows.
                    className={`${HINT_CLASS}${gagHint === 'empty' ? '' : ' hidden'}`}
                    role="status"
                    data-testid="gag-suggestions-hint"
                  >
                    {gagHint === 'empty'
                      ? 'No jobs, rigs or gags set up for this department yet'
                      : 'No jobs, rigs or gags match'}
                  </p>
                )}
                {form.gag_id != null && <GagLinkedMarker />}
              </>
            )}
          </div>

          {/* ── SUPPLIER, NEEDED BY DATE, LEAD TIME — SAME ROW AS PO ETC.
              SINCE 2026-08-16, WAS A SEPARATE SIBLING GRID BEFORE THAT. ─────
              Product owner: *"then we can put supplier, needed by date and
              lead time on the same row as Po, Requested by etc."*, and then,
              on staging, after a first pass settled onto two rows: *"these
              are still not on the same line as PO Number etc."* Same three
              controls, same `name`/`id`/`value`/`onChange`/`data-testid` —
              only the WRAPPER, the spans and (Needed By Date's) VISIBLE
              LABEL changed; see the grid's own top comment for the
              per-role N arithmetic and the browser-check caveat.

              🔀 The Needed By Date field's rendered `<label>` reads "Needed
              by" since 2026-08-16 (was "Needed By Date") — a copy change
              only, folded into this same pass; `custom_needed_by_date`
              stays the `id`/`name`/`data-testid`/state key throughout this
              comment block, which still calls the field by its old
              descriptive name for continuity with its own history.

              📏 Supplier is `xl:col-span-2` (was `lg:col-span-2` before the
              breakpoint moved) — the "FULL" size, matching Requested By /
              Sub Department / Job/Rig/Gag — per the owner's refined sizing
              note (*"Supplier can stay full/wider"*, narrowed to exactly
              "full" on his follow-up). Needed By Date ("Needed by") and
              Lead Time are `xl:col-span-1` — "HALF" — per *"po, need by and
              lead time can be half width. if it helps."*
              Neither span is chosen
              for grid-alignment reasons any more; the two-size budget is a
              flat rule applied to every field, stated once in the grid's own
              top comment. */}
          <div ref={supplierRef} className="relative sm:col-span-2 xl:col-span-2">
            {/* Supplier — label reads "Supplier — Exclusive ⓘ" only while
                `only_use_this_supplier` is truthy in read-only mode; edit
                always shows the "Exclusive" checkbox beside the label
                (folded in from its own standalone field, 2026-08-15 — see
                `client/src/pages/CLAUDE.md`'s Order List Pages box for the
                full history if it is ever needed). Unchanged by this move. */}
            {readOnly ? (
              <label
                htmlFor="supplier_name"
                data-phone-field-label=""
                className={form.only_use_this_supplier
                  ? 'flex items-center gap-1 text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5'
                  : labelClass}
              >
                {form.only_use_this_supplier ? (
                  <>
                    Supplier — Exclusive
                    <InfoTip title="Tells the buyer you want this supplier only — they should not shop elsewhere." />
                  </>
                ) : 'Supplier'}
              </label>
            ) : (
              /* 🚨 THE `InfoTip` IS A SIBLING OF THE CHECKBOX'S `<label>`,
                 NEVER A CHILD. Inside that label, a click on the ⓘ toggles
                 the checkbox — the bubble is `pointer-events-none` but the
                 icon's wrapper span is not, so anything inside the label's
                 hit area flips the value. That would ship as a mystery
                 mis-click, not as a visual bug. The field-name label and the
                 checkbox's own label are two SEPARATE `<label>`s side by
                 side in one flex row; the ⓘ is a third sibling, outside
                 both — never nested inside the checkbox's. */
              // 📱 Phone wording (owner 2026-10-03): "Supplier, must use this exact
              // supplier ☐ ⓘ" — the words sit INSIDE the checkbox's label so a tap
              // on them toggles it. Desktop keeps "Supplier ☐ Exclusive ⓘ".
              <div className="flex items-center gap-3 max-md:gap-1 mb-1.5">
                <label htmlFor="supplier_name" data-phone-field-label="" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Supplier<span className="md:hidden">,</span>
                </label>
                <label className="flex items-center gap-1.5 cursor-pointer">
                  <span data-phone-field-label="" className="md:hidden whitespace-nowrap">must use this exact supplier</span>
                  <input
                    type="checkbox"
                    id="only_use_this_supplier"
                    name="only_use_this_supplier"
                    checked={form.only_use_this_supplier}
                    onChange={handleChange}
                    className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <span data-phone-field-label="" className="max-md:hidden text-sm font-medium text-gray-700 dark:text-gray-300 whitespace-nowrap">Exclusive</span>
                </label>
                <InfoTip title="Tells the buyer you want this supplier only — they should not shop elsewhere." />
              </div>
            )}
            {readOnly ? (
              <ReadOnlyValue testId="supplier-name">{form.supplier_name}</ReadOnlyValue>
            ) : (
              <>
                <input
                  type="text"
                  id="supplier_name"
                  name="supplier_name"
                  value={form.supplier_name}
                  onChange={handleSupplierInput}
                  placeholder="Start typing to search suppliers..."
                  className={inputClass('supplier_name')}
                  autoComplete="off"
                  data-testid="supplier-name"
                  role="combobox"
                  aria-autocomplete="list"
                  aria-expanded={showSupplierSuggestions}
                  aria-controls="supplier-suggestions-list"
                />
                {showSupplierSuggestions && (
                  <SupplierOptionList
                    id="supplier-suggestions-list"
                    options={supplierSuggestionMatches}
                    onSelect={selectSupplier}
                    testId="supplier-suggestions-list"
                  />
                )}
                {/* See the two-case note beside `autocompleteHint`. 'empty' means
                    the department has no `suppliers` rows at all, so no amount of
                    typing will ever produce a suggestion — a different message
                    from "none of these match", because it calls for a different
                    action (go and add suppliers, rather than keep typing). */}
                {/* 🚨 'load-failed' is NOT styled as a muted hint. The other two
                    sentences describe the world; this one says we could not see
                    it, and it must not read as a quiet aside beneath a box that
                    looks like it is working. The amber pair is the same one the
                    department switcher uses for the same class of failure. */}
                {supplierHint && (
                  <p
                    // "No suppliers match" is hidden at EVERY width (owner 2026-10-03:
                    // typing a new supplier is normal, the line is clutter). The
                    // load-failed and no-suppliers-yet lines still show.
                    className={supplierHint === 'load-failed'
                      ? LOAD_FAILED_HINT_CLASS
                      : `${HINT_CLASS}${supplierHint === 'empty' ? '' : ' hidden'}`}
                    role="status"
                    data-testid="supplier-suggestions-hint"
                  >
                    {supplierHint === 'load-failed'
                      ? 'Couldn’t load the supplier list — type the name in full'
                      : supplierHint === 'empty'
                        ? 'No suppliers set up for this department yet'
                        : 'No suppliers match'}
                  </p>
                )}
              </>
            )}
          </div>

          {/* Needed By Date — the order-level needed-by date. `custom_needed_by_date`
              is the stored column name (legacy — see the column comment in
              `server/database.js`); read it as "the order needed-by date".
              🔀 VISIBLE LABEL ONLY changed 2026-08-16, "Needed By Date" →
              "Needed by" — the `id`/`name`/`htmlFor`/`data-testid`/form-state
              key (`custom_needed_by_date`) are ALL unchanged; only the string
              a sighted user reads moved, folded into the same width-saving
              pass as the rest of this row. */}
          <div className="xl:col-span-1" data-phone-half="">
            <label htmlFor="custom_needed_by_date" className={labelClass}>Needed by</label>
            {readOnly ? (
              <ReadOnlyValue testId="custom-needed-by-date">{formatReadableDate(form.custom_needed_by_date)}</ReadOnlyValue>
            ) : (
              <input
                type="date"
                id="custom_needed_by_date"
                name="custom_needed_by_date"
                value={form.custom_needed_by_date}
                onChange={handleChange}
                className={inputClass('custom_needed_by_date')}
                data-testid="custom-needed-by-date"
              />
            )}
          </div>

          {/* Lead Time — the order-level lead-time scale, `constants/leadTime.js`.
              🪤 `value` is `form.lead_time_days` unchanged — NOT
              `form.lead_time_days || ''`. `'0'` is "Same day"; `'0' || ''` is
              `'0'` (strings are fine), but the moment anyone "tidies" this to a
              number the same expression silently selects the placeholder. */}
          <div className="xl:col-span-1" data-phone-half="">
            <label htmlFor="lead_time_days" className={labelClass}>Lead Time</label>
            {readOnly ? (
              <ReadOnlyValue testId="lead-time-days">
                {formatLeadTimeDays(form.lead_time_days) || 'Not set'}
              </ReadOnlyValue>
            ) : (
              <select
                id="lead_time_days"
                name="lead_time_days"
                value={form.lead_time_days}
                onChange={handleChange}
                className={`${inputClass('lead_time_days')} bg-transparent`}
                data-testid="lead-time-days"
              >
                <option value="">-- Select --</option>
                {LEAD_TIME_OPTIONS.map(opt => (
                  <option key={opt.value} value={String(opt.value)}>{opt.label}</option>
                ))}
              </select>
            )}
          </div>

          {/* Department — admin-only override (hidden in view mode for non-admin since the buyer/user already knows their department) */}
          {isAdmin && (
            <div className="xl:col-span-2">
              <label htmlFor="department" className={labelClass}>Department</label>
              {readOnly ? (
                <ReadOnlyValue testId="department">{form.department}</ReadOnlyValue>
              ) : (
                <input
                  type="text"
                  id="department"
                  name="department"
                  value={form.department}
                  onChange={handleChange}
                  placeholder="e.g. SFX, Props, Construction"
                  className={inputClass('department')}
                  data-testid="department"
                />
              )}
            </div>
          )}

          {/* -- ORDER DETAILS ROW 2 (2026-09-13, SECOND PASS) --------------
              Owner, verbatim: *"put 'expected delivery date' on a new row
              under PO Number. Internal notes goes next to Expected delivery
              date"*. Both cells are CHILDREN OF THIS SAME GRID (not a
              sibling row's own grid) — `xl:row-start-2` places them into a
              second implicit row that reuses the container's existing N
              column tracks, so `orderDetailsGridColsClass` needs no fourth
              arithmetic for them (see that constant's own comment).

              🔑 `orderDetailsRow2Lead` is pinned UNDER PO — the same
              `xl:col-start-1` PO's own cell uses, one row down. Below `xl`
              (stacked) NEITHER cell has an explicit position; they simply
              follow Department in document order, which is what puts
              Expected Delivery then Internal notes at the bottom of the
              stack, each full width via `sm:col-span-2` (the two-column
              tablet grid would otherwise seat them side by side, which the
              owner's "under" / "next to" wording is about the desktop row
              only).

              🔀 **CORRECTED 2026-09-13, THIRD PASS — THE LEAD CELL IS THREE
              TRACKS WIDE, NOT ONE.** Owner, live in the browser at 1280:
              *"it looks cramped … Expected delivery label and text can be
              wider and Internal notes can be narrower if required"*, and
              *"I don't want anything from the above row (PO Number,
              Requested by, etc) changing size."* A HALF cell (PO Number's
              own ~90px width) wrapped "Expected delivery" and its helper
              note to 3-4 lines; three tracks (roughly PO Number + Requested
              By's combined width) fits the label on one line and the helper
              note on at most two. `xl:col-span-3` is a FIXED literal — it
              does not vary with N, because row 2's own split is independent
              of row 1's per-role field count (see `orderDetailsRow2RestClass`
              above). Row 1's own track literal and every ROW-1 cell's span
              are UNTOUCHED by this pass — the owner's hard constraint.

              🪤 A NODE THAT RENDERS NOTHING IS STILL TRUTHY — see the prop
              docs. Each cell is gated on the PROP, never on rendering the
              node and inspecting the result. */}
          {orderDetailsRow2Lead && (
            <div className="min-w-0 sm:col-span-2 xl:col-start-1 xl:row-start-2 xl:col-span-3" data-testid="order-details-row2-lead">
              {orderDetailsRow2Lead}
            </div>
          )}
          {orderDetailsRow2Rest && (
            <div className={`min-w-0 sm:col-span-2 ${orderDetailsRow2RestClass}`} data-testid="order-details-row2-rest">
              {orderDetailsRow2Rest}
            </div>
          )}
        </div>
        {/* -- HEADER DETAILS -- MY SPACE ONLY (2026-09-11) -----------------
            Owner: *"in new order, Order Details section I want a full width
            text input box starting at 4 lines deep called "Header details".
            [Height doubled to 8 rows later that day — see HEADER_DETAILS_ROWS.]
            This will be where a user in MySpace can type in delivery address,
            email, phone number, order rules etc and it will appear on the order
            (Print and PDF) under the ORDER REQUEST title."*

            🚨 **A SIBLING OF THE FIELDS GRID, NOT A CHILD OF IT, AND THAT
            IS NOT A STYLISTIC CHOICE.** The grid above is
            `xl:grid-cols-[repeat(N,minmax(0,1fr))]` with N pinned at 13 / 12
            / 11 / 10 (see `orderDetailsGridColsClass`'s own comment — 🔻
            CORRECTED here 2026-09-13: this said "13 / 11 / 10" for a while,
            three values for what is actually a FOUR-way condition once
            `showPoNumberField` diverged from `isAdmin`), and the four passes
            documented above it warn that *"A
            wrong sum is still the one way that bug reappears"* -- too low
            re-wraps a variant, too high reopens dead trailing whitespace. A
            full-width member would have to be a fourth span class and a fourth
            arithmetic, in a layout the owner has already reviewed four times.
            Outside the grid it is simply full width at every breakpoint and the
            three track counts are unchanged -- asserted against the shipped
            class literals by §5 of `OrderForm.headerDetails.test.js`
            rather than believed.

            ⚠ THE PRECISE CLAIM IS **CLASS-IDENTICAL**, NOT BYTE-IDENTICAL,
            AND THE DIFFERENCE IS THIS CHANGE'S OWN DOING. The grid element
            gained `data-testid="order-details-grid"` so §5 can address it,
            and that attribute renders on the DEPARTMENT form too, where this
            box does not. So the department form's Order Details markup is NOT
            byte-for-byte what it was; its CLASS STRING is, in all three
            role variants, which is the property the layout depends on. Do not
            let a stronger-sounding word stand in for the one that was
            measured.

            🎨 The control copies the `Notes / Comments` textarea idiom
            at the bottom of this form verbatim -- `inputClass(name)` +
            `h-auto`, and a `whitespace-pre-wrap` paragraph in `readOnly` mode
            so the line breaks that are the point of the field survive on a
            read-only surface. No new styling primitive, no new colour token.

            ⓘ The tooltip is the shared `components/InfoTip.js`, INSIDE the
            `<label>` in the documented `flex items-center gap-1` idiom. Never a
            native `title=` on the icon -- a second browser tooltip over the
            bubble is the exact defect that component's rules forbid -- and its
            nesting hazard applies to a label wrapping a checkbox or radio,
            which this is not. */}
        {showHeaderDetails && (
          <div className="mt-3">
            <label htmlFor="header_details" className={`${labelClass} flex items-center gap-1`}>
              Header details
              <InfoTip title={HEADER_DETAILS_TOOLTIP} />
            </label>
            {readOnly ? (
              <p
                className="text-sm text-gray-800 dark:text-gray-200 whitespace-pre-wrap min-h-[2.75rem]"
                data-testid="header-details"
              >
                {form.header_details || <span className="text-gray-400 dark:text-gray-500">—</span>}
              </p>
            ) : (
              <textarea
                id="header_details"
                name="header_details"
                value={form.header_details}
                onChange={handleChange}
                rows={HEADER_DETAILS_ROWS}
                maxLength={HEADER_DETAILS_MAX_LENGTH}
                /* 🔑 THE EXAMPLE IS THE `placeholder` ATTRIBUTE AND
                   NOTHING ELSE -- never a value, never a default, never
                   stored. Full reasoning in `constants/headerDetails.js`. */
                placeholder={HEADER_DETAILS_PLACEHOLDER}
                className={`${inputClass('header_details')} h-auto`}
                data-testid="header-details"
              />
            )}
          </div>
        )}
        </div>
      </div>

      {/* Line Items — header bar itself toggles collapse-all (was a dedicated
          "Expand All / Collapse All" button until 2026-05-28). Chevron reflects
          the all-collapsed vs anything-expanded state.

          🔀 TWO BRANCHES SINCE 2026-08-15, AND THE `received == null` ONE IS
          BYTE-IDENTICAL TO WHAT SHIPPED (pinned). With `received` supplied the
          bar becomes "Line Items — received (x/N)" + a Select-all tick-all —
          the owner's rename for PLACED orders. Three structural facts:
          (1) The Select-all is a SIBLING of the collapse-all button, never a
              child — a checkbox inside a <button> is invalid HTML and its
              clicks would toggle the collapse; it stops propagation on click
              AND keydown itself (see OrderItemReceivedControls).
          (2) `data-section-title` moves to the WRAPPER in that branch —
              index.css:202-208 is a dark-mode-only `!important` fill, and left
              on the button it would paint a bar that stops dead before the
              Select-all, for dark-mode users alone (the recorded headerActions
              lesson).
          (3) The counts come from `received` (the DETAIL payload), never from
              `items` (form state) — an unsaved new line must not move the
              count. */}
      <div className="bg-white rounded-lg shadow-md p-2.5 space-y-4" data-form-card="">
        {received ? (
          <div
            className={`flex items-center justify-between gap-2 md:gap-3 w-full md:w-[calc(100%+1.25rem)] -mx-2.5 -mt-2.5 px-2 py-1 md:py-2.5 border-b border-gray-200 dark:border-gray-700 rounded-t-lg ${DESKTOP_SECTION_HEAD}${itemsHeaderClosed ? ' md:border-b-transparent' : ''}`}
            data-section-title
            // 🖥️ The WHOLE bar folds the list on desktop, like Order Details (owner
            // 2026-10-10), and hovers as one (it stopped short of "Mark all as
            // delivered"); a click on the button itself or on the tick is theirs.
            onClick={(e) => {
              if (!(window.matchMedia && window.matchMedia('(min-width: 768px)').matches)) return;
              if (e.target.closest && e.target.closest('button, label, input')) return;
              if (itemsHeaderClick) itemsHeaderClick();
            }}
          >
            {/* 🖥️ Chevron FIRST, like every other section (owner 2026-10-10:
                "each section … in a similar style to the next"). */}
            <button
              type="button"
              onClick={itemsHeaderClick}
              className="flex items-center justify-start gap-2 flex-1 min-w-0 max-md:min-h-[44px] text-left rounded hover:bg-gray-50 dark:hover:bg-gray-700/30 md:hover:bg-transparent md:dark:hover:bg-transparent transition-colors"
              aria-expanded={!itemsHeaderClosed}
              aria-label={itemsListOpen ? 'Hide line items' : 'Show line items'}
              data-testid="toggle-all-items-btn"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className={`h-4 w-4 shrink-0 text-gray-400 transition-transform ${itemsHeaderClosed ? '-rotate-90' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
              </svg>
              {/* 📱 Below `md` the short one-line form (owner, 2026-09-30, Android
                  346px: the full title + "Mark all as delivered" wrapped to three
                  lines). The full title is the md+ one and the only one a
                  screen reader meets at md+; below md `hidden` removes it. */}
              <h3 className="md:hidden text-base font-semibold text-gray-700 dark:text-gray-200 whitespace-nowrap truncate" data-testid="order-form-items-title-mobile">
                {lineItemsReceivedShortTitle(received.orderedCount || 0, received.receivedCount, received.total)}
              </h3>
              <h3 className="hidden md:block text-lg font-semibold text-gray-700 dark:text-gray-200">
                {lineItemsReceivedTitle(received.orderedCount || 0, received.receivedCount, received.total)}
              </h3>
            </button>
            {/* 🔑 ONE countable line → no header button (owner 2026-10-19): the
                row's own step is the same action, so two controls stood for one
                fact. It returns at 2+. Keyed on `received.total` — the SAME count
                the title reads. 🔀 2026-10-28: the tri-state tick-all became ONE
                button naming the NEXT step for every line at that stage
                (`headerNextStep`) — "Mark all ordered (n)", then "Mark all
                delivered (n)"; nothing once every line is delivered (↺ per line
                steps back). */}
            {received.total > 1 && (
            <NextStepAllButton
              step={headerNextStep({ orderedCount: received.orderedCount || 0, receivedCount: received.receivedCount, total: received.total, canOrder: !!received.canOrder })}
              disabled={!!received.busy || !!received.locked}
              onOrderAll={(next) => received.onOrderAll(next)}
              onReceiveAll={(next) => received.onReceiveAll(next)}
              testId="order-form-next-step-all"
            />
            )}
            {/* ── THE ALIGNMENT SPACER (2026-09-13) ─────────────────────
                Owner: *"the checkbox should be aligned vertically with the
                individual 'Mark as delivered' check boxes for each item."*
                Each item row ends `… Mark as delivered [☐] · [Actions ▾]`,
                so the header's tick is one Actions-button wider than the
                row ticks below it. This reserves exactly that width.

                🚨 **STRUCTURAL, NOT A MAGIC PIXEL.** It is the compact
                `Actions ▾` trigger's OWN class tokens and its OWN content
                (the label plus the `h-3 w-3` caret), rendered `invisible`
                and `aria-hidden`, so its width is the trigger's width by
                CONSTRUCTION and tracks any future restyle of `BTN_XS`. The
                file's own history is why: the retired `OrderExpandedDetails`
                alignment used a measured `pr-[13px]`, which drifted the
                moment the geometry beneath it moved.
                🔑 It renders on EXACTLY the condition the rows' menu does
                (`showsItemActionsMenu`), from the same expression — reserve
                the width for a viewer whose rows have no menu and the header
                tick is pushed out of line in the other direction.
                📱 Below `md` it is `hidden`: the item rows there are the
                44px tap-target stack and the header keeps today's geometry
                exactly, which is what the owner has already signed off.
                ⚠️ jsdom lays nothing out, so the PIXEL alignment is a
                browser check on staging; what is pinned is the structure. */}
            {showsItemActionsMenu && (
              <span
                aria-hidden="true"
                data-testid="order-form-received-all-actions-spacer"
                className={`invisible pointer-events-none hidden md:inline-flex items-center justify-center min-h-[44px] md:min-h-0 ${BTN_XS.secondary}`}
              >
                Actions
                <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                </svg>
              </span>
            )}
          </div>
        ) : (
          <button
            type="button"
            onClick={itemsHeaderClick}
            className={`flex items-center justify-start gap-2 w-full md:w-[calc(100%+1.25rem)] text-left -mx-2.5 -mt-2.5 px-2 py-1 md:py-2.5 border-b border-gray-200 dark:border-gray-700 rounded-t-lg hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors ${DESKTOP_SECTION_HEAD}${itemsHeaderClosed ? ' md:border-b-transparent' : ''}`}
            aria-expanded={!itemsHeaderClosed}
            aria-label={itemsListOpen ? 'Hide line items' : 'Show line items'}
            data-testid="toggle-all-items-btn"
            data-section-title
          >
            <svg xmlns="http://www.w3.org/2000/svg" className={`h-4 w-4 shrink-0 text-gray-400 transition-transform ${itemsHeaderClosed ? '-rotate-90' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
            </svg>
            <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-200">Line Items<span className="md:hidden"> · {items.length}</span></h3>
          </button>
        )}

        {errors.items && <p role="alert" className="text-red-500 text-sm">{errors.items}</p>}

        <div ref={itemsListRef} className={`space-y-2${!itemsListShown ? ' hidden' : ''}`} data-testid="line-items-list">
          {items.map((item, index) => (
            <div key={index} className={`rounded-lg relative bg-gray-50 ${collapsedItems.has(index) ? 'border border-gray-100 dark:border-gray-700 p-1.5' : 'border border-gray-200 dark:border-gray-700 p-4'}`} data-testid={`line-item-${index}`} data-line-item="" data-expanded={collapsedItems.has(index) ? undefined : ''}>
              {/* Header bar — the whole row is clickable in both collapsed AND
                  expanded states (toggles collapse). Chevron + Remove stop propagation
                  so their own clicks don't double-fire. Keyboard a11y via Enter/Space. */}
              {/* Negative margins bleed the hover hit-area out to the card's padding
                  edges (top + sides), so the entire row looks clickable instead of just
                  a thin slither over the text. Vertical padding (`py-3`) matches the
                  card's collapsed/expanded padding to keep visual height constant. */}
              {/* 🚨 MOBILE P1 FIX (2026-08-17) — `flex-wrap gap-y-1` added.
                  Root cause (mobile audit P1-1/P1-4): this row is
                  `justify-between` with two children — the left cluster
                  (chevron/"Item N"/description) and the right cluster
                  (notify flag / Qty·price columns / received tick /
                  `OrderItemActionsMenu`, line ~1930 below) — and neither
                  ever wrapped, so at 375px the right cluster's own content
                  (up to flag + prices + a 44px tick + an 87px Actions
                  button) simply ran past the card's right edge. The card
                  wrapper (the since-retired card row) was `overflow-hidden` on both
                  axes, so the overflow was not scrollable — it was
                  invisible and untappable, ~49px of the Actions button
                  gone (measured: button `left: 336.2, right: 423.6` in a
                  375px viewport).
                  `flex-wrap` here lets the right cluster drop to its own
                  line below the left cluster when they cannot share one;
                  `gap-y-1` gives the two lines breathing room once that
                  happens. Paired with `flex-wrap w-full md:w-auto
                  md:flex-nowrap` on the right cluster itself (below) so
                  IT also wraps internally on mobile (a flagged item's
                  pill can itself be wide enough to force a further wrap)
                  while staying byte-identical to today's single-line,
                  non-wrapping, auto-width behaviour at `md` and up, where
                  the `md:min-w-*` column alignment this row's comments
                  describe still applies. No width was shrunk and no
                  `min-h-[44px]` tap target changed — only where things
                  sit when they do not fit. Applies to BOTH the read-only
                  and edit/create branches (they share this one JSX path),
                  so it covers My Orders/Dept Orders expanded rows AND the
                  create/edit path (P1-4) in one change.
                  jsdom cannot see wrapping or the clip itself — those are
                  browser checks; `OrderForm.itemHeaderWrap.test.js` pins
                  the structural classes this comment describes. */}
              {/* 📱 THE COLLAPSED ROW BELOW `md` IS THREE LINES (owner, 2026-10-01):
                  1 description (truncated, full text on `title`) · 2 part number,
                  small grey (omitted when blank; a flag pill sits right) ·
                  3 "Qty 2 × £4.50 = £9.00" left, "Received?" + the tick right.
                  No item Actions menu on the collapsed row — it shows once the
                  item is OPENED. Done with `max-md:` variants ONLY, so desktop
                  keeps every class it had: the row becomes a 3-column grid and
                  the two clusters (and the name span) become `display: contents`
                  so their children are placed directly. ONE element per control
                  — the tick and the chevron are never rendered twice.
                  ⚠️ jsdom applies no media queries: `OrderForm.mobileItemRow
                  .test.js` pins the classes; the geometry is a browser check. */}
              <div
                className={`flex flex-wrap items-center justify-between gap-y-1 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700/50 transition-colors ${
                  collapsedItems.has(index)
                    ? `-m-1.5 px-1.5 py-1.5 rounded-lg ${MOBILE_COLLAPSED_ITEM_ROW}`
                    : '-mx-4 -mt-4 px-4 py-3 mb-3 rounded-t-lg'
                }`}
                data-item-header=""
                onClick={(e) => toggleItemCollapse(index, e.currentTarget)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleItemCollapse(index, e.currentTarget); } }}
                role="button"
                tabIndex={0}
                aria-label={collapsedItems.has(index) ? `Expand item ${index + 1}` : `Collapse item ${index + 1}`}
                aria-expanded={!collapsedItems.has(index)}
                data-testid={`item-header-${index}`}
              >
                <div className={`flex items-center gap-2${collapsedItems.has(index) ? ' max-md:contents' : ''}`}>
                  {items.length >= 1 && (
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); toggleItemCollapse(index); }}
                      className={`text-gray-400 hover:text-gray-600 transition-colors p-0.5${collapsedItems.has(index) ? ' max-md:hidden' : ''}`}
                      data-testid={`toggle-item-${index}`}
                      title={collapsedItems.has(index) ? 'Expand item' : 'Collapse item'}
                      aria-label={collapsedItems.has(index) ? `Expand item ${index + 1}` : `Collapse item ${index + 1}`}
                      aria-expanded={!collapsedItems.has(index)}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className={`h-4 w-4 transition-transform ${collapsedItems.has(index) ? '-rotate-90' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                  )}
                  <span className={`text-sm font-semibold text-gray-500${collapsedItems.has(index) ? ' max-md:hidden' : ''}`} data-item-number="">Item {index + 1}</span>
                  {/* 📱 The OPEN item's band names it too (owner 2026-10-03, option A): "Item 1 · Wind machine". */}
                  {!collapsedItems.has(index) && (
                    <span className="md:hidden min-w-0 flex-1 truncate text-sm" data-item-band-desc="">{item.description ? '· ' + item.description : ''}</span>
                  )}
                  {collapsedItems.has(index) && (
                    <span
                      className="text-sm text-gray-700 truncate max-w-[200px] sm:max-w-[300px] max-md:contents"
                      title={[item.part_number, item.description].filter(Boolean).join(' — ') || undefined}
                      data-testid={`item-name-${index}`}
                    >
                      {item.part_number && <span className={`text-gray-400 mr-2 ${MOBILE_ITEM_PART_NUMBER}`} data-testid={`item-summary-part-number-${index}`}>{item.part_number}</span>}
                      {/* The description is its own element so the phone grid
                          can place it on line 1 (desktop: an inline span inside
                          the truncating name span — renders as before). */}
                      <span className={MOBILE_ITEM_DESCRIPTION} data-testid={`item-summary-description-${index}`}>
                        {/* 📱 Phone only (owner 2026-10-01): a quiet "Item N" leads line 1 so each item's start is clear while scrolling. */}
                        {/* 📱 The chevron LEADS the line, like every section header (owner 2026-10-03); the row itself toggles. */}
                        <svg className="md:hidden mr-1.5 h-3.5 w-3.5 shrink-0 self-center text-gray-400 -rotate-90" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true" data-testid={`item-summary-chevron-${index}`}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                        </svg>
                        <span className="md:hidden mr-1.5 shrink-0 text-xs font-semibold text-gray-500 dark:text-gray-400" data-testid={`item-summary-number-${index}`}>Item {index + 1}:</span>
                        <span className="max-md:truncate max-md:min-w-0" data-item-summary-text="">{item.description || <span className="italic text-gray-400">No description</span>}</span>
                        {/* 📱 (Hire) straight after the name on phone, quiet grey so it does not read as a link (owner 2026-10-03); the name truncates, the tag never does. */}
                        {item.is_hire && <span className="md:hidden ml-1.5 shrink-0 text-gray-500 dark:text-gray-400 text-xs font-normal whitespace-nowrap" data-testid={`item-hire-tag-phone-${index}`}>(Hire)</span>}
                      </span>
                    </span>
                  )}
                  {/* (Hire) beside the name — owner 2026-09-29: "keep the hire near the item name but in brackets and blue and remove the other"; GREY since 2026-10-03 (owner: "the blue makes it look clickable"). Outside the truncating span so it is never clipped. */}
                  {collapsedItems.has(index) && item.is_hire && (
                    <span className="text-gray-500 dark:text-gray-400 text-xs font-normal whitespace-nowrap max-md:hidden" data-testid={`item-hire-tag-${index}`}>(Hire)</span>
                  )}
                </div>
                {/* 🚨 MOBILE P1 FIX (2026-08-17) — the PAIRED half of the outer
                    row's `flex-wrap` above. `w-full md:w-auto` drops this right
                    cluster onto its own full-width line below `md` (so the
                    Actions button stays inside the 375px viewport instead of
                    clipping off the `overflow-hidden` card), and `flex-wrap
                    md:flex-nowrap` lets its OWN children wrap if a flagged item's
                    pill makes even the full-width line too narrow. md+ is
                    byte-identical: `md:w-auto` + `md:flex-nowrap` restore today's
                    single-line, auto-width, `md:min-w-*`-aligned layout exactly. */}
                {/* 📱 2026-09-30 (owner, real Android): an EXPANDED item has no
                    price columns here — only the tick (+ menu) — so `w-full`
                    put the tick alone on an empty second row. Expanded: the
                    cluster stays on the "Item N" row, pushed right with
                    `ml-auto`. Collapsed keeps the full-width price row. md+
                    unchanged (`md:w-auto`, `md:ml-0`). */}
                <div className={`flex flex-wrap md:flex-nowrap items-center gap-3 ${collapsedItems.has(index) ? 'w-full max-md:contents' : 'ml-auto md:ml-0'} md:w-auto`}>
                  {/* NOTIFY USER — the line-flag mark. Rendered in the HEADER so
                      it is visible collapsed AND expanded; `OrderLineItemFlag`
                      returns null for an unflagged line, so no caller repeats
                      the predicate. Reads `notifyRows`, never `item`: see
                      `buildNotifyRows`.
                      🔀 MOVED LEFT OF THE PRICE COLUMNS 2026-08-15 (it sat
                      between the prices and the tick): the pill is VARIABLE
                      width, and anywhere to the right of the labelled columns
                      it would push a flagged row's Qty / Unit price / Total
                      price out of the vertical alignment the owner asked for
                      ("all verticaly aligned please"). Leftmost, it bleeds
                      into the flexible gap between the description and the
                      cluster instead, and the columns hold on flagged rows.
                      Expanded state is unchanged by the move — the prices
                      don't render there, so the flag was leftmost already. */}
                  <OrderLineItemFlag
                    item={notifyRows[item.id]}
                    testId={`item-flag-${index}`}
                    className={collapsedItems.has(index) ? MOBILE_ITEM_FLAG : ''}
                  />
                  {/* LABELLED, COLUMN-ALIGNED since 2026-08-15. Owner, from his
                      staging pass, verbatim: "the collapsed line item has
                      NOTHING to say what the tick boxes are for? Update so the
                      we have Qty:, unit pice: total price:  Mark as delivered
                      [] the action menu all verticaly aligned please".
                      - His labels: "Qty:" / "Unit price:" / "Total price:"
                        here, "Mark as delivered" on the tick below. Labels are
                        muted text-gray-500 beside the existing gray-600/800
                        values — no `dark:` pairs, index.css's override table
                        remaps gray text in dark mode (recorded cascade rule).
                      - md+: each column is `md:min-w-*` + `md:text-right`, so
                        Qty / Unit price / Total price / tick / menu form
                        aligned columns down the item list. min-WIDTH, not
                        width, deliberately: an outsized value widens its own
                        row's column (graceful, one row degrades) instead of
                        overlapping its right-hand neighbour. Pixel alignment
                        is a BROWSER check — jsdom lays nothing out; the class
                        structure is pinned by receivedTicks.test.js §5.
                      - Below md the price labels are hidden (`hidden
                        md:inline`; "Qty:" stays — mobile has always shown it),
                        keeping today's ~56px header and 44px tap floor at 375:
                        full labels would wrap the row. Flagged to the owner as
                        a judgement call, not decided.
                      - A HIRE line keeps its blue chip IN the unit-price
                        column slot with no "Unit price:" label — it has no
                        unit price and a label would assert one.
                      - Applies to BOTH branches (create form too — `received`
                        gates only the tick), so create and edit read the same.
                        CONTRACT CHANGE: the no-`received` branch's collapsed
                        header is no longer the shipped literal
                        `<div className="flex items-center gap-3 text-sm
                        text-gray-600"><span>Qty: {qty}</span>…` — the §1
                        byte-identity pin narrowed to the SECTION BAR + tick
                        absence, recorded in the test file. */}
                  {collapsedItems.has(index) && (
                    <div className={`flex items-center gap-3 md:gap-1.5 max-md:justify-between text-sm text-gray-600 ${MOBILE_ITEM_PRICE_LINE} ${MOBILE_ITEM_PRICE_LINE_FULL}`} data-testid={`item-price-line-${index}`}>
                      {/* 📐 FIXED-WIDTH columns (prototype 2026-09-29, owner: "make sure QTY,
                          hire duration and total price are vertically aligned … a larger value
                          pushes the titles like 'Total price:' to not line up"). Labels sit at
                          each column's LEFT edge, amounts at its RIGHT, so labels align down the
                          list whatever the amount's length. */}
                      <span className="whitespace-nowrap md:w-[3.75rem] md:shrink-0 max-md:shrink-0">
                        {/* 📱 "Qty 2" on a phone, "Qty: 2" from md (desktop unchanged). */}
                        <span className="text-gray-500">Qty<span className="max-md:hidden">:</span> </span>
                        {item.quantity}
                      </span>
                      {item.is_hire ? (
                        <>
                        <span className="whitespace-nowrap text-gray-500 md:w-[5.5rem] md:shrink-0" data-testid={`item-hire-length-${index}`}>
                          {hireBilledLength(item) ? <><span aria-hidden="true" className="text-gray-400 mr-1 max-md:hidden">×</span>{hireBilledLength(item)}</> : ''}
                        </span>
                        {/* 📱 Phone: the rate is its own item after the length —
                            "Qty 2 · 12 days · £120.00/day · £2,880.00", spread evenly
                            (owner 2026-10-03). A TOTAL-priced hire has no per-unit rate. */}
                        {(() => {
                          const type = item.hire_rate_type || (item.daily_rate ? 'daily' : item.weekly_rate ? 'weekly' : 'total');
                          const amount = type === 'daily' ? item.daily_rate : type === 'weekly' ? item.weekly_rate : null;
                          const rate = amount != null && amount !== '' && hireBilledLength(item)
                            ? `${formatMoney(amount)}/${type === 'daily' ? 'day' : 'week'}` : null;
                          return rate ? <span className="md:hidden whitespace-nowrap text-gray-500 tabular-nums" data-testid={`item-hire-rate-${index}`}>{rate}</span> : null;
                        })()}
                        </>
                      ) : (
                        <span className="whitespace-nowrap md:w-[5.5rem] md:shrink-0" title="Unit price">
                          <span aria-hidden="true" className="text-gray-400 mr-1 max-md:hidden">×</span>
                          {/* Screen-reader only from md; a visible "Unit price:" label on a phone (owner 2026-10-02). */}
                          <span className="sr-only max-md:not-sr-only text-gray-500">Unit price<span className="md:hidden">:</span> </span>
                          {formatMoney(item.unit_price)}
                        </span>
                      )}
                      <span className="whitespace-nowrap font-medium text-gray-800 md:w-[9.75rem] md:shrink-0 md:flex md:justify-between max-md:font-semibold max-md:text-gray-900 tabular-nums" data-testid={`item-mobile-total-${index}`}>
                        <span className="hidden md:inline font-normal text-gray-500">Total price: </span>
                        <span>{formatMoney(getLineTotal(item))}</span>
                      </span>
                    </div>
                  )}

                  {/* RECEIVED TICK (2026-08-15) — "between the price and the
                      actions menu", the owner's placement. In the HEADER, like
                      the flag and the menu, so it works collapsed AND expanded
                      — never behind `!collapsedItems.has(index)`, which both
                      EDIT callers' `initialItemsCollapsed` would make the
                      default-invisible state on every surface (the recorded
                      Notify lesson one block down). Directly LEFT of the menu
                      so the two fixed-width controls align in a column down
                      the rows; the variable-width amber flag sits LEFT of the
                      price columns (see its own comment) and the two coexist
                      on a flagged line.
                      🏷️ "Mark as delivered" (2026-08-15) — the owner's label,
                      after his staging pass found the tick anonymous. It rides
                      INSIDE the checkbox's own <label> via `labelText`, so it
                      exists exactly when the tick exists (no dangling label),
                      clicking it toggles the tick, and the label element's own
                      stopPropagation covers it. `hidden md:inline` — below md
                      the 44px hit-area box carries the tick alone (today's
                      geometry, ~56px header) and title/aria still name it.
                      text-xs text-gray-500 matches the section bar's
                      "Select all". ⚠️ The native `title` still says
                      "Mark as received" — it is shared with the read-only
                      tables, whose column header is "Received"; the
                      delivered/received wording split is recorded for the
                      owner, not unified here.
                      🔴 `item.id != null` AND an entry in `received.items` —
                      a NEW unsaved line has neither and gets no tick; the
                      state is the DETAIL's, never form state. The label stops
                      its own propagation (click + keydown) so ticking cannot
                      collapse the row. */}
                  {/* 📐 STATUS ZONE (prototype 2026-09-29, owner: "its more about
                      good spacing"; "I want it all on a single line"). The
                      line's ticks side by side in ONE zone; when any line on
                      the order carries a second tick (the hires "Off hired"),
                      every line reserves the same zone width so a hire line's
                      extra tick no longer pushes its price columns out of line
                      with the non-hire rows. `md:ml-4` / `md:gap-5` space the
                      zones. Orders with no second tick keep their auto width. */}
                  {(received || headerTickFor(item, index)) && (
                  <div
                    data-testid={`item-tick-zone-${index}`}
                    className={`flex items-center gap-3 md:gap-5 md:shrink-0 ml-auto md:ml-16 ${collapsedItems.has(index) ? `${headerTickFor(item, index) || isLineFlagged(notifyRows[item.id]) ? MOBILE_ITEM_TICK_ZONE_OWN_LINE : MOBILE_ITEM_TICK_ZONE} ` : ''}${items.some((it, i) => headerTickFor(it, i)) ? 'md:w-[23rem]' : ''}`}
                  >
                  {/* 🔀 PER-LINE ORDERED + DELIVERED (2026-10-28): two on/off
                      controls in fixed columns, Delivered greyed until Ordered,
                      Ordered locked while Delivered. `LineOrderedDeliveredControls`
                      draws; the PAGE decided `canOrder` and that this viewer may
                      receive. 📐 With a hire line's Off hired tick the zone is
                      23rem (two 7.5rem columns + the tick). */}
                  {received && item.id != null && received.items[item.id] != null && (
                    <LineOrderedDeliveredControls
                      line={received.items[item.id]}
                      canOrder={!!received.canOrder}
                      busy={!!received.busy}
                      locked={!!received.locked}
                      lockedReason={received.lockedReason}
                      orderLockedReason={received.orderLockedReason}
                      onToggleOrdered={(current) => received.onToggleOrdered(item.id, current)}
                      onToggleReceived={(current) => received.onToggleItem(item.id, current)}
                      testIdSuffix={index}
                      itemLabel={`Item ${index + 1}`}
                    />
                  )}

                  {/* 🔀 THE CALLER'S PER-LINE TICK (2026-09-29) — the hires
                      pages' "Off hired", beside "Delivered" (owner's staging
                      feedback). Same checkbox, geometry and propagation stop
                      as the received tick; everything about it (gate, state,
                      handler, tooltip) is decided by the caller. */}
                  {headerTickFor(item, index) && (() => {
                    const tick = headerTickFor(item, index);
                    return (
                      <ItemReceivedCheckbox
                        checked={!!tick.checked}
                        disabled={!!tick.disabled}
                        title={tick.title}
                        onChange={tick.onChange}
                        testId={tick.testId}
                        ariaLabel={tick.ariaLabel}
                        /* 📱 `tick.mobileLabel` (optional, 2026-10-01): the
                           phone label beside the tick, "Received?"'s shape —
                           md+ keeps `tick.label` exactly. Absent: md+ only,
                           as before. */
                        labelText={tick.mobileLabel ? (
                          <>
                            <span className="md:hidden">{tick.mobileLabel}</span>
                            <span className="hidden md:inline">{tick.label}</span>
                          </>
                        ) : tick.label}
                        labelClassName={`${tick.mobileLabel ? '' : 'hidden md:inline '}text-xs text-gray-500 whitespace-nowrap`}
                        className="flex items-center justify-center gap-1.5 min-w-[44px] min-h-[44px] md:min-w-0 md:min-h-0 cursor-pointer"
                      />
                    );
                  })()}
                  </div>
                  )}

                  {/* ══════════════════════════════════════════════════════════
                      THE LINE'S `Actions ▾` — IT REPLACED THE `Remove` BUTTON.
                      ══════════════════════════════════════════════════════════
                      Product owner, 2026-08-11: *"Change the remove button on
                      expanded item to actions menu"*.

                      🚨 **THE GATE IS `!readOnly` PLUS AUTHORITY — IT DELIBERATELY
                      DOES **NOT** INHERIT THE TWO TERMS THE `Remove` BUTTON
                      CARRIED, AND BOTH OMISSIONS ARE MEASURED RATHER THAN
                      PREFERRED.** `Remove` was
                      `items.length > 1 && !collapsedItems.has(index) && !readOnly`:

                        (1) `items.length > 1` encodes *"you cannot remove the
                            LAST item"* — a fact about REMOVE. Inherited, Notify
                            would be unreachable on a single-line order, which is
                            the commonest order in the app and exactly the one a
                            buyer most often needs to flag.

                        (2) `!collapsedItems.has(index)` is the same error one
                            level along, and it is the worse of the two.
                            **MEASURED: both `OrderForm` EDIT callers pass
                            `initialItemsCollapsed`** (`OrderInlineEditor.js:418`,
                            `OrderPage.js:1117`), and `collapsedItems` then
                            initialises to EVERY index — so every line opens
                            collapsed and the menu would be invisible in the
                            default state of every surface that can reach it. On
                            **To Approve** that is fatal rather than awkward:
                            every row there is `can_edit: true`
                            (`GET /api/approvals/pending` selects the orders where
                            the viewer IS the pending approver), so the read-only
                            panel never renders and this menu is the page's ONLY
                            Notify surface.

                      🔀 **`Remove` KEPT BOTH TERMS ON ITS OWN MENU ITEM UNTIL
                      2026-09-28; the owner then ruled the collapsed term OFF**
                      (*"add the remove option to the action menu when the item
                      is collapsed"*) — (2) above is exactly why it was hidden in
                      the default state. `items.length > 1` survives as the
                      item's `disabled` (🔀 2026-10-01: only when the page passes no cancel flow — see `onRequestCancelOrder`). **Do not
                      "restore parity" by lifting either term onto the menu.**

                      🪤 `stopPropagation` on BOTH click and keydown: the header row
                      is itself a `role="button"` that toggles collapse on click AND
                      on Enter/Space, so without the second one a keyboard user
                      opening the menu would also collapse the item under it. The
                      chevron and the old Remove button both stop clicks; neither
                      needed the keydown, because neither was focusable-and-inside
                      a control that opens a panel. */}
                  {/* 🔴 **THE MENU EXISTS ONLY WHERE NOTIFYING IS POSSIBLE, AND
                      EVERYWHERE ELSE THE ORIGINAL `Remove` BUTTON IS RENDERED
                      BYTE-IDENTICALLY. That is a deliberate narrowing of "the
                      menu replaces Remove", and the reason is CREATE MODE.**
                      A new line has no `order_items.id` — there is nothing to
                      flag and nobody to notify — so on `/orders/new` a menu
                      would hold exactly one item, `Remove`, and would have
                      buried the create form's only way to drop a line behind a
                      disclosure for no gain. Keeping the button there also keeps
                      `e2e/orders.spec.js` and `e2e/mobile.spec.js` (all six
                      `remove-item-*` assertions, every one of them on
                      `/orders/new`) true without an edit — **not a happy
                      accident: a change that forces an e2e rewrite on the
                      untouched create path is a change that went too wide.** */}
                  {/* 🔀 2026-08-16 — the outer gate ADMITS a readOnly render when
                      `onReadOnlyItemNotifyUpdated` is supplied: `(!readOnly ||
                      onReadOnlyItemNotifyUpdated)` reduces to exactly `!readOnly`
                      when the prop is absent (every existing caller, incl.
                      `OrderPage.js`'s own view mode), so this is byte-identical
                      there. `extraItems` is `[]` in the readOnly case — there is
                      no add/remove-item editing in view mode, so "Remove" has
                      nothing to attach to. */}
                  {showsItemActionsMenu && item.id != null ? (
                    <div
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={(e) => e.stopPropagation()}
                      // 📱 Not on a COLLAPSED row below md — the item's menu
                      // appears once it is opened (owner, 2026-10-01).
                      className={collapsedItems.has(index) ? 'max-md:hidden' : undefined}
                    >
                      <OrderItemActionsMenu
                        order={initialOrder}
                        item={{ ...item, ...(notifyRows[item.id] || {}) }}
                        testIdPrefix="order-form-item"
                        onItemUpdated={(updated) => {
                          if (readOnly) {
                            onReadOnlyItemNotifyUpdated && onReadOnlyItemNotifyUpdated(updated);
                            return;
                          }
                          setNotifyRows((prev) => ({
                            ...prev,
                            [updated.id]: {
                              notify_reasons: updated.notify_reasons,
                              notify_note: updated.notify_note,
                            },
                          }));
                        }}
                        extraItems={[
                          ...(itemExtraActions ? (itemExtraActions(item, index) || []) : []),
                          // 🔒 A LOCKED order keeps Remove where it always is; a tap explains the lock (owner 2026-10-03).
                          ...(readOnly ? [{
                            key: 'remove-locked',
                            label: 'Remove item 🔒',
                            onClick: showLockedNotice,
                            testId: `remove-item-locked-${index}`,
                            title: 'This order is locked',
                          }] : [
                          // 🔀 2026-09-28, owner: *"Please add the remove
                          // option to the action menu when the item is
                          // collapsed"*. The `!collapsedItems.has(index)` term
                          // is GONE from the menu item (the plain create-mode
                          // button below keeps it, byte-identical), and the
                          // last line shows Remove DISABLED with the reason
                          // rather than hiding it — a missing control reads as
                          // a bug, a greyed one explains itself.
                          // 🔀 2026-10-01: the only line's Remove is LIVE for a
                          // caller the page lets cancel (it opens the
                          // cancel-instead dialog), disabled with the reason
                          // otherwise. "Only" counts FORM lines.
                          {
                            key: 'remove',
                            label: 'Remove item',
                            onClick: () => requestRemoveItem(index),
                            testId: `remove-item-${index}`,
                            tone: 'reject',
                            disabled: items.length <= 1 && !onRequestCancelOrder,
                            title: items.length <= 1
                              ? (onRequestCancelOrder ? ONLY_ITEM_REMOVE_TOOLTIP : ONLY_ITEM_REMOVE_DENIED_TOOLTIP)
                              : `Remove item ${index + 1} from this order`,
                          },
                        ]),
                        ]}
                      />
                    </div>
                  ) : (
                    // 🔀 2026-10-01: an EDIT of a saved order also shows the
                    // only line's Remove — live when the page passes its
                    // cancel flow, disabled with the reason otherwise. Create
                    // mode keeps hiding it (no order to cancel; e2e unchanged).
                    readOnly ? (item.id != null && !collapsedItems.has(index) && (
                      // 🔒 Locked order, no item menu for this viewer: the same Remove, greyed with a
                      // padlock; a tap explains the lock (owner 2026-10-03).
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); showLockedNotice(); }}
                        className="inline-flex items-center gap-1 text-gray-400 dark:text-gray-500 text-sm font-medium cursor-not-allowed"
                        title="This order is locked"
                        aria-label={`Remove item ${index + 1} (locked)`}
                        data-testid={`remove-item-locked-${index}`}
                      >
                        Remove <PadlockIcon />
                      </button>
                    )) : (items.length > 1 || isSavedOrderEdit) && !collapsedItems.has(index) && (
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); requestRemoveItem(index); }}
                        disabled={items.length <= 1 && !onRequestCancelOrder}
                        title={items.length <= 1
                          ? (onRequestCancelOrder ? ONLY_ITEM_REMOVE_TOOLTIP : ONLY_ITEM_REMOVE_DENIED_TOOLTIP)
                          : undefined}
                        className="text-red-500 hover:text-red-700 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:text-red-500"
                        aria-label={`Remove item ${index + 1}`}
                        data-testid={`remove-item-${index}`}
                      >
                        Remove
                      </button>
                    )
                  )}
                </div>
              </div>

              {collapsedItems.has(index) ? null : (
                <>
                  {/* ── ITEM BODY GRID — SIX FIELDS, ONE ROW AT `lg` (2026-08-16) ──
                      Product owner, from prod/staging use: *"Unit price should
                      be able to fit on the same row. Partnumber can be
                      shortened to fit. also quanity and unit price can be
                      smaller. tool tips for any truncated fields"*.

                      🔑 `lg:grid-cols-12`, not the old `lg:grid-cols-5
                      2xl:grid-cols-6` — this grid has TWO conditional gates
                      (`canViewAccounting` hides Cost Code + Payment Type;
                      `item.is_hire` hides Unit Price), so the field COUNT
                      varies 3-6 and a fixed column count either wraps Unit
                      Price onto a row of its own (the defect reported) or
                      leaves a gap once accounting is hidden. Same "derive the
                      span, don't leave a hole" idiom Description's
                      `col-span-full` below already uses on this grid — see
                      `itemGridSpans()` above the component.

                      Fixed compact spans: Cost Code 3 (widest — its options
                      are long, e.g. "1009 — UK pyrotechnic consumables"),
                      Payment Type 2, Part Number 2 (narrower), Quantity 1
                      (small), Unit Price 2 (small). Item Category and Part
                      Number absorb whatever Cost Code/Payment Type/Unit Price
                      are not rendering, so every combination sums to 12:
                        accounting✓ + price shown:  IC2 CC3 PT2 PN2 Q1 UP2 = 12
                        accounting✓ + hire (no price): IC3 CC3 PT2 PN3 Q1 = 12
                        accounting✗ + price shown:  IC5      PN4 Q1 UP2 = 12
                        accounting✗ + hire (no price): IC7      PN4 Q1 = 12
                      `md:grid-cols-3` added for a 3-per-row middle step; sm
                      stays 2-per-row (unspanned, default col-span-1); mobile
                      stacks 1-per-row, unchanged. */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-12 gap-3" data-phone-pair-grid="">
                    {/* Item Category */}
                    <div className={itemGridSpans(canViewAccounting, item.is_hire).itemCategory}>
                      <label htmlFor={`item-category-${index}`} className={FIELD_LABEL_CLASS}>Item Category</label>
                      {readOnly ? (
                        <ReadOnlyValue testId={`item-category-${index}`}>{itemCategoryLabel(item.item_category)}</ReadOnlyValue>
                      ) : (
                        <select
                          id={`item-category-${index}`}
                          value={item.item_category || ''}
                          onChange={(e) => updateItem(index, 'item_category', e.target.value)}
                          className={`${itemInputClass}${categoryHighlightIndex === index ? ' ring-2 ring-red-500 dark:ring-red-400 animate-pulse' : ''}`}
                          data-testid={`item-category-${index}`}
                          title={
                            // 🔧 FIX ROUND 1, finding 7 (adversarial review):
                            // this used to show the conflict warning on
                            // EVERY non-decider select the moment a mode was
                            // established — including a line whose OWN
                            // category already agrees with it, silently
                            // replacing that select's normal "what's
                            // selected" truncation tooltip with a warning
                            // that does not describe its actual state.
                            // Reuses `isCategoryOptionGated` against the
                            // item's OWN current category: true only when
                            // THIS select's own choice is the one that
                            // conflicts, matching the per-option gating
                            // immediately below rather than a blanket
                            // per-line flag.
                            isCategoryOptionGated(orderHireMode, orderHireDeciderIndex, index, item.item_category)
                              ? HIRE_MODE_CONFLICT_TOOLTIP
                              : (itemCategoryLabel(item.item_category) || undefined)
                          }
                        >
                          <option value="">-- Select --</option>
                          {itemCategoryOptions(item.item_category).map((category) => {
                            const gated = isCategoryOptionGated(orderHireMode, orderHireDeciderIndex, index, category);
                            return (
                              <option key={category} value={category} disabled={gated} title={gated ? HIRE_MODE_CONFLICT_TOOLTIP : undefined}>{itemCategoryLabel(category)}</option>
                            );
                          })}
                        </select>
                      )}
                    </div>

                    {/* Cost Code — TWO NESTED GATES ASKING TWO DIFFERENT
                        QUESTIONS, WITH TWO DIFFERENT SERVER COUNTERPARTS. Do
                        not collapse them into one condition.
                          · VISIBILITY (`canViewAccounting`, outer) ↔ the server's
                            `canViewOrderAccounting`, which STRIPS the accounting
                            columns from the response (`stripAccountingItemFields`,
                            routes/orders.js:1333) rather than refusing the
                            request. `sub_department_lead` is seeded 0 here.
                          · EDITABILITY (`canManageOrderFields`, inner) ↔ the
                            server's `canSetAccountingFields`, which is
                            `canManageDepartmentOrders` verbatim on both writers
                            (`= canManageOrdersHere` on create, routes/orders.js:1539;
                            `= canSetPoNumber` on update, :1949) and silently
                            NULLS the three FKs for a caller without it.
                        🔀 The inner gate was `(isAdmin || isBuyer)` until
                        2026-08-09 — the unscoped flag, the same defect as the PO
                        field above and the same fix. It is genuinely the same
                        question as the PO number (one server expression governs
                        both), which is why they share `canManageOrderFields`;
                        the OUTER gate is NOT, and was deliberately left alone. */}
                    {canViewAccounting && (
                      <div className="lg:col-span-3" data-phone-half="">
                        <label htmlFor={`item-cost-code-${index}`} className={FIELD_LABEL_CLASS}>Cost Code</label>
                        {/* 🚨 THE LIST FAILED TO LOAD — RENDER NO CONTROL AT ALL,
                            2026-08-22, AND THE "NO CONTROL" HALF IS THE POINT.
                            A controlled `<select value={item.cost_code_id}>`
                            whose value has no matching `<option>` falls back to
                            `selectedIndex = 0`, which here is the literal
                            `-- None --` (measured: `value=""`,
                            `selectedIndex=0`, `optionsShown=['-- None --']`).
                            So an emptied option list made every coded line read
                            UNCODED, on three order pages at once, with no error
                            anywhere. Nothing writes automatically — but a
                            manager who sees an apparently-missing code and
                            reaches for the dropdown to correct it can only pick
                            `-- None --`, and `PATCH /:id/items/:itemId/cost-code`
                            writes that as `null` over a real cost code.
                            Removing the control is what makes that
                            unreachable; the sentence is what stops the reader
                            believing the code is gone. Its value is untouched
                            in form state, so a submit round-trips it unchanged.
                            🔑 Do NOT "fix" this by synthesising an `<option>`
                            for the stored id — `buildInitialItems` does not
                            carry the code's NAME into form state, so any such
                            option could only be a fabricated label, and a
                            fabricated label is the confident-false-claim defect
                            wearing a repair's clothes. */}
                        {loadFailures.costCodes ? (
                          <p
                            className={LOAD_FAILED_HINT_CLASS}
                            role="status"
                            data-testid={`item-cost-code-load-failed-${index}`}
                          >
                            Cost codes couldn’t be loaded — this line’s code is unchanged
                          </p>
                        ) : (readOnly || !canManageOrderFields) ? (
                          // 🔀 A THIRD sub-branch, 2026-08-16: readOnly + manager +
                          // `onReadOnlyCostCodeChange` supplied renders the SAME
                          // live `<select>` as edit mode, wired to the caller's
                          // dedicated-endpoint handler (`PATCH
                          // /:id/items/:itemId/cost-code`) instead of `updateItem`
                          // (local form state a read-only form never submits).
                          // This is what keeps a manager's cost-code edit LIVE on a
                          // non-editable order once `OrderExpandedRow` renders this
                          // form instead of its own table — see the prop's own doc.
                          // Every other caller passes nothing here and gets exactly
                          // today's static text.
                          (readOnly && canManageOrderFields && onReadOnlyCostCodeChange) ? (
                            <select
                              id={`item-cost-code-${index}`}
                              value={item.cost_code_id || ''}
                              onChange={(e) => onReadOnlyCostCodeChange(item.id, e.target.value ? parseInt(e.target.value, 10) : '')}
                              className={itemInputClass}
                              data-testid={`item-cost-code-${index}`}
                              title={selectedOptionLabel(costCodes, item.cost_code_id, (cc) => `${cc.code} — ${cc.name}`)}
                            >
                              <option value="">-- None --</option>
                              {costCodes.map(cc => (
                                <option key={cc.id} value={cc.id}>{cc.code} — {cc.name}</option>
                              ))}
                            </select>
                          ) : (
                            <ReadOnlyValue testId={`item-cost-code-${index}`}>
                              {(() => {
                                if (item.cost_code && item.cost_code_name) return `${item.cost_code} — ${item.cost_code_name}`;
                                const cc = costCodes.find(c => c.id === item.cost_code_id);
                                if (cc) return `${cc.code} — ${cc.name}`;
                                return item.cost_code_name || '';
                              })()}
                            </ReadOnlyValue>
                          )
                        ) : (
                          <>
                          <select
                            id={`item-cost-code-${index}`}
                            value={item.cost_code_id || ''}
                            onChange={(e) => setLineCostCode(index, e.target.value ? parseInt(e.target.value, 10) : '')}
                            className={`${itemInputClass}${jobFilledLine(item) ? ' ring-1 ring-violet-500 dark:ring-violet-400' : ''}`}
                            data-testid={`item-cost-code-${index}`}
                            title={selectedOptionLabel(costCodes, item.cost_code_id, (cc) => `${cc.code} — ${cc.name}`)}
                          >
                            <option value="">-- None --</option>
                            {costCodes.map(cc => (
                              <option key={cc.id} value={cc.id}>{cc.code} — {cc.name}</option>
                            ))}
                          </select>
                          {/* JOB COST CODES (2026-10-28): the fill's mark, and
                              the offer to a line that already had a code. */}
                          {jobFilledLine(item) ? (
                            <p className="mt-1 text-xs text-violet-700 dark:text-violet-300" data-testid={`item-cost-code-job-filled-${index}`}>
                              ◆ Set from the job — change it if needed
                            </p>
                          ) : (jobFillAllowed && activeJobCode && item.cost_code_id && Number(item.cost_code_id) !== Number(activeJobCode.id)) ? (
                            <button
                              type="button"
                              onClick={() => setLineCostCode(index, activeJobCode.id)}
                              className="mt-1 text-xs text-blue-700 dark:text-blue-300 underline underline-offset-2 hover:text-blue-900 dark:hover:text-blue-200"
                              data-testid={`item-cost-code-use-job-${index}`}
                            >
                              Use {activeJobCode.code} for this line?
                            </button>
                          ) : null}
                          </>
                        )}
                      </div>
                    )}

                    {/* Payment Type — same two-gate structure as Cost Code above,
                        same two server counterparts, same 2026-08-09 correction
                        to the inner one. Read that block before changing either. */}
                    {canViewAccounting && (
                      <div className="lg:col-span-2" data-phone-half="">
                        <label htmlFor={`item-payment-type-${index}`} className={FIELD_LABEL_CLASS}>Payment Type</label>
                        {/* Same load-failure branch as Cost Code above — read
                            that block before changing either. Tracked
                            SEPARATELY because the two lists are two requests:
                            `.../payment-types/active` can be refused while
                            `.../cost-codes/active` lands, and blanking a
                            working control is its own small false claim. */}
                        {loadFailures.paymentTypes ? (
                          <p
                            className={LOAD_FAILED_HINT_CLASS}
                            role="status"
                            data-testid={`item-payment-type-load-failed-${index}`}
                          >
                            Payment types couldn’t be loaded — this line’s type is unchanged
                          </p>
                        ) : (readOnly || !canManageOrderFields) ? (
                          // 🔀 Same third sub-branch as Cost Code above — read
                          // that block's comment before changing either.
                          (readOnly && canManageOrderFields && onReadOnlyPaymentTypeChange) ? (
                            <select
                              id={`item-payment-type-${index}`}
                              value={item.payment_type_id || ''}
                              onChange={(e) => onReadOnlyPaymentTypeChange(item.id, e.target.value ? parseInt(e.target.value, 10) : '')}
                              className={itemInputClass}
                              data-testid={`item-payment-type-${index}`}
                              title={selectedOptionLabel(paymentTypes, item.payment_type_id, (pt) => pt.name)}
                            >
                              <option value="">-- None --</option>
                              {paymentTypes.map(pt => (
                                <option key={pt.id} value={pt.id}>{pt.name}</option>
                              ))}
                            </select>
                          ) : (
                            <ReadOnlyValue testId={`item-payment-type-${index}`}>
                              {item.payment_type_name || (paymentTypes.find(pt => pt.id === item.payment_type_id)?.name) || ''}
                            </ReadOnlyValue>
                          )
                        ) : (
                          <select
                            id={`item-payment-type-${index}`}
                            value={item.payment_type_id || ''}
                            onChange={(e) => updateItem(index, 'payment_type_id', e.target.value ? parseInt(e.target.value, 10) : '')}
                            className={itemInputClass}
                            data-testid={`item-payment-type-${index}`}
                            title={selectedOptionLabel(paymentTypes, item.payment_type_id, (pt) => pt.name)}
                          >
                            <option value="">-- None --</option>
                            {paymentTypes.map(pt => (
                              <option key={pt.id} value={pt.id}>{pt.name}</option>
                            ))}
                          </select>
                        )}
                      </div>
                    )}

                    {/* Part Number — narrower than before (2026-08-16): was
                        `2xl:col-span-2` on the old 5/6-col grid, now absorbs
                        slack alongside Item Category on the 12-col grid — see
                        `itemGridSpans()`. */}
                    {/* 📱 A HIRE line has no Unit Price, so Part Number pairs with Quantity on a phone (owner 2026-10-03). */}
                    <div className={`${itemGridSpans(canViewAccounting, item.is_hire).partNumber} max-md:order-2`} {...(item.is_hire ? { 'data-phone-half': '' } : {})}>
                      <label htmlFor={`item-part-number-${index}`} className={FIELD_LABEL_CLASS}>Part Number</label>
                      {readOnly ? (
                        <ReadOnlyValue testId={`item-part-number-${index}`}>{item.part_number}</ReadOnlyValue>
                      ) : (
                        <input
                          type="text"
                          id={`item-part-number-${index}`}
                          value={item.part_number}
                          onChange={(e) => updateItem(index, 'part_number', e.target.value)}
                          placeholder="e.g. RS-12345"
                          className={itemInputClass}
                          data-testid={`item-part-number-${index}`}
                          title={item.part_number || undefined}
                        />
                      )}
                    </div>

                    {/* Quantity — small numeric column (2026-08-16, product
                        owner: "quanity and unit price can be smaller") */}
                    <div className="lg:col-span-1 max-md:order-2" data-phone-half="">
                      <label htmlFor={`item-quantity-${index}`} className={FIELD_LABEL_CLASS}>Quantity</label>
                      {readOnly ? (
                        <ReadOnlyValue testId={`item-quantity-${index}`}>{item.quantity}</ReadOnlyValue>
                      ) : (
                        <>
                          {/* `ui/NumberInput` (input audit #7, 2026-10-29): unreadable text arrives as
                              its MALFORMED_NUMBER marker, never '' — the validator says "not a
                              number". Fractions stay allowed; step/min are native hints only. */}
                          <NumberInput
                            id={`item-quantity-${index}`}
                            min="1"
                            step="1"
                            value={item.quantity}
                            onChange={(v) => {
                              updateItem(index, 'quantity', v);
                              clearItemErrors(index);
                            }}
                            // Same tokens as `itemInputClass` (32px on md+, `text-sm`) with only the
                            // border colour switched on error. It carried its own `md:h-[38px]
                            // text-base px-3` from the 2026-05-28 view+edit unification, so it sat
                            // 6px taller than Part Number / Unit Price beside it (owner, 2026-09-11).
                            className={`w-full border rounded-lg px-2 h-11 md:h-8 text-base md:text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none focus:border-blue-500 ${
                              errors.itemErrors && errors.itemErrors[index] && errors.itemErrors[index].quantity
                                ? 'border-red-500 bg-red-50'
                                : 'border-gray-300'
                            }`}
                            testId={`item-quantity-${index}`}
                          />
                          {errors.itemErrors && errors.itemErrors[index] && errors.itemErrors[index].quantity && (
                            <p id={`item-quantity-error-${index}`} role="alert" className="text-red-500 text-xs mt-1">{errors.itemErrors[index].quantity}</p>
                          )}
                        </>
                      )}
                    </div>

                    {/* Unit Price - hidden when hire is enabled. Small
                        numeric column (2026-08-16), same request as
                        Quantity above. */}
                    {!item.is_hire && (
                      <div className="lg:col-span-2 max-md:order-2" data-phone-half="">
                        {/* 🔻 "ex VAT" DROPPED FROM THIS LABEL 2026-08-08, and
                            this one is the sharpest of the lot: it INSTRUCTED
                            the user what to type. The product decision is that
                            the department chooses whether it enters ex- or
                            inc-VAT prices, so the app must not tell them. See
                            `hooks/useVatMode.js`. */}
                        <label htmlFor={`item-unit-price-${index}`} className={FIELD_LABEL_CLASS}>Unit Price (£)</label>
                        {readOnly ? (
                          <ReadOnlyValue testId={`item-unit-price-${index}`}>{formatMoney(item.unit_price)}</ReadOnlyValue>
                        ) : (
                          /* 💷 SHARED `ui/CurrencyInput` SINCE 2026-09-22, NOT A
                             RAW `<input type="number">`. ⚖️ Owner: *"round to
                             2dp and include £ when the value is typed in"* —
                             this is the "include £" half, and it is FEEDBACK,
                             never a gate: the rounding that matters is done at
                             the server write boundary (`routes/orders.js`,
                             `moneyForStorage`), because the browser is not the
                             authority on what a column holds.

                             🔑 The control shows `£19.33` at rest and the raw
                             number while focused, so the user sees the amount
                             the ROW will carry rather than the string they
                             typed. 🪤 It does NOT round what it emits — it
                             hands `onChange` the raw typed STRING, exactly as
                             the previous `<input>` did, so `updateItem` and
                             every dirty-check downstream are unchanged.

                             🪤 `prefix` IS DELIBERATELY NOT PASSED. With it the
                             control renders a standalone `£` glyph and adds
                             `pl-7` to the field — a padding change inside a
                             `lg:col-span-2` numeric column whose width is
                             deliberate (root `CLAUDE.md`). Defaulted off, the
                             `£` arrives as part of the VALUE via
                             `formatCurrency`, which is what the ruling asked
                             for. Never pass an independent `includeSymbol`, and
                             never render a glyph from here — `prefix` derives
                             `includeSymbol` in one expression precisely so a
                             double `£` cannot be reintroduced.

                             🔒 `id`, `data-testid` (via `testId`), the
                             placeholder and `className` are carried through
                             UNCHANGED: `id` is the `<label htmlFor>` target and
                             therefore this control's ACCESSIBLE NAME, and
                             `itemInputClass` is what keeps the field the same
                             width and height as Quantity beside it. `min="0"`
                             and `step="0.01"` are not passed because the
                             component sets both itself. */
                          <CurrencyInput
                            id={`item-unit-price-${index}`}
                            value={item.unit_price}
                            onChange={(value) => updateItem(index, 'unit_price', value)}
                            placeholder="0.00"
                            className={itemInputClass}
                            testId={`item-unit-price-${index}`}
                          />
                        )}
                      </div>
                    )}

                    {/* 🔻 THE PER-ITEM LEAD TIME CONTROL WAS HERE AND IS GONE
                        (2026-08-09, lead-time increment 3). Product owner: *"one
                        lead time per order rather than each item as thats gets
                        hard to manage"*. It was a six-preset `<select>` plus a
                        free-text "Custom…" box writing the TEXT column
                        `order_items.lead_time`, which every consumer then had to
                        put back through a three-regex parser.

                        🔑 **THE INPUT IS GONE. THE COLUMN IS NOT, AND NEITHER IS
                        ITS VALUE.** `buildInitialItems` still hydrates
                        `lead_time` into item state and `normalizeItemRate`
                        spreads it into the payload, so a legacy item round-trips
                        BYTE-IDENTICALLY through a save that touches some other
                        field. That is a property of THIS change, not of the
                        parser, and it is pinned by
                        `OrderForm.legacyRoundTrip.test.js` — because
                        dropping an input is exactly the shape that blanks data
                        on save without anybody noticing.
                        `server/helpers/leadTimeHelpers.js` still reads the
                        column as the legacy fallback for the ~1,113 orders that
                        carry one.

                        ⚠️ **The read-only display went with the input, not just
                        the input.** Keeping a read-out would have re-introduced
                        the per-item surface the owner asked to be rid of, and it
                        would have rendered "Unknown" on every line of every NEW
                        order, because `createEmptyItem()` defaults the field to
                        that string. The value survives in the CSV/XLS export's
                        `Estimated Lead Time` column. */}

                    {/* ── DESCRIPTION — FULL ROW WIDTH (2026-08-11) ─────────
                        Product owner: *"any reason description is not a full
                        row width?"* — and there was not one. It carried
                        `sm:col-span-2 lg:col-span-3 2xl:col-span-4`, i.e. 3 of
                        5 tracks at `lg` and 4 of 6 at `2xl`, never the row.

                        🔑 NOTHING WAS RESERVING THE REMAINDER, which is the
                        fact that settles it: **Description is the LAST child of
                        this grid.** The only things after it are the two
                        comment blocks below recording the per-item lead-time
                        and needed-by inputs deleted in increment 3, so the
                        leftover tracks were unconditionally empty in every
                        permission and hire combination.
                        📏 MEASURED before the change, Chromium, `/orders/new`,
                        sidebar open, on the longest free-text field on the
                        form: at 1280 a BUYER (accounting visible) got 542.4px
                        with **184.8px** dead to its right; a user without
                        accounting got the same 542.4px with **369.6px** dead,
                        because Cost Code and Payment Type were not there to
                        fill row 1 either.

                        🔑 `col-span-full` (`grid-column: 1 / -1`), NOT a span
                        count, and the difference is the whole robustness
                        argument. This grid has FOUR conditional members — Cost
                        Code and Payment Type (`canViewAccounting`), Unit Price
                        (hidden for a hire line) and Part Number's own `2xl`
                        span — so "the number of tracks left over" is different
                        in each combination. A tuned `lg:col-span-4` is right
                        for exactly one of them and leaves a gap in the others,
                        which is the same defect class as the Order Details
                        misalignment fixed in this commit. `1 / -1` is correct
                        in all of them and needs no `2xl:` override.
                        🔴 THE COST, stated so it is not discovered later:
                        because it must START at track 1, Description now takes
                        a row of its own rather than sharing one with Unit
                        Price. Measured at 1280 for a buyer, an EXPANDED line
                        item grows 296px → 366px (+70px, one grid row). A
                        six-line order expanded is ~420px taller. Flagged to the
                        owner; the alternative that avoids it is filling only to
                        the right edge of the current row, which is the fragile
                        one above.
                        🔀 **UPDATED 2026-08-16 — the grid above is now
                        `lg:grid-cols-12` with `itemGridSpans()` sizing Item
                        Category and Part Number so EVERY combination sums to
                        exactly 12 (see the block comment at the top of this
                        grid). There is no longer a "tracks left over" case at
                        all — the row above Description is always full before
                        it starts.** `col-span-full` is still correct and still
                        required: Description must own its own row regardless
                        of the row above, and a span count would still need a
                        `2xl:` override the moment any of this grid's spans
                        change again. The "Part Number's own `2xl` span" clause
                        above is HISTORICAL — Part Number has no `2xl`-specific
                        span any more, it is one of the two fields
                        `itemGridSpans()` sizes at `lg` and up. */}
                    {/* 📱 Phone: Description sits ABOVE Part Number / Quantity / Unit Price (owner 2026-10-03) — `order`, phone only; desktop already gives it its own row below. */}
                    <div className="col-span-full max-md:order-1">
                      <label htmlFor={`item-description-${index}`} className={FIELD_LABEL_CLASS}>Description</label>
                      {readOnly ? (
                        <ReadOnlyValue testId={`item-description-${index}`}>{item.description}</ReadOnlyValue>
                      ) : (
                        <>
                          <input
                            type="text"
                            id={`item-description-${index}`}
                            value={item.description}
                            onChange={(e) => {
                              updateItem(index, 'description', e.target.value);
                              if (errors.items) setErrors(prev => ({ ...prev, items: '' }));
                              // Only the blank-SAVED-line error is cleared by typing
                              // here; a quantity/price error on this row stays until
                              // that field is edited.
                              if (errors.itemErrors?.[index]?.description) clearItemErrors(index);
                            }}
                            placeholder="Item description"
                            className={errors.itemErrors?.[index]?.description
                              ? itemInputClass.replace('border-gray-300', 'border-red-500 bg-red-50 dark:bg-red-900/20')
                              : itemInputClass}
                            aria-invalid={errors.itemErrors?.[index]?.description ? true : undefined}
                            aria-describedby={errors.itemErrors?.[index]?.description ? `item-description-error-${index}` : undefined}
                            data-testid={`item-description-${index}`}
                          />
                          {errors.itemErrors?.[index]?.description && (
                            <p id={`item-description-error-${index}`} role="alert" className="text-red-500 text-xs mt-1" data-testid={`item-description-error-${index}`}>{errors.itemErrors[index].description}</p>
                          )}
                        </>
                      )}
                    </div>

                    {/* 🔻 THE PER-ITEM NEEDED-BY DATE WAS HERE AND IS GONE
                        (2026-08-09). The order now carries ONE needed-by date,
                        in the Order Details section above — it lived in a
                        section of its own called "Supplier & Timing" until that
                        was folded into Order Details on 2026-08-10. Same as
                        the lead time beside it: the INPUT is removed, the
                        `order_items.needed_by_date` COLUMN and its values are
                        kept and round-tripped unchanged, and
                        `computeSlackDetail` still honours a legacy item date
                        AHEAD of the order-level one — 747 of the 1,736 orders in
                        the dev database carry both, and the item date wins on
                        every one of them.
                        🔑 That legacy-first ordering is exactly what makes
                        removing this input safe: nothing raised from here on has
                        an item date, so the branch can only ever fire on rows
                        that predate this change. */}
                  </div>

                  {/* Hire details */}
                  {!!item.is_hire && (
                    <div className="mt-3 pt-3 border-t border-gray-200">
                      {/* 🔀 2026-09-29, owner's staging feedback: the header
                          names the hire's length on the same line —
                          "Hire Details: 12 days" / "3 weeks" / "3 weeks & 2 days
                          (charged as 4 weeks)" — live from the dates and rate
                          type. Wording: `utils/hireCost.js#hireDetailsLength`. */}
                      <p className="text-xs font-medium text-blue-600 mb-2" data-testid={`item-hire-details-heading-${index}`}>
                        Hire Details{hireDetailsLength(item) ? `: ${hireDetailsLength(item)}` : ''}
                      </p>
                      {/* 🔀 2026-09-29, owner's staging feedback: the four hire
                          fields on ONE row at desktop widths (was `lg:grid-cols-3`,
                          which wrapped Rate (£) alone onto a second row); still one
                          column on a phone, two at `sm`. MEASURED in Chromium
                          against the CI build: a date input is 143.33px natural
                          width at `text-sm px-2`, so 4 columns + 3 × 12px gaps need
                          612px. The narrowest item card at `lg` (1024px, sidebar
                          open, Department Orders' expanded row) is ~634px; at 1280
                          it is ~890px (≈213px a column) and at 1440 ~1050px (≈253px)
                          — those three are SUMMED from the layout's own paddings
                          (sidebar 256, page lg:p-6, panel p-4, form p-2.5, card
                          p-4), not read off the live page: re-check in a browser.
                          Pinned by `OrderForm.hireFieldsRow.test.js`. */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-phone-pair-grid="">
                        <div data-phone-half="">
                          <label htmlFor={`item-hire-from-${index}`} className={FIELD_LABEL_CLASS}>First day of hire</label>
                          {readOnly ? (
                            <ReadOnlyValue testId={`item-hire-from-${index}`}>{formatReadableDate(item.hire_from)}</ReadOnlyValue>
                          ) : (
                            <>
                              {/* 📅 Required on save too ("Yes, require both
                                  dates", 2026-09-28) — same pattern as the
                                  end date below. */}
                              <input
                                type="date"
                                id={`item-hire-from-${index}`}
                                value={item.hire_from}
                                onChange={(e) => {
                                  updateItem(index, 'hire_from', e.target.value);
                                  if (errors.itemErrors?.[index]?.hire_from) clearItemErrors(index);
                                }}
                                className={errors.itemErrors?.[index]?.hire_from
                                  ? itemInputClass.replace('border-gray-300', 'border-red-500 bg-red-50 dark:bg-red-900/20')
                                  : itemInputClass}
                                aria-invalid={errors.itemErrors?.[index]?.hire_from ? true : undefined}
                                aria-describedby={errors.itemErrors?.[index]?.hire_from ? `item-hire-from-error-${index}` : undefined}
                                data-testid={`item-hire-from-${index}`}
                              />
                              {errors.itemErrors?.[index]?.hire_from && (
                                <p id={`item-hire-from-error-${index}`} role="alert" className="text-red-500 dark:text-red-400 text-xs mt-1" data-testid={`item-hire-from-error-${index}`}>{errors.itemErrors[index].hire_from}</p>
                              )}
                            </>
                          )}
                        </div>
                        <div data-phone-half="">
                          <label htmlFor={`item-hire-to-${index}`} className={FIELD_LABEL_CLASS}>Last day of hire</label>
                          {readOnly ? (
                            <ReadOnlyValue testId={`item-hire-to-${index}`}>{formatReadableDate(item.hire_to)}</ReadOnlyValue>
                          ) : (
                            <>
                              {/* 📅 Required on save (owner ruling 2026-09-28) —
                                  `validateForm`'s `hire_to` error; same inline
                                  pattern as Description above. */}
                              <input
                                type="date"
                                id={`item-hire-to-${index}`}
                                value={item.hire_to}
                                onChange={(e) => {
                                  updateItem(index, 'hire_to', e.target.value);
                                  if (errors.itemErrors?.[index]?.hire_to) clearItemErrors(index);
                                }}
                                className={errors.itemErrors?.[index]?.hire_to
                                  ? itemInputClass.replace('border-gray-300', 'border-red-500 bg-red-50 dark:bg-red-900/20')
                                  : itemInputClass}
                                aria-invalid={errors.itemErrors?.[index]?.hire_to ? true : undefined}
                                aria-describedby={errors.itemErrors?.[index]?.hire_to ? `item-hire-to-error-${index}` : undefined}
                                data-testid={`item-hire-to-${index}`}
                              />
                              {errors.itemErrors?.[index]?.hire_to && (
                                <p id={`item-hire-to-error-${index}`} role="alert" className="text-red-500 dark:text-red-400 text-xs mt-1" data-testid={`item-hire-to-error-${index}`}>{errors.itemErrors[index].hire_to}</p>
                              )}
                            </>
                          )}
                        </div>
                        <div data-phone-half="">
                          <label htmlFor={`item-hire-rate-type-${index}`} className={FIELD_LABEL_CLASS}>Rate Type</label>
                          {readOnly ? (
                            <ReadOnlyValue testId={`item-hire-rate-type-${index}`}>
                              {item.hire_rate_type === 'daily' ? 'Daily Rate' : item.hire_rate_type === 'weekly' ? 'Weekly Rate' : item.hire_rate_type === 'total' ? 'Total Hire Rate' : ''}
                            </ReadOnlyValue>
                          ) : (
                            <select
                              id={`item-hire-rate-type-${index}`}
                              value={item.hire_rate_type || 'daily'}
                              onChange={(e) => updateItem(index, 'hire_rate_type', e.target.value)}
                              className={itemInputClass}
                              data-testid={`item-hire-rate-type-${index}`}
                            >
                              <option value="daily">Daily Rate</option>
                              <option value="weekly">Weekly Rate</option>
                              <option value="total">Total Hire Rate</option>
                            </select>
                          )}
                        </div>
                        <div data-phone-half="">
                          <label className={FIELD_LABEL_CLASS}>
                            {item.hire_rate_type === 'daily' ? 'Daily' : item.hire_rate_type === 'weekly' ? 'Weekly' : 'Total'} Rate (£)
                          </label>
                          {readOnly ? (
                            <ReadOnlyValue testId={`item-hire-rate-${index}`}>{formatMoney(item.hire_rate)}</ReadOnlyValue>
                          ) : (
                            /* 💷 SHARED `ui/CurrencyInput` SINCE 2026-09-22, same
                               ruling and same contract as Unit Price above —
                               read that call site's note for the `prefix`
                               reasoning, which applies here unchanged.
                               🪤 THIS FIELD IS NOT `unit_price`. It is the
                               form-only `hire_rate`, which `normalizeItemRate`
                               maps onto ONE of `daily_rate` / `weekly_rate` /
                               `total_hire_rate` on submit according to
                               `hire_rate_type`. The label above changes with
                               that type, so the `£` shown here is a rate, not a
                               line total — `getLineTotal` multiplies it by the
                               duration and the quantity.
                               🔻 THE SERVER DOES NOT ROUND THIS FIELD'S COLUMNS.
                               `moneyForStorage` bounds `unit_price` and
                               `line_total` only; the three rate columns are
                               written raw, and `hireCostForDays` rounds the
                               PRODUCT rather than the rate. So a 7dp daily rate
                               still persists at 7dp. That is DECLARED SCOPE, not
                               an oversight — widening the rounding to the rate
                               columns is an unruled change. */
                            <CurrencyInput
                              id={`item-hire-rate-${index}`}
                              value={item.hire_rate}
                              onChange={(value) => updateItem(index, 'hire_rate', value)}
                              placeholder="0.00"
                              className={itemInputClass}
                              testId={`item-hire-rate-${index}`}
                            />
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Per-item attachments — create (NewOrder), view/edit (OrderPage) and the
                      inline editor. Saved files (by the item's own id) show with download +
                      remove (or strikethrough + undo if pending removal); pending uploads show
                      as PendingUploadRow. Drag/drop + Attach render only when there is a target
                      (`itemFilesImmediate.onUpload` on a saved line, or `pendingFiles`). */}
                  {showItemFileUpload && (() => {
                    // Saved files by the item's OWN id — never by index.
                    const savedItemId = items[index] && items[index].id;
                    const itemExisting = (itemFilesImmediate && savedItemId != null
                      && itemFilesImmediate.filesByItemId
                      && itemFilesImmediate.filesByItemId[savedItemId]) || [];
                    // 🔀 2026-09-15 (R13): a file with no immediate target UPLOADS
                    // AT ONCE to the pending store under this line's key (its id,
                    // else its `client_key`) and the parent's save attaches it.
                    const immediateUpload = !!(itemFilesImmediate && savedItemId != null
                      && typeof itemFilesImmediate.onUpload === 'function');
                    // Listed under EVERY line that has some — including a saved
                    // line whose new picks go straight up: a new line whose claim
                    // was refused after its save keeps its uploads here.
                    const pendingLineKey = pendingFiles ? pendingLineKeyFor(items[index]) : null;
                    const itemPending = pendingLineKey ? pendingFiles.entriesFor(pendingLineKey) : [];
                    // A SAVED line's files on their way up, or refused (2026-09-14).
                    const itemUploading = (itemFilesImmediate && savedItemId != null
                      && typeof itemFilesImmediate.uploadingEntriesFor === 'function')
                      ? itemFilesImmediate.uploadingEntriesFor(savedItemId)
                      : [];
                    const canAttach = immediateUpload || !!pendingLineKey;
                    // In flight on THIS line only (a saved line's immediate upload),
                    // or the parent's save + claims. The control stays MOUNTED and
                    // merely inert — unmounting the input mid-upload would discard
                    // the element the browser is reading from.
                    const attachBusy = immediateUpload
                      ? !!(itemFilesImmediate.uploadingItemIds && itemFilesImmediate.uploadingItemIds.has(savedItemId))
                      : !!(pendingLineKey && pendingFilesDisabled);
                    const attachDisabledReason = attachBusy ? ITEM_FILE_UPLOADING_TOOLTIP : null;
                    const attachInert = !canAttach || attachBusy;
                    // 🚨 **AN INERT ZONE KEEPS ITS DRAG HANDLERS — second review
                    // round, 2026-09-13, probe P7.** The first fix detached
                    // `onDragOver`/`onDrop` while a line was uploading, reasoning
                    // that no handler means no upload. True, and the wrong
                    // question: with no handler nothing calls `preventDefault`,
                    // so the BROWSER takes the drop and Chrome opens the file in
                    // the tab — the user loses the page, and an unsaved edit with
                    // it. So the zone stays attached whenever the control renders
                    // at all; an inert one refuses the highlight
                    // (`refuseItemDrag`), and a drop still goes through
                    // `handleItemDrop` → `acceptItemFiles`, whose in-flight checks
                    // are what refuse it.
                    // 🔀 2026-09-15: an UNSAVED line is not inert — its drop
                    // uploads to the pending store (see `acceptItemFiles`).
                    const dropZoneLive = canAttach;
                    const canRemoveExisting = typeof onExistingFileToggleRemove === 'function';
                    if (itemExisting.length === 0 && itemUploading.length === 0 && itemPending.length === 0 && !canAttach) return null;
                    const visibleCount = itemExisting.filter(f => !(pendingDeleteFileIds && pendingDeleteFileIds.has(f.id))).length + itemPending.length;
                    return (
                      <div
                        className={`mt-3 pt-3 border-t border-gray-200 ${itemDragOver[index] ? 'bg-blue-50 border-t-blue-300' : ''}`}
                        onDragOver={!dropZoneLive ? undefined : (attachInert ? refuseItemDrag : (e) => handleItemDragOver(index, e))}
                        onDragLeave={!dropZoneLive ? undefined : (attachInert ? refuseItemDrag : (e) => handleItemDragLeave(index, e))}
                        onDrop={!dropZoneLive ? undefined : (e) => handleItemDrop(index, e)}
                        data-testid={`item-drop-zone-${index}`}
                      >
                        <div className="flex items-center gap-2 mb-2">
                          {canAttach && (
                            <button
                              type="button"
                              disabled={attachInert}
                              onClick={() => itemFileInputRefs.current[index] && itemFileInputRefs.current[index].click()}
                              className={`p-0 border-0 bg-transparent ${attachInert ? 'cursor-not-allowed opacity-50' : 'cursor-pointer hover:opacity-70'}`}
                              title={attachDisabledReason || 'Attach file'}
                              aria-label={`Attach file to item ${index + 1}`}
                              data-testid={`item-paperclip-btn-${index}`}
                            >
                              <PaperclipIcon className={`h-4 w-4 text-gray-400 ${attachInert ? '' : 'hover:text-blue-500'}`} />
                            </button>
                          )}
                          {/* The words open the picker too (owner 2026-10-03), the same
                              action as the paperclip and "+ Attach File" beside them. */}
                          {canAttach ? (
                            <button
                              type="button"
                              disabled={attachInert}
                              onClick={() => itemFileInputRefs.current[index] && itemFileInputRefs.current[index].click()}
                              className={`p-0 border-0 bg-transparent text-left text-xs font-medium text-gray-500 ${attachInert ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                              data-testid={`item-attach-label-${index}`}
                            >
                              Item Attachments{visibleCount > 0 ? ` (${visibleCount})` : ''}
                              {/* 📱 phone: the hint replaces the "+ Attach File" button (owner 2026-10-03). */}
                              <span className="md:hidden font-normal">{attachBusy ? ' – Uploading…' : visibleCount === 0 ? ' – Click to attach files' : ''}</span>
                            </button>
                          ) : (
                            <span className="text-xs font-medium text-gray-500">
                              Item Attachments{visibleCount > 0 ? ` (${visibleCount})` : ''}
                            </span>
                          )}
                          {canAttach && (
                            <button
                              type="button"
                              disabled={attachInert}
                              onClick={() => itemFileInputRefs.current[index] && itemFileInputRefs.current[index].click()}
                              title={attachDisabledReason || undefined}
                              className={`ml-auto text-xs font-medium flex items-center gap-1 max-md:hidden ${attachInert ? 'text-gray-400 cursor-not-allowed' : 'text-blue-600 hover:text-blue-800'}`}
                              data-testid={`item-upload-btn-${index}`}
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                              </svg>
                              {attachBusy ? 'Uploading…' : 'Attach File'}
                            </button>
                          )}
                          {canAttach && (
                            <input
                              ref={el => { itemFileInputRefs.current[index] = el; }}
                              type="file"
                              multiple
                              disabled={attachBusy}
                              onChange={(e) => handleItemFileSelect(index, e)}
                              className="hidden"
                              aria-label={`Upload files for item ${index + 1}`}
                              data-testid={`item-file-input-${index}`}
                            />
                          )}
                        </div>
                        {itemDragOver[index] && canAttach && (
                          <div className="border-2 border-dashed border-blue-400 rounded-lg p-3 text-center mb-2">
                            <p className="text-xs text-blue-600 font-medium">Drop files here to attach</p>
                          </div>
                        )}
                        {(itemExisting.length > 0 || itemUploading.length > 0 || itemPending.length > 0) && (
                          <div className="space-y-1" data-testid={`item-file-list-${index}`}>
                            {itemExisting.map(file => {
                              const isPending = pendingDeleteFileIds && pendingDeleteFileIds.has(file.id);
                              // 🔑 PER FILE in immediate mode, mirroring
                              // `DELETE /api/orders/:id/files/:fileId`, which decides
                              // per file. FAILS CLOSED — a caller that supplies the
                              // bag but forgets the predicate shows no Remove rather
                              // than a button the server 403s.
                              const mayRemove = canRemoveExisting && (
                                !itemFilesImmediate
                                || (typeof itemFilesImmediate.canRemoveFile === 'function'
                                    && itemFilesImmediate.canRemoveFile(file))
                              );
                              return (
                                <div key={`existing-${file.id}`} className={`flex items-center justify-between bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded px-2 py-1.5 text-xs ${isPending ? 'opacity-60' : ''}`} data-testid={`item-existing-file-${file.id}`}>
                                  <div className={`flex items-center gap-1.5 min-w-0 ${isPending ? 'line-through' : ''}`}>
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5 text-blue-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                    </svg>
                                    <span className="text-gray-700 dark:text-gray-200 truncate">{file.file_name}</span>
                                    {file.file_size != null && (
                                      <span className="text-gray-400 flex-shrink-0">({formatFileSize(file.file_size)})</span>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                                    {!isPending && (
                                      <a
                                        href={file.file_path}
                                        download={file.file_name}
                                        className="text-blue-600 hover:text-blue-800 font-medium"
                                        data-testid={`download-item-existing-${file.id}`}
                                      >
                                        Download
                                      </a>
                                    )}
                                    {mayRemove && (
                                      <button
                                        type="button"
                                        onClick={() => onExistingFileToggleRemove(file.id)}
                                        className={`${isPending ? 'text-blue-600 hover:text-blue-800' : 'text-red-500 hover:text-red-700'} font-medium`}
                                        aria-label={isPending ? `Undo remove ${file.file_name}` : `Remove ${file.file_name}`}
                                        data-testid={`toggle-remove-item-existing-${file.id}`}
                                      >
                                        {isPending ? 'Undo' : 'Remove'}
                                      </button>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                            {itemUploading.map((entry) => (
                              <UploadingFileRow
                                key={entry.key}
                                file={entry.file}
                                fraction={entry.fraction}
                                error={entry.error}
                                onDismiss={typeof itemFilesImmediate.onDismissUpload === 'function'
                                  ? () => itemFilesImmediate.onDismissUpload(entry.key)
                                  : null}
                                className={ITEM_UPLOADING_ROW_CHROME}
                                testId={`item-uploading-${index}-${entry.key}`}
                              />
                            ))}
                            {itemPending.map((entry) => (
                              <PendingUploadRow
                                key={entry.key}
                                entry={entry}
                                onRemove={pendingFiles.remove}
                                onRetry={pendingFiles.retry}
                                className={ITEM_UPLOADING_ROW_CHROME}
                                testId={`item-pending-${index}-${entry.key}`}
                              />
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })()}

                  {/* Item Notes (left) + Line Total (right).
                      🐛 FIXED 2026-09-30 (owner: on an expanded order on mobile
                      "Item Notes" and "Line Total" OVERLAP). The row was a
                      non-wrapping flex: the Line Total group is `shrink-0`
                      (~190px) and the notes column `min-w-0`, so in a narrow
                      expanded panel the notes column shrank below its own
                      `shrink-0` "Item Notes" label and the label PAINTED OVER
                      the Line Total. Now the row WRAPS: the notes column keeps a
                      real floor (`min-w-[6.5rem]` — just clears the label; measured in
                      Chromium at 320/360/375/414px: never overlaps, never clips
                      the label, still ONE line at 414) and clips (`overflow-hidden`),
                      and when both cannot fit the Line Total drops to its own
                      line, right-aligned (`ml-auto`). Wide screens are unchanged
                      — there both fit on one line exactly as before.
                      ⚠️ jsdom has no layout; the classes are pinned by
                      `OrderForm.itemNotesLineTotal.test.js`, the fit is a
                      BROWSER check.
                      📱 2026-09-30 (owner): below `md` they still wrapped, so
                      the link reads "Notes" and both are `text-xs` there; the
                      floor drops to `min-w-[4rem]`. One line at 360px. md+
                      unchanged ("Item Notes", `text-sm`, 6.5rem floor). */}
                  <div className="mt-3 border-t border-gray-200 pt-2 flex flex-wrap items-start gap-x-3 gap-y-2" data-testid={`item-notes-total-row-${index}`}>
                    <div className="flex-1 min-w-[4rem] md:min-w-[6.5rem] overflow-hidden">
                      <button
                        type="button"
                        onClick={() => setItemNotesExpanded(prev => ({ ...prev, [index]: !prev[index] }))}
                        className="flex items-center gap-1.5 w-full min-h-[44px] md:min-h-0 text-left text-xs md:text-sm font-medium text-gray-800 hover:text-gray-900 dark:text-white dark:hover:text-gray-200 transition-colors"
                        aria-expanded={!!itemNotesExpanded[index]}
                        aria-label={`Toggle notes for item ${index + 1}`}
                        data-testid={`item-notes-toggle-${index}`}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className={`h-3.5 w-3.5 shrink-0 max-md:hidden transition-transform ${itemNotesExpanded[index] ? 'rotate-90' : ''}`}
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth={2}
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                        </svg>
                        {/* 📱 "+ Item notes" in the subtle field-label grey (owner 2026-10-03); − while open. */}
                        <span className="shrink-0 md:hidden" data-phone-field-label="">{itemNotesExpanded[index] ? '− Item notes' : '+ Item notes'}</span>
                        <span className="shrink-0 hidden md:inline">Item Notes</span>
                        {/* Collapsed + has notes: show a one-line preview of the note
                            content (truncates to fit) so it's obvious there's a note
                            without expanding. Full text on hover. */}
                        {item.item_notes && !itemNotesExpanded[index] && (
                          <span
                            className="min-w-0 flex-1 truncate text-xs font-normal"
                            title={item.item_notes}
                          >
                            <span className="font-semibold text-blue-600 dark:text-blue-400">NOTES:</span>{' '}
                            <span className="text-gray-500 dark:text-gray-400">{item.item_notes.replace(/\s+/g, ' ').trim()}</span>
                          </span>
                        )}
                      </button>
                    </div>
                    {/* Line total: plain text, not a boxed field — it is computed,
                        and a grey box read as editable (owner 2026-10-01). Same
                        width/padding as before so the right edge still aligns. */}
                    <div className="shrink-0 ml-auto flex items-center gap-2" data-testid={`item-line-total-group-${index}`}>
                      <span className="text-xs md:text-sm font-normal text-gray-500 dark:text-gray-400 whitespace-nowrap">Line Total</span>
                      <div className="w-24 md:w-28 px-2 md:px-3 h-11 md:h-[38px] text-sm font-semibold tabular-nums text-gray-900 dark:text-white flex items-center justify-end" data-testid={`item-line-total-${index}`}>
                        {formatMoney(getLineTotal(item))}
                      </div>
                    </div>
                    {/* Notes toggle shares the Line total row; the box opens full
                        width UNDERNEATH it (owner 2026-10-01: save vertical space). */}
                      {itemNotesExpanded[index] && (
                        readOnly ? (
                          <p
                            className="basis-full w-full text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap min-h-[2.5rem] max-md:max-h-[8.5rem] max-md:overflow-y-auto"
                            data-testid={`item-notes-input-${index}`}
                          >
                            {item.item_notes || <span className="text-gray-400 dark:text-gray-500 italic">No notes</span>}
                          </p>
                        ) : (
                          <textarea
                            value={item.item_notes}
                            onChange={(e) => updateItem(index, 'item_notes', e.target.value)}
                            rows={2}
                            placeholder="Add notes about this item..."
                            className="basis-full w-full border border-gray-300 rounded-lg px-3 py-2 text-sm max-md:text-base focus:ring-2 focus:ring-blue-500 focus:outline-none focus:border-blue-500 resize-y max-md:h-[8.5rem] max-md:resize-none max-md:overflow-y-auto"
                            aria-label={`Notes for item ${index + 1}`}
                            data-testid={`item-notes-input-${index}`}
                          />
                        )
                      )}
                  </div>
                  {/* 📱 On a phone the LOCKED Remove sits at the foot of the open item, away from
                      the band's Received? tick, so a near-miss on the tick never opens the lock
                      notice (owner 2026-10-03). The band's copy is hidden below md (index.css). */}
                  {readOnly && item.id != null && (
                    <div className="md:hidden flex justify-end mt-3">
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); showLockedNotice(); }}
                        className="inline-flex items-center gap-1.5 min-h-11 px-3 rounded-md border border-gray-300 text-gray-500 text-sm font-medium cursor-not-allowed"
                        title="This order is locked"
                        aria-label={`Remove item ${index + 1} (locked)`}
                        data-testid={`remove-item-locked-foot-${index}`}
                      >
                        Remove item <PadlockIcon />
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          ))}

          {/* Add Item — reads just "+ Add Item", centred, with no "Item N"
              counter (owner, 2026-10-09 demo run-through: first on mobile —
              "change the text to just ' + Add Item' and centralise it" — then
              "on desktop … the same centralised like the mobile update"). On
              desktop it is the height of a collapsed line item (41px; owner
              2026-10-10: "the same height as each line item"). */}
          {!readOnly && (
            <button
              type="button"
              onClick={addItem}
              className="w-full border-2 border-dashed border-gray-300 rounded-lg p-3 md:py-0 md:h-[41px] flex items-center justify-center gap-2 text-gray-500 hover:border-blue-400 hover:text-blue-600 hover:bg-blue-50 transition-colors cursor-pointer group"
              data-testid="add-item-btn"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-gray-400 group-hover:text-blue-500 transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
              </svg>
              <span className="text-sm font-medium">Add Item</span>
            </button>
          )}
        </div>

        {/* ── ORDER TOTAL — MOVED HERE FROM THE NOTES CARD, 2026-08-10 ──
            Product owner: *"I feel like the 'Total: £39.98' should go at the
            bottom of the line items section and not in the notes section"*. He
            is right on the merits: the total is the SUM OF THE LINE ITEMS, and
            sitting in the notes block it read as unrelated to the numbers
            above it. It is now the last thing in the Line Items card, directly
            under Add Item.

            🔑 IT STAYS VISIBLE WHEN THE ITEMS ARE COLLAPSED, DELIBERATELY. The
            total is the one number you still want when the detail is hidden —
            collapsing is how you get the detail out of the way to see it. It
            also happens to be the smaller change: `collapsedItems` collapses
            individual items INSIDE the `space-y-2` list, so a sibling of that
            list is untouched by the collapse-all button either way.

            🔻 THE COMMENT BELOW USED TO CLAIM THE testid WAS KEPT "so the
            existing selectors still resolve", AND THAT WAS SIMPLY NOT TRUE ON
            THIS TREE — `grep -rn "grand-total"` across `client`, `e2e` and
            `server` returns exactly two hits: that comment and this element.
            There are no such selectors and there were none when the claim was
            written. **The testid is kept anyway** — it costs nothing, renaming
            it is the one thing that WOULD break a future selector, and the
            brief for this change required it unrenamed — but it is kept on
            those grounds and not on a false one. (Every `grand_total` hit in
            `e2e/` is the budget API's JSON field, a different thing entirely,
            which is probably how the claim came to be believed.)

            The rest of the original note still holds: the Subtotal / VAT (20%)
            / Grand Total (Inc VAT) breakdown went with the VAT display on
            2026-08-08, and the label is a bare "Total" because the app no
            longer knows whether the figures were typed ex- or inc-VAT, so
            "(Ex VAT)" would be a claim it cannot support. Reasoning in
            `hooks/useVatMode.js`. */}
        {/* 🪤 `border-t` is deliberately BARE — do not "complete" it to
            `border-t border-gray-200`. Tailwind's preflight already resolves a
            bare border to gray-200, and naming the class opts this element in
            to `index.css`'s `html.dark .border-gray-200 { … !important }`,
            which then BEATS `dark:border-gray-700` and silently changes the
            rule's dark colour. Carried over verbatim from its old home. */}
        {/* 📱 hidden below md: the phone submit bar shows the same total. */}
        <div className="max-md:hidden flex justify-between text-base font-bold border-t dark:border-gray-700 pt-2">
          <span className="text-gray-800 dark:text-white">Total:</span>
          <span className="text-gray-800 dark:text-white" data-testid="grand-total">{formatCurrency(subtotal)}</span>
        </div>
      </div>

      {/* Notes & Attachments
          🔻 "Notes, Totals & Attachments" UNTIL 2026-08-10 — the Total moved
          to the foot of the Line Items card, where the numbers it sums are.
          🔑 THE `lg:grid-cols-5` WRAPPER WENT WITH IT, AND REMOVING IT IS PART
          OF THE FIX RATHER THAN TIDYING AROUND IT. That grid existed only to
          stand Notes (`lg:col-span-3`) beside the Total (`lg:col-span-2`);
          with the Total gone it would have left Notes at three fifths width
          with two dead columns beside it — a textarea arbitrarily stopping
          short of the card edge for no reason a reader could see. Notes is now
          the only thing here, so it is simply full width. */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-2.5 space-y-3" data-form-card="">
        <div>
          {/* ⓘ (2026-09-13) — the PRINTED half of the note pair. Owner:
              *"update the tool tip hints about printed/not printed"*. The
              string lives in `constants/orderNotes.js` beside its opposite
              number on Internal notes; neither sentence means much alone —
              what a user needs is which of the two boxes reaches the supplier.
              Shared `components/InfoTip.js`, INSIDE the `<label>` in the
              documented `flex items-center gap-1` idiom (the `Header details`
              box above is the worked example). Never a native `title=` on the
              icon, and the nesting prohibition is about a label wrapping a
              checkbox or radio, which this is not. */}
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setNotesOpen((o) => !o); }}
            className={`flex items-center gap-2 w-full text-left ${DESKTOP_SECTION_HEAD_BLEED}`}
            aria-expanded={notesOpen}
            aria-controls="order-notes-body"
            data-testid="notes-toggle"
            data-phone-section-head=""
          >
            <svg data-chevron xmlns="http://www.w3.org/2000/svg" className={`h-3.5 w-3.5 ${DESKTOP_SECTION_CHEVRON} text-gray-400 transition-transform ${notesOpen ? '' : '-rotate-90'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
            </svg>
            <span className={`text-sm font-medium text-gray-600 dark:text-gray-400 ${DESKTOP_SECTION_TITLE}`}>Notes / Comments</span>
            <span onClick={(e) => e.stopPropagation()} className="inline-flex"><InfoTip title={ORDER_NOTES_TOOLTIP} /></span>
            {!notesOpen && form.notes ? (
              <span className="text-xs font-medium text-blue-600 bg-blue-50 dark:bg-blue-900/30 dark:text-blue-300 px-2 py-0.5 rounded-full">Has notes</span>
            ) : null}
          </button>
          {notesOpen && (
          <div id="order-notes-body" className="mt-2">
          <label htmlFor="notes" className="sr-only">Notes / Comments</label>
          {readOnly ? (
            // 🔒 Locked like every other field (owner 2026-10-03): the same greyed box
            // and padlock as `ReadOnlyValue`, multi-line; a tap explains the lock.
            <p
              className="w-full flex items-start gap-2 border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/60 rounded-lg px-2 py-2 text-base md:text-sm text-gray-600 dark:text-gray-400 min-h-[2.75rem] max-md:max-h-[8.5rem] max-md:overflow-y-auto cursor-not-allowed"
              data-testid="general-notes"
              aria-readonly="true"
              title="Locked"
              onClick={showLockedNotice}
            >
              <span className="flex-1 min-w-0 whitespace-pre-wrap">{form.notes || <span className="text-gray-400 dark:text-gray-500">—</span>}</span>
              <PadlockIcon />
            </p>
          ) : (
            <textarea
              id="notes"
              name="notes"
              value={form.notes}
              onChange={handleChange}
              rows={3}
              placeholder="Any additional notes about this order..."
              // Phone (owner 2026-10-01): the same ~6-line scrolling box as the item notes.
              className={`${inputClass('notes')} h-auto py-2 max-md:h-[8.5rem] max-md:resize-none max-md:overflow-y-auto`}
              data-testid="general-notes"
            />
          )}
          </div>
          )}
        </div>

        {/* Order-level attachments — renders in both create (NewOrder) and view/edit
            (OrderPage) modes. Existing already-saved files show with download + remove
            (or strikethrough + undo if pending removal). Pending uploads (`pendingFiles`)
            show as PendingUploadRow. Drop zone hidden when there is neither
            `pendingFiles` nor `onOrderFilesChange`.

            🔀 EXTRACTED TO `components/OrderAttachments.js` ON 2026-08-10, VERBATIM.
            The expanded order row on Department Orders / To Approve needed the same
            section, and its READ-ONLY branch has no `OrderForm` to switch on — so
            leaving the markup here would have meant a second hand-rolled copy for
            everyone the server does not let edit. Same classes, same testids (the
            component's `testIdPrefix` defaults to '', which reproduces
            `file-upload-area` / `file-input` / `file-list` / `file-item-N` /
            `remove-file-N` / `order-existing-file-N` / `download-order-existing-N` /
            `toggle-remove-order-existing-N` character for character), same
            collapsed-by-default disclosure. The `accept=`-filter prohibition and the
            authenticated-`/uploads/*` download reasoning moved with it. */}
        {showOrderFileUpload && (
          <OrderAttachments
            phoneTitle="Order attachments"
            existingFiles={existingOrderFiles}
            onFilesChange={pendingFiles
              ? (pendingFilesDisabled ? null : (files) => pendingFiles.add(PENDING_ORDER_LINE, files))
              : onOrderFilesChange}
            pendingDeleteFileIds={pendingDeleteFileIds}
            onExistingFileToggleRemove={onExistingFileToggleRemove}
            pendingEntries={pendingFiles ? pendingFiles.entriesFor(PENDING_ORDER_LINE) : []}
            onPendingRemove={pendingFiles ? pendingFiles.remove : null}
            onPendingRetry={pendingFiles ? pendingFiles.retry : null}
          />
        )}
      </div>

      {/* Error summary near the submit button — hidden in read-only mode */}
      {!readOnly && (() => {
        const list = [];
        // 🔻 `errors.production_id` was read here until 2026-08-12. `validateForm`
        // can no longer produce it — Production is not a control — so the line
        // was dead and is removed rather than left to imply a check still exists.
        if (errors.requested_by)  list.push(errors.requested_by);
        if (errors.buyer_email)   list.push(errors.buyer_email);
        if (errors.urgency)       list.push(errors.urgency);
        if (errors.supplier_name) list.push(errors.supplier_name);
        if (errors.items)         list.push(errors.items);
        if (errors.itemErrors) {
          Object.entries(errors.itemErrors).forEach(([idx, errs]) => {
            Object.values(errs).forEach((msg) => {
              list.push(`Item ${Number(idx) + 1}: ${msg}`);
            });
          });
        }
        if (list.length === 0) return null;
        return (
          <div
            className="p-3 bg-red-50 border border-red-300 rounded-lg text-red-800 dark:bg-red-900/20 dark:border-red-700/60 dark:text-red-200"
            role="alert"
            data-testid="submit-error-summary"
          >
            <p className="font-semibold text-sm mb-1">
              Please fix the following before submitting:
            </p>
            <ul className="list-disc list-inside text-sm space-y-0.5">
              {list.map((msg, i) => (
                <li key={i}>{msg}</li>
              ))}
            </ul>
          </div>
        );
      })()}

      {/* Server-side submit error (parent passes via prop) — also hidden when read-only */}
      {!readOnly && submitError && (
        <div role="alert" className="p-4 bg-red-50 border border-red-300 rounded-lg text-red-800 dark:bg-red-900/20 dark:border-red-700/60 dark:text-red-200">
          <p className="font-semibold">{mode === 'edit' ? 'Error updating order' : 'Error submitting order'}</p>
          <p className="text-sm mt-1">{submitError}</p>
        </div>
      )}

      {/* Submit / Cancel row — hidden in read-only mode */}
      {showSaveBar && (
        // 📱 New Order: Submit (with the total) is always there, pinned below `md`.
        // An EDIT (owner 2026-10-03): no in-flow Save / Discard — they appear only
        // once something has changed, CENTRED and pinned to the bottom at every width.
        // A pending upload counts as a change: it is attached only BY a save, and
        // after a refused claim the form re-hydrates clean while the files wait
        // under their line — Save is then the only way to retry them.
        <PhoneActionBar
          pinned
          desktopPinned={mode !== 'create'}
          className={`flex gap-3 flex-wrap max-md:items-center ${mode === 'create' ? 'justify-end' : 'justify-center'}`}
          testId="order-form-actions"
        >
          {/* 📱 Approve / Reject stepped aside while there are unsaved changes
              (`ui/PhoneDecisionBar`) — say where they went. Phone only. */}
          {hasPhoneDecision && mode !== 'create' && (
            <p className="md:hidden w-full text-center text-xs text-gray-600 dark:text-gray-300" data-testid="order-form-decision-hint">
              {PHONE_DECISION_HINT}
            </p>
          )}
          {mode === 'create' && (
            // The SAME `subtotal` as `grand-total` above — never a second sum.
            <span className="md:hidden mr-auto text-sm font-bold text-gray-800 dark:text-white tabular-nums" data-testid="order-form-phone-total">
              Total {formatCurrency(subtotal)}
            </span>
          )}
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              disabled={cancelDisabled}
              className={BTN['danger-soft']}
              aria-label="Discard unsaved changes"
              data-testid="discard-changes-button"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
              </svg>
              Discard Changes
            </button>
          )}
          {/* Edit mode → green styling + checkmark, matching the OrderPage header's
              top "Save Changes" button so the two prominent submit affordances on the
              same screen read as the same action. Create mode (NewOrder) keeps the
              shared BTN.primary blue — that's a "submit a new thing" action, not "save". */}
          {/* `submitInFlight` covers the frames before the parent's
              `submitting` prop has re-rendered — see the double-submit guard
              in `handleSubmit`. */}
          <button
            type="submit"
            disabled={submitting || submitInFlight || submitDisabled}
            title={submitDisabled ? (submitDisabledHint || undefined) : undefined}
            className={mode === 'edit'
              ? BTN.confirm
              // `max-md:w-auto`: on a phone it shares the pinned bar with the total.
              : `${BTN.primary} ${onCancel ? '' : 'w-full sm:w-auto max-md:w-auto'}`
            }
            data-testid={mode === 'edit' ? 'save-changes' : 'submit-order'}
          >
            {submitting ? (
              <span className="inline-flex items-center gap-2">
                <LoadingSpinner size="sm" inline />
                {mode === 'edit'
                  ? (originalStatus === 'rejected' ? 'Resubmitting...' : 'Saving...')
                  : 'Submitting...'}
              </span>
            ) : (
              <>
                {mode === 'edit' && (
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                )}
                {submitLabel}
              </>
            )}
          </button>
        </PhoneActionBar>
      )}
      {/* The post-approval removal pause. Rendered only while a removal is
          pending, so every caller that never passes `postApprovalGuardStatus`
          renders nothing new. */}
      {pendingRemoval && (
        <PostApprovalEditDialog
          open
          status={postApprovalGuardStatus}
          action={POST_APPROVAL_ACTIONS.REMOVE_ITEM}
          itemLabel={pendingRemoval.label}
          amountText={pendingRemoval.amountText}
          adjustmentsText={pendingRemoval.adjustmentsText}
          countsInBudget={pendingRemoval.countsInBudget}
          onConfirm={confirmPendingRemoval}
          onCancel={() => setPendingRemoval(null)}
        />
      )}
      {/* "Untick delivered first" — a NOTICE, not a question: one button,
          and nothing is removed whichever way it closes. */}
      <ConfirmDialog
        open={receivedRemovalNotice}
        title={RECEIVED_ITEM_REMOVAL_NOTICE.title}
        message={<span data-testid="received-item-removal-notice">{RECEIVED_ITEM_REMOVAL_NOTICE.message}</span>}
        confirmLabel={RECEIVED_ITEM_REMOVAL_NOTICE.okLabel}
        hideCancel
        onConfirm={() => setReceivedRemovalNotice(false)}
        onCancel={() => setReceivedRemovalNotice(false)}
      />
      {/* "Remove the only item?" (2026-10-01) — "Cancel order" hands over to
          the page's existing cancel flow; nothing is removed either way. */}
      <ConfirmDialog
        open={onlyItemPrompt}
        variant="danger"
        title={ONLY_ITEM_REMOVAL.title}
        message={<span data-testid="only-item-removal-prompt">{ONLY_ITEM_REMOVAL.message}</span>}
        confirmLabel={ONLY_ITEM_REMOVAL.cancelOrderLabel}
        cancelLabel={ONLY_ITEM_REMOVAL.keepLabel}
        initialFocus="cancel"
        onConfirm={() => {
          setOnlyItemPrompt(false);
          if (onRequestCancelOrder) onRequestCancelOrder();
        }}
        onCancel={() => setOnlyItemPrompt(false)}
      />
      <ConfirmDialog
        open={lockedNotice}
        title="This order is locked"
        message={<span data-testid="locked-order-notice">{initialOrder && initialOrder.status === 'submitted'
          ? 'This order is waiting for approval, so it can’t be changed here. Please speak to the buyer.'
          : 'This order has already been approved, so it can’t be changed here. Please speak to the buyer.'}</span>}
        confirmLabel="OK"
        hideCancel
        onConfirm={() => setLockedNotice(false)}
        onCancel={() => setLockedNotice(false)}
      />
    </form>
    </OrderFormDecisionContext.Provider>
    </LockedFieldContext.Provider>
  );
}

export default OrderForm;
